'use client'

import { useRef, useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react'
import { Minus, Plus, Maximize2, Expand, Shrink, ArrowLeft } from 'lucide-react'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import { richTextToPlain } from '@/lib/sanitize-rich-text'
import { SectionSeatCache } from '@/lib/seat-map/section-seat-cache'
import { flattenPolygonOutline } from '@/lib/seat-map/section-geometry'
import { sectionParts, sectionBounds as sectionPartsBounds, largestPart, partCenter, partBounds, hasMultipleParts, pointInSection } from '@/lib/seat-map/section-parts'
import { planGestureCache, gestureCacheUsable, gestureCacheCovers, gestureCacheHolds, gestureCacheDest, nextBandRows, sameInputs, type GestureCacheView, type GesturePlanOptions } from '@/lib/seat-map/gesture-frame-cache'
import {
  createSectionGradient,
  sectionShapeInputFrom,
  drawSeatCheckmark,
  drawSeatBatch,
  drawArcShape,
  drawRoundedPolygon,
  traceSectionPath,
  lightenColor,
  darkenColor,
  getRelativeLuminance,
  pointInPolygon,
  easeOutCubic,
  FONT_FAMILY,
} from '@/lib/seat-map/canvas-utils'

// Feature flag (OFF by default): route the live seat-bucket draw through the shared
// canvas-utils primitive so it can be canary-verified before fully replacing the local
// copy. Identical pixel output by construction; set NEXT_PUBLIC_USE_SHARED_SEAT_RENDERER=1
// on staging to A/B it. See venue-builder refactor Order 13.
// ON by default (opt out with =0): these were built for visual parity with
// the builder/seats.io-grade rendering and sat dark behind canary flags.
const USE_SHARED_SEAT_RENDERER =
  typeof process === 'undefined' || process.env.NEXT_PUBLIC_USE_SHARED_SEAT_RENDERER !== '0'

// Feature flag (OFF by default): render beveled 3D seat sprites instead of flat
// circles on the live map. Perf-sensitive (a per-seat drawImage), so it is also
// guarded at the call site by a seat-count ceiling. Enable with NEXT_PUBLIC_SEAT_3D=1.
// See venue-builder refactor Order 14.
// OFF: the beveled sprites read as glossy "balls" on the live map — rejected
// on sight. Flat crisp circles are the look. (Opt back in with =1 only for
// experiments; do NOT default this on again without visual sign-off.)
const SEAT_3D_ENABLED =
  typeof process !== 'undefined' && process.env.NEXT_PUBLIC_SEAT_3D === '1'
// Above this many on-screen seats, force flat circles regardless of the flag.
const SEAT_3D_MAX_SEATS = 2500

// Feature flag (OFF by default): trace section outlines via the shared
// traceSectionPath() so the live renderer and the builder are one source of
// truth for section geometry. Output is identical to the current inline tracing
// by construction; the flag lets it be canary-verified before the inline copy is
// removed. Enable with NEXT_PUBLIC_USE_SHARED_SECTION_GEOMETRY=1.
const USE_SHARED_SECTION_GEOMETRY =
  typeof process === 'undefined' || process.env.NEXT_PUBLIC_USE_SHARED_SECTION_GEOMETRY !== '0'
import { drawVenueShape } from './venue-shapes'
import { getSeatsInBox, getSeatsInCircle, getSeatsInRow, type BoxSelection, type CircleSelection } from '@/lib/seating/selection-utils'
import { computeTableSeats } from '@/lib/seat-map/table-seat-layout'
import type {
  ChartLayoutData,
  LayoutSection,
  LayoutStage,
  DecorativeShape,
  LayoutLabel,
  SectionOverview,
  SeatCategory,
  ComputedSeatPosition,
  VenueShape,
  FloorInfo,
  MapVisualConfig,
  LayoutImage,
} from '@/types/seating'

const CLICK_THRESHOLD = 5
const DETAIL_PRELOAD_PX = 60   // start loading seat data at this threshold (eager)
const DETAIL_SECTION_WIDTH_PX = 180 // min screen px for section to show individual seats
// Seated sections show as solid shapes until a seat is big enough to see, then
// switch to seats over this short band of seat width on screen (CSS px). The
// fade used to run over a 3x zoom range of faded fills and specks of seats.
const SEAT_FADE_FROM_PX = 5
const SEAT_FADE_TO_PX = 8
// Zoom stops once a seat is about finger-sized on screen.
const MAX_SEAT_SCREEN_PX = 26
// Venue text (designer labels, shape and stage names) never draws larger than
// this on screen: at seat zoom "PRODUCTION" filled the phone.
const MAX_VENUE_TEXT_PX = 18
// Tables are small by nature (~120 world px), so the section thresholds above put
// them permanently below the seat-detail cutoff on a venue-wide view: every table
// rendered as an overview dot on first load instead of as a table. A table's seats
// ARE its content, so they earn detail much earlier than a large seated block.
// R2 serves no CORS header, so a direct crossOrigin load always fails and falls
// back to a second no-CORS request — a wasted round trip per image, a late-
// popping background and a tainted canvas. Route remote assets through our own
// origin instead. Same-origin and data URLs are passed through untouched.
function sameOrigin(url: string | null | undefined): string {
  if (!url) return ''
  if (!/^https?:\/\//i.test(url)) return url
  if (typeof window !== 'undefined' && url.startsWith(window.location.origin)) return url
  return `/api/asset-image?url=${encodeURIComponent(url)}`
}

const TABLE_PRELOAD_PX = 24
const TABLE_DETAIL_PX = 70
const LABEL_COMPACT_PX = 280   // switch from full labels to compact pill badge
// Drawing gestures and zoom animations from cached bitmaps of the map: OFF.
// On phones it showed blown-up section fills, a step to the seats and strips
// of the old picture at the edges while zooming into a section. Every frame
// is painted normally; the bitmap code below stays inert behind this switch.
const GESTURE_BITMAPS = false
// A gesture without an end event (wheel, a finger held still) ends once the
// map has not moved for this long; the map is then painted normally.
const GESTURE_HOLD_MS = 150
// This long after a normal paint, the gesture bitmaps for that view start
// being painted in idle time, so the next gesture starts on ready bitmaps.
const GESTURE_PREP_DELAY_MS = 250
// A bitmap painted during a gesture is painted a band per frame, each band
// about this long, so no frame stalls on a whole paint...
const GESTURE_JOB_BUDGET_MS = 5
// ...and in idle time at rest, a band per idle slot of at most this long.
const GESTURE_IDLE_BUDGET_MS = 3
// Margin of a bitmap painted mid-zoom (a share of the view's longer side):
// the zoom outgrows it soon, so little is spent on what a pan would need.
const GESTURE_ZOOM_MARGIN = 0.1
// CSS px painted past each edge of a band, then clipped: shadows and strokes
// of shapes just outside it.
const GESTURE_BAND_PAD_PX = 8
// A gesture is drawn from the bitmaps only when a normal frame of the map
// takes longer than this (start of paint to the next frame): it would drop
// frames. On a fast device or a light view it is painted normally, and
// switches over mid-gesture after two normal frames slower than the second.
const GESTURE_LIVE_FRAME_MS = 24
const GESTURE_LIVE_SLOW_FRAME_MS = 24
// A bitmap painted mid-gesture that shows the view would paint in under this
// (paint and rasterize, scaled to the view) turns the gesture back to normal
// frames.
const GESTURE_LIVE_PAINT_MS = 8


// Utilities imported from @/lib/seat-map/canvas-utils for visual parity with builder

// --- Section bounding box ---
/** Rotate a point around a center */
/**
 * A seat HELD by the viewer's own session. Public seat data carries no session
 * ids (lib/cart/hold-session), so the storefront marks its own holds `mine`
 * from its own answers; staff surfaces still read holdSessionId from their
 * admin endpoints.
 */
function isOwnHold(seat: { status: string; mine?: boolean; holdSessionId?: string | null }, sessionId: string | null | undefined): boolean {
  return seat.status === 'HELD' && (seat.mine === true || (!!sessionId && seat.holdSessionId === sessionId))
}

function rotatePoint(px: number, py: number, cx: number, cy: number, angleDeg: number): [number, number] {
  const rad = (angleDeg * Math.PI) / 180
  const cos = Math.cos(rad), sin = Math.sin(rad)
  const dx = px - cx, dy = py - cy
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos]
}

// Where the chart's background image lands in world space: cover-fit to the
// designer canvas, then the admin's scale / offset / rotation / flip. Shared by
// the draw and the overview frame so the two cannot disagree.
function backgroundImageRect(layout: ChartLayoutData, imgW: number, imgH: number) {
  const bgT = (layout as any).backgroundTransform || {}
  const bgVc = layout.visualConfig as any
  const bgScale = bgT.scale ?? bgVc?.backgroundScale ?? 1
  const lockAspect = bgT.lockAspect !== false
  const sx = lockAspect ? bgScale : (bgT.scaleX ?? bgScale)
  const sy = lockAspect ? bgScale : (bgT.scaleY ?? bgScale)
  const coverScale = Math.max(layout.width / imgW, layout.height / imgH)
  return {
    cx: (bgT.offsetX ?? bgVc?.backgroundOffsetX ?? 0) + layout.width / 2,
    cy: (bgT.offsetY ?? bgVc?.backgroundOffsetY ?? 0) + layout.height / 2,
    w: imgW * coverScale * sx,
    h: imgH * coverScale * sy,
    rotation: (bgT.rotation ?? bgVc?.backgroundRotation ?? 0) as number,
    flipX: !!(bgT.flipX ?? bgVc?.backgroundFlipX ?? false),
    flipY: !!(bgT.flipY ?? bgVc?.backgroundFlipY ?? false),
    opacity: (bgT.opacity ?? bgVc?.backgroundOpacity ?? 1) as number,
  }
}

// Axis-aligned box of a w x h rectangle centred on (cx, cy) and turned by deg.
function rotatedBox(cx: number, cy: number, w: number, h: number, deg: number) {
  const rad = (deg * Math.PI) / 180
  const c = Math.abs(Math.cos(rad)), s = Math.abs(Math.sin(rad))
  const bw = w * c + h * s, bh = w * s + h * c
  return { x: cx - bw / 2, y: cy - bh / 2, w: bw, h: bh }
}

// --- Per-section caches ---
// A loaded layout's section objects never change (a new chart or a pruned
// layout arrives as new objects), so anything derived from one section alone
// is computed once and kept against the object. The map redraws every frame of
// a pan or pinch; recomputing a curved polygon's bounds, its overview dots and
// its outline inputs on each of them was a large part of every frame on a
// phone. Read-only results: callers must not mutate them.
const sectionBoundsCache = new WeakMap<LayoutSection, { x: number; y: number; w: number; h: number }>()
const sectionCenterCache = new WeakMap<LayoutSection, { cx: number; cy: number }>()

// Bounds and centre now span every PART of the section (a GA zone can be drawn
// in several places). A single-shape section has exactly one part, so the maths
// is unchanged for every chart built before multi-place zones existed.
function getSectionBounds(section: LayoutSection): { x: number; y: number; w: number; h: number } {
  let b = sectionBoundsCache.get(section)
  if (!b) { b = sectionPartsBounds(section); sectionBoundsCache.set(section, b) }
  return b
}

/** Median radius of the seats in these sections (layout rows), or null. */
function medianSeatRadius(sections: LayoutSection[]): number | null {
  const radii: number[] = []
  for (const ls of sections) {
    for (const row of ls.rows || []) for (const st of row.seats || []) if ((st.radius ?? 0) > 0) radii.push(st.radius!)
  }
  if (!radii.length) return null
  radii.sort((a, b) => a - b)
  return radii[Math.floor(radii.length / 2)]
}

/** Sections of individual seats, which fade from overview to seats together
 *  (tables, booths and GA zones keep their own size-based fade). */
function sharesSeatFade(section: LayoutSection): boolean {
  const t = section.sectionType
  return !t || t === 'SEATED'
}

// --- Section center (the largest part, so the label sits on the main block) ---
function getSectionCenter(section: LayoutSection): { cx: number; cy: number } {
  let c = sectionCenterCache.get(section)
  if (!c) { c = partCenter(largestPart(section)); sectionCenterCache.set(section, c) }
  return c
}

// Overview dots of one section as a single Path2D, in the section's own
// (un-rotated) space — built once, filled every frame. Keyed by the overview
// figures the estimate depends on, so a capacity change rebuilds it.
const overviewDotsCache = new WeakMap<LayoutSection, { key: string; path: Path2D | null }>()
function getOverviewDotsPath(ls: LayoutSection, capacity: number, rowCount: number | undefined): Path2D | null {
  const hasRealRows = !!(ls.rows && ls.rows.length > 0)
  const key = `${hasRealRows ? 'rows' : 'est'}:${capacity}:${rowCount ?? ''}`
  const hit = overviewDotsCache.get(ls)
  if (hit && hit.key === key) return hit.path
  const sectionRotated = !!ls.rotation
  // Use the REAL saved seat positions (ls.rows) so the overview preview is a
  // pixel-accurate stand-in for the actual seats — never a generic estimated
  // grid that drifts out of alignment with the rows. These positions are in
  // world space and already rotation-baked, so we un-rotate them into the
  // section's local space (the ctx is already rotated by ls.rotation), making
  // each dot land exactly where its seat will.
  const realDots: Array<{ cx: number; cy: number; r: number }> = []
  if (hasRealRows) {
    const rcx = ls.x + ls.width / 2, rcy = ls.y + ls.height / 2
    for (const row of ls.rows!) {
      for (const st of (row.seats || [])) {
        const [lx, ly] = sectionRotated
          ? rotatePoint(st.x, st.y, rcx, rcy, -ls.rotation!)
          : [st.x, st.y]
        realDots.push({ cx: lx, cy: ly, r: st.radius || 3 })
      }
    }
  }
  // Fall back to the estimate only when real positions aren't in the layout.
  const dots = realDots.length > 0
    ? realDots
    : estimateOverviewDots(sectionRotated ? { ...ls, rotation: 0 } : ls, capacity, rowCount)
  let path: Path2D | null = null
  if (dots.length > 0 && typeof Path2D !== 'undefined') {
    path = new Path2D()
    for (const dot of dots) {
      path.moveTo(dot.cx + dot.r, dot.cy)
      path.arc(dot.cx, dot.cy, dot.r, 0, Math.PI * 2)
    }
  }
  overviewDotsCache.set(ls, { key, path })
  return path
}

// Outline inputs per part (shared with the designer's traceSectionPath), one
// list per section and section type.
type ShapeInput = ReturnType<typeof sectionShapeInputFrom>
const shapeInputsCache = new WeakMap<LayoutSection, Map<string, ShapeInput[]>>()
function getSectionShapeInputs(ls: LayoutSection, sType: string): ShapeInput[] {
  let byType = shapeInputsCache.get(ls)
  if (!byType) { byType = new Map(); shapeInputsCache.set(ls, byType) }
  let inputs = byType.get(sType)
  if (!inputs) {
    inputs = sectionParts(ls).map(part => sectionShapeInputFrom({
      shape: part.shape,
      sectionType: ls.sectionType,
      x: part.x, y: part.y, width: part.width, height: part.height,
      points: part.points,
      edgeCurves: part.edgeCurves,
      edgeSkew: part.edgeSkew,
      arcConfig: part.arcConfig,
      tableConfig: ls.tableConfig,
    }, sType))
    byType.set(sType, inputs)
  }
  return inputs
}
const gradientCache = new WeakMap<ShapeInput, Map<string, CanvasGradient>>()
function getCachedGradient(input: ShapeInput, color: string): CanvasGradient | undefined {
  return gradientCache.get(input)?.get(color)
}
function setCachedGradient(input: ShapeInput, color: string, g: CanvasGradient): void {
  let m = gradientCache.get(input)
  if (!m) { m = new Map(); gradientCache.set(input, m) }
  m.set(color, g)
}

// Which layers a paint draws: the whole map, the gesture bitmap's static
// layers, or the live labels drawn over that bitmap.
type PaintLayers = 'all' | 'base' | 'labels'

// Device px per CSS px of the map canvas. Capped at 2 on touch devices: a 3x
// phone would paint 2.25x the pixels for little visible gain.
function mapCanvasDpr(isMobile: boolean): number {
  const rawDpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1
  return isMobile ? Math.min(rawDpr, 2) : rawDpr
}

function viewKey(t: { x: number; y: number; scale: number }, cw: number, ch: number, dpr: number): string {
  return `${t.x},${t.y},${t.scale},${cw},${ch},${dpr}`
}

// A gesture bitmap, the inputs it was painted from and the view it was laid out for.
type GestureLayer = GestureCacheView & { canvas: HTMLCanvasElement; inputs: unknown[]; viewKey: string }
// A bitmap being painted a band at a time (rows [0, nextRow) are done), to
// become the main or the overview bitmap.
interface GestureJob { layer: GestureLayer; role: 'main' | 'backdrop'; nextRow: number; rows: number; ms: number }
interface GestureLayers {
  backdrop: GestureLayer | null
  main: GestureLayer | null
  job: GestureJob | null
  spare: HTMLCanvasElement | null
}

// Run `fn` when the page is idle, with the ms it may take (a short slot
// every frame or so where requestIdleCallback is missing: Safari). Short
// slots: a tap that lands during one waits for it.
function whenIdle(fn: (budgetMs: number) => void): void {
  const w = window as Window & { requestIdleCallback?: (cb: (d: { timeRemaining: () => number }) => void, opts?: { timeout: number }) => number }
  if (w.requestIdleCallback) w.requestIdleCallback((d) => fn(Math.min(GESTURE_IDLE_BUDGET_MS, Math.max(1, d.timeRemaining()))), { timeout: 1000 })
  else window.setTimeout(() => fn(GESTURE_IDLE_BUDGET_MS), 32)
}

// Give a canvas's memory back now (WebKit keeps it until collected otherwise).
function releaseCanvas(canvas: HTMLCanvasElement): void {
  canvas.width = 0
  canvas.height = 0
}
function keepSpareCanvas(L: GestureLayers, canvas: HTMLCanvasElement): void {
  if (L.spare && L.spare !== canvas) releaseCanvas(L.spare)
  L.spare = canvas
}
function takeSpareCanvas(L: GestureLayers): HTMLCanvasElement | null {
  const c = L.spare
  L.spare = null
  return c
}
function setMainLayer(L: GestureLayers, layer: GestureLayer): void {
  const old = L.main
  L.main = layer
  if (old && old !== layer && old !== L.backdrop) keepSpareCanvas(L, old.canvas)
}
function setBackdropLayer(L: GestureLayers, layer: GestureLayer): void {
  const old = L.backdrop
  L.backdrop = layer
  if (old && old !== layer && old !== L.main) keepSpareCanvas(L, old.canvas)
}

// Make the browser rasterize a canvas's pending drawing now, by drawing one
// pixel of it onto a 1x1 scratch canvas.
let rasterScratch: CanvasRenderingContext2D | null = null
function rasterizeCanvasNow(canvas: HTMLCanvasElement): void {
  if (!rasterScratch) {
    const c = document.createElement('canvas')
    c.width = 1
    c.height = 1
    rasterScratch = c.getContext('2d')
  }
  rasterScratch?.drawImage(canvas, 0, 0, 1, 1, 0, 0, 1, 1)
  // Drop the recorded draw (and with it the scratch's hold on the source's pixels).
  rasterScratch?.clearRect(0, 0, 1, 1)
}

// Put a 2D context back to the state a freshly sized canvas has: default
// transform, compositing, shadow, line, text and filter state, and no saved
// states left on the stack (save() inside an interrupted draw).
function resetCanvasContext(ctx: CanvasRenderingContext2D): void {
  const anyCtx = ctx as CanvasRenderingContext2D & { reset?: () => void }
  if (typeof anyCtx.reset === 'function') { anyCtx.reset(); return }
  for (let i = 0; i < 64; i++) ctx.restore()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalAlpha = 1
  ctx.globalCompositeOperation = 'source-over'
  ctx.fillStyle = '#000000'
  ctx.strokeStyle = '#000000'
  ctx.shadowColor = 'rgba(0, 0, 0, 0)'
  ctx.shadowBlur = 0
  ctx.shadowOffsetX = 0
  ctx.shadowOffsetY = 0
  ctx.lineWidth = 1
  ctx.lineCap = 'butt'
  ctx.lineJoin = 'miter'
  ctx.miterLimit = 10
  ctx.lineDashOffset = 0
  ctx.setLineDash([])
  ctx.font = '10px sans-serif'
  ctx.textAlign = 'start'
  ctx.textBaseline = 'alphabetic'
  ctx.direction = 'inherit'
  if ('filter' in ctx) (ctx as CanvasRenderingContext2D & { filter: string }).filter = 'none'
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'low'
  ctx.beginPath()
}

// Bounding box of a loaded section's seat circles (world space), so whole
// sections off screen are skipped instead of walking every seat each frame.
const seatBoxCache = new WeakMap<ComputedSeatPosition[], { x0: number; y0: number; x1: number; y1: number }>()
function getSeatBox(seats: ComputedSeatPosition[]) {
  let b = seatBoxCache.get(seats)
  if (!b) {
    b = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity }
    for (const st of seats) {
      const r = st.radius * 1.5
      if (st.absX - r < b.x0) b.x0 = st.absX - r
      if (st.absY - r < b.y0) b.y0 = st.absY - r
      if (st.absX + r > b.x1) b.x1 = st.absX + r
      if (st.absY + r > b.y1) b.y1 = st.absY + r
    }
    seatBoxCache.set(seats, b)
  }
  return b
}

// Ray-casting point-in-polygon test (used to clip overview dots to a section's
// real outline instead of its bounding box).
function ptInPoly(x: number, y: number, pts: number[][]): boolean {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i][0], yi = pts[i][1], xj = pts[j][0], yj = pts[j][1]
    if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) inside = !inside
  }
  return inside
}

// --- Estimate seat dots for overview (no real data loaded) ---
// Uses the SAME placement formula as computeArcPositions() in section-seat-cache.ts
// so that overview dots align with real seat positions when zooming in.
function estimateOverviewDots(
  section: LayoutSection,
  capacity: number,
  dbRowCount?: number
): Array<{ cx: number; cy: number; r: number }> {
  if (capacity <= 0) return []

  // Arc sections: mirror computeArcPositions() formula exactly
  if (section.shape === 'arc' && section.arcConfig) {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle } = section.arcConfig
    const radialDepth = outerRadius - innerRadius
    const angleRange = endAngle - startAngle
    if (radialDepth < 1 || angleRange < 1) return []

    // Use actual DB row count when available, otherwise estimate
    const rows = dbRowCount && dbRowCount > 0
      ? dbRowCount
      : Math.max(2, Math.min(Math.round(radialDepth / 14), 18))
    const seatsPerRow = Math.max(2, Math.round(capacity / rows))

    // Same formula as computeArcPositions: radiusStep = depth / (rows + 1)
    const radiusStep = radialDepth / (rows + 1)

    // Same radius computation as computeArcPositions
    const maxRadiusRadial = radiusStep / 2.5
    const innerRowRadius = innerRadius + radiusStep
    const angleRangeRad = (angleRange * Math.PI) / 180
    const angleStepRad = angleRangeRad / (seatsPerRow + 1)
    const arcDist = innerRowRadius * angleStepRad
    const maxRadiusAngular = arcDist / 2.5
    const dotR = Math.min(maxRadiusRadial, maxRadiusAngular, 12)
    if (dotR < 0.4) return []

    const dots: Array<{ cx: number; cy: number; r: number }> = []
    for (let ri = 0; ri < rows; ri++) {
      // Same radial placement: innerRadius + (ri + 1) * radiusStep
      const rowRadius = innerRadius + (ri + 1) * radiusStep
      // Same angular placement: startAngle + (si + 1) * angleRange / (seatCount + 1)
      const angleStep = angleRange / (seatsPerRow + 1)
      for (let si = 0; si < seatsPerRow; si++) {
        const angleDeg = startAngle + (si + 1) * angleStep
        const angleRad = (angleDeg * Math.PI) / 180
        dots.push({
          cx: cx + rowRadius * Math.cos(angleRad),
          cy: cy + rowRadius * Math.sin(angleRad),
          r: dotR,
        })
      }
    }
    return dots
  }

  // Rectangular / polygon sections: grid layout
  // For tall narrow sections (h > w), orient dots along the height
  const bounds = getSectionBounds(section)
  const { w, h } = bounds
  const isVertical = h > w * 1.5

  // Polygon sections: clip dots to the real outline (not the bounding box) so the
  // placeholder follows the section's actual shape. Oversample the grid by the
  // bbox/polygon area ratio so ~capacity dots survive the clip.
  const polyPts = (section.shape === 'polygon' && section.points && section.points.length >= 3)
    ? flattenPolygonOutline(section.points, section.edgeCurves, section.edgeSkew)
    : null
  let targetCount = capacity
  if (polyPts) {
    let area2 = 0
    for (let i = 0, j = polyPts.length - 1; i < polyPts.length; j = i++) {
      area2 += (polyPts[j][0] + polyPts[i][0]) * (polyPts[j][1] - polyPts[i][1])
    }
    const polyArea = Math.abs(area2 / 2)
    const fill = Math.min(1, Math.max(0.25, polyArea / Math.max(w * h, 1)))
    targetCount = Math.round(capacity / fill)
  }

  const padX = w * 0.1
  const padY = h * 0.1
  const innerW = w - padX * 2
  const innerH = h - padY * 2

  let cols: number, rows: number
  if (isVertical) {
    // Vertical section: few columns, many rows
    cols = Math.max(1, Math.min(Math.round(innerW / 12), 10))
    rows = Math.max(1, Math.min(Math.ceil(targetCount / cols), 60))
  } else {
    // Horizontal or square: many columns, fewer rows
    const aspect = w / Math.max(h, 1)
    cols = Math.max(3, Math.round(Math.sqrt(targetCount * aspect)))
    rows = Math.max(1, Math.min(Math.ceil(targetCount / cols), 24))
    cols = Math.min(cols, 40)
  }

  const dotR = Math.min(innerW / (cols * 2.4), innerH / (rows * 2.4), 4.0)
  if (dotR < 0.4) return []

  const dots: Array<{ cx: number; cy: number; r: number }> = []
  for (let row = 0; row < rows; row++) {
    const rowY = bounds.y + padY + (row + 0.5) * (innerH / rows)
    for (let col = 0; col < cols; col++) {
      const dotX = bounds.x + padX + (col + 0.5) * (innerW / cols)
      if (polyPts && !ptInPoly(dotX, rowY, polyPts)) continue
      dots.push({ cx: dotX, cy: rowY, r: dotR })
    }
  }
  return dots
}

// --- Rounded polygon path ---
// drawRoundedPolygon + drawArcShape now live in canvas-utils (shared with the
// builder so both render identical section geometry). Imported above.

// --- Hit test: is point inside an arc? ---
function pointInArc(
  px: number,
  py: number,
  arcConfig: NonNullable<LayoutSection['arcConfig']>
): boolean {
  const dx = px - arcConfig.cx
  const dy = py - arcConfig.cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist < arcConfig.innerRadius || dist > arcConfig.outerRadius) return false

  let angle = (Math.atan2(dy, dx) * 180) / Math.PI
  let start = arcConfig.startAngle
  let end = arcConfig.endAngle

  // Normalize angles
  while (angle < start) angle += 360
  while (angle > start + 360) angle -= 360
  while (end < start) end += 360

  return angle >= start && angle <= end
}

// --- Component ---

interface Transform {
  x: number
  y: number
  scale: number
}

interface AnimState {
  fromX: number; fromY: number; fromScale: number
  toX: number; toY: number; toScale: number
  startTime: number; duration: number
}

export interface UnifiedSeatMapHandle {
  zoomToOverview: () => void
  zoomToSection: (sectionKey: string) => void
  applyStatusChanges: (changes: Array<{ seatId: string; status: string; holdSessionId?: string | null }>) => void
  /** The seats the viewer's own session holds (from its own hold / my-holds answers). */
  setOwnHolds: (seatIds: string[]) => void
  /**
   * Force-refresh a section's seats from the server. Drops the cache entry
   * and re-requests, then bumps the render tick so the canvas repaints.
   * Use after admin actions that change seat state (holds, releases, sales).
   */
  refreshSection: (sectionKey: string) => void
  navigateTo: (x: number, y: number) => void
  isZoomedIn: () => boolean
}

export type SelectionMode = 'click' | 'row' | 'box' | 'circle'

interface UnifiedSeatMapProps {
  eventId: string
  layout: ChartLayoutData
  sections: SectionOverview[]
  categories: SeatCategory[]
  selectedSeatIds: Set<string>
  selectedSectionKeys?: Set<string>  // Sections (booths/tables) that are selected/in cart
  sessionId?: string | null  // Current user's session ID for hold distinction
  onSeatClick: (seat: ComputedSeatPosition) => void
  onSeatHover: (seat: ComputedSeatPosition | null, x: number, y: number) => void
  onSectionFocus?: (sectionKey: string | null) => void
  onTransformChange?: (transform: { x: number; y: number; scale: number }) => void
  onFloorSwitch?: (floor: number | null) => void
  onSectionClick?: (sectionKey: string, mode: 'GA' | 'WHOLE_TABLE' | 'BOOTH') => void
  onError?: (error: string) => void
  floors?: FloorInfo[]
  venueShape?: VenueShape
  stage?: LayoutStage
  decorativeShapes?: DecorativeShape[]
  layoutLabels?: LayoutLabel[]
  layoutImages?: LayoutImage[]
  activeFloor?: number | null // null = show all floors
  bottomOffset?: number // Extra px offset for bottom-positioned UI (e.g. floating bar)
  // Multi-select props for admin holds / box office
  selectionMode?: SelectionMode
  onBulkSelect?: (seats: ComputedSeatPosition[]) => void
  allowMultiSelect?: boolean
  // Background image (from chart, shown to buyers)
  backgroundImageUrl?: string | null
  /** Preview switch (?viz=1) for visual treatments awaiting sign-off */
  vizPreview?: boolean
  /** Legend hover: fade FREE seats outside this category */
  highlightCategoryId?: string | null
  // Custom URL prefix for section detail fetch (admin bypass for DRAFT events)
  sectionFetchUrlPrefix?: string
  /** Extra query string appended to section fetches, e.g. 'channel=boxoffice'. */
  sectionFetchQuery?: string
  // Admin mode: allow clicking HELD seats and sections with held seats
  allowHeldSeatInteraction?: boolean
  // Hide "X avail" counts on section labels and tooltips
  showRemainingQuantity?: boolean
  // Highlighted seats (best available preview — amber glow ring)
  highlightedSeatIds?: Set<string>
  // Fullscreen toggle callback (desktop only)
  isFullscreen?: boolean
  onFullscreenToggle?: (fullscreen: boolean) => void
  // Bundled seat data from overview API — pre-populates cache to avoid N+1 requests
  bundledSeatData?: Record<string, unknown> | null
  /** The caller is still fetching bundledSeatData: hold the per-section
   *  requests until it lands (or fails, when this goes false without data). */
  seatBundlePending?: boolean
  /** Fires when the view leaves (true) or returns to (false) the fitted overview */
  onZoomedChange?: (zoomed: boolean) => void
}

const UnifiedSeatMap = forwardRef<UnifiedSeatMapHandle, UnifiedSeatMapProps>(function UnifiedSeatMap(
  {
    eventId,
    layout,
    sections,
    categories,
    selectedSeatIds,
    selectedSectionKeys,
    sessionId,
    onSeatClick,
    onSeatHover,
    onSectionFocus,
    onTransformChange,
    onFloorSwitch,
    onSectionClick,
    onError,
    floors,
    venueShape,
    stage,
    decorativeShapes,
    layoutLabels,
    layoutImages,
    backgroundImageUrl,
    vizPreview = false,
    highlightCategoryId = null,
    activeFloor,
    bottomOffset = 0,
    selectionMode = 'click',
    onBulkSelect,
    allowMultiSelect = false,
    sectionFetchUrlPrefix,
    sectionFetchQuery,
    allowHeldSeatInteraction = false,
    showRemainingQuantity = true,
    highlightedSeatIds,
    isFullscreen = false,
    onFullscreenToggle,
    bundledSeatData,
    seatBundlePending = false,
    onZoomedChange,
  },
  ref
) {
  const formatPrice = useFormatPrice()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map())

  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })
  const [transform, setTransform] = useState<Transform>({ x: 0, y: 0, scale: 1 })
  const hasInitialFitRef = useRef(false)
  const [hoveredSeatId, setHoveredSeatId] = useState<string | null>(null)
  const [hoveredSeat, setHoveredSeat] = useState<ComputedSeatPosition | null>(null)
  const [hoveredSectionKey, setHoveredSectionKey] = useState<string | null>(null)
  const [focusedSectionKey, setFocusedSectionKey] = useState<string | null>(null)
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null)
  const [seatHoverPos, setSeatHoverPos] = useState<{ x: number; y: number } | null>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [renderTick, setRenderTick] = useState(0) // forces re-render when cache loads

  // Keyboard navigation state
  const [keyboardFocusedSectionIdx, setKeyboardFocusedSectionIdx] = useState<number>(-1)
  const [keyboardMode, setKeyboardMode] = useState(false)

  // Mobile floating context (section + row shown when zoomed in)
  const [mobileContext, setMobileContext] = useState<{ sectionName: string; rowLabel?: string } | null>(null)

  // Canvas 2D perspective tilt config (applied in draw callback, not CSS)
  const perspectiveEnabled = layout.visualConfig?.perspectiveTilt === true
  const perspectiveAngle = layout.visualConfig?.perspectiveAngle ?? 8

  // Multi-select drawing state (for box and circle modes)
  const [dragBoxState, setDragBoxState] = useState<BoxSelection | null>(null)
  const [circleState, setCircleState] = useState<CircleSelection | null>(null)
  const [isDrawingSelection, setIsDrawingSelection] = useState(false)
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null)

  // Refs for draw-hot values (avoid draw function recreation on hover/focus changes)
  const hoveredSeatIdRef = useRef(hoveredSeatId)
  const hoveredSectionKeyRef = useRef(hoveredSectionKey)
  const focusedSectionKeyRef = useRef(focusedSectionKey)
  const selectedSeatIdsRef = useRef(selectedSeatIds)
  const selectedSectionKeysRef = useRef(selectedSectionKeys)
  const keyboardFocusedSectionIdxRef = useRef(keyboardFocusedSectionIdx)
  const transformRef = useRef(transform)
  hoveredSeatIdRef.current = hoveredSeatId
  hoveredSectionKeyRef.current = hoveredSectionKey
  focusedSectionKeyRef.current = focusedSectionKey
  selectedSeatIdsRef.current = selectedSeatIds
  selectedSectionKeysRef.current = selectedSectionKeys
  keyboardFocusedSectionIdxRef.current = keyboardFocusedSectionIdx
  transformRef.current = transform

  const animRef = useRef<AnimState | null>(null)
  const dragStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 })
  const mouseDownRef = useRef(false)
  const isDraggingRef = useRef(false)
  const pendingHoverRef = useRef<number | null>(null)
  const drawRef = useRef<(() => void) | null>(null)
  const isMobileRef = useRef(typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches)

  // --- Gesture frames (see lib/seat-map/gesture-frame-cache) ---
  // While a pan, pinch, wheel zoom or zoom animation moves a map that is slow
  // to paint on this device, each frame draws bitmaps of the static layers
  // moved to the new view, with the section labels drawn live on top; the
  // map is painted normally once the gesture ends. A gesture "lasts" until
  // this time (touch / mouse / wheel); a zoom animation or momentum glide
  // counts while it runs.
  const gestureUntilRef = useRef(0)
  // The canvas shows a gesture frame, so a normal paint is still owed.
  const gestureFrameShownRef = useRef(false)
  // This gesture's way of drawing frames (null: no gesture) and what the
  // last normal frames took, start of paint to the next frame.
  const gestureModeRef = useRef<{ mode: 'live' | 'bitmap' | null; lastLiveFrameMs: number | null; slowLiveFrames: number }>({ mode: null, lastLiveFrameMs: null, slowLiveFrames: 0 })
  // The view of the previous gesture frame (which way the gesture zooms).
  const lastGestureViewRef = useRef<Transform | null>(null)
  const gestureSettleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The bitmaps: the main one (the view and a margin round it), the overview
  // one (the whole map, shown wherever the main one does not reach), the one
  // being painted a band per frame to take over from the main one, and a
  // spare canvas to paint the next one into.
  const gestureLayersRef = useRef<GestureLayers>({ backdrop: null, main: null, job: null, spare: null })
  const gesturePrepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const gesturePrepRef = useRef<((budgetMs: number) => boolean) | null>(null)
  const gesturePrepActiveRef = useRef(false)
  // Bumped whenever loaded seats change in place (status polling, a section
  // landing or refetched): the bitmap was painted from the old states.
  const seatDataVersionRef = useRef(0)
  /** A move of the map by the user: gesture frames until it has been still for a moment. */
  const markGesture = useCallback(() => {
    gestureUntilRef.current = performance.now() + GESTURE_HOLD_MS
  }, [])
  const inGesture = useCallback(() => (
    performance.now() < gestureUntilRef.current || animRef.current != null || momentumRafRef.current != null
  ), [])
  /** Paint the map normally now if the canvas shows a gesture frame and the gesture is over. */
  const settleGesture = useCallback(() => {
    if (gestureFrameShownRef.current && !inGesture()) requestAnimationFrame(() => drawRef.current?.())
  }, [inGesture])
  /** The user let go: no more gesture frames (momentum, if any, keeps its own). */
  const endGesture = useCallback(() => {
    gestureUntilRef.current = 0
    settleGesture()
  }, [settleGesture])
  /** Bring the gesture bitmaps up to date in idle time, a moment from now:
   *  one band per idle slot, for as long as there is something to paint. */
  const scheduleGesturePrep = useCallback(() => {
    if (!GESTURE_BITMAPS) return
    const step = () => {
      if (gesturePrepActiveRef.current) return
      gesturePrepActiveRef.current = true
      whenIdle((ms) => {
        gesturePrepActiveRef.current = false
        if (gesturePrepRef.current?.(ms)) step()
      })
    }
    if (gesturePrepTimerRef.current) clearTimeout(gesturePrepTimerRef.current)
    gesturePrepTimerRef.current = setTimeout(() => {
      gesturePrepTimerRef.current = null
      step()
    }, GESTURE_PREP_DELAY_MS)
  }, [])

  // Natural size of the background image once it has loaded — the overview
  // frame includes the image, which it can only place once it knows its size.
  const [bgImageSize, setBgImageSize] = useState<{ w: number; h: number } | null>(null)
  // Height of the desktop state key along the bottom edge (0 when hidden), so
  // the overview frames the chart above it instead of under it.
  const [legendH, setLegendH] = useState(0)
  const legendRo = useRef<ResizeObserver | null>(null)
  const legendRef = useCallback((el: HTMLDivElement | null) => {
    legendRo.current?.disconnect()
    if (!el) { setLegendH(0); return }
    const measure = () => setLegendH(el.offsetHeight)
    measure()
    if (typeof ResizeObserver !== 'undefined') {
      legendRo.current = new ResizeObserver(measure)
      legendRo.current.observe(el)
    }
  }, [])

  // What is actually drawn: sections, decorative shapes, the stage, labels,
  // the background image and placed images. The overview frames THIS, not the
  // designer's whole canvas: a chart drawn in part of a large canvas opened
  // off-centre (on a phone, mostly hidden under the bottom panel). Leaving the
  // images out cropped charts whose artwork IS the venue (GCA's stage logo).
  const contentBox = useMemo(() => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    const add = (x: number, y: number, w = 0, h = 0) => {
      if (![x, y, w, h].every(Number.isFinite)) return
      minX = Math.min(minX, x); minY = Math.min(minY, y)
      maxX = Math.max(maxX, x + w); maxY = Math.max(maxY, y + h)
    }
    for (const sec of layout.sections || []) { const b = getSectionBounds(sec); add(b.x, b.y, b.w, b.h) }
    for (const sh of decorativeShapes || []) add(sh.x, sh.y, sh.width, sh.height)
    if (stage && stage.visible !== false) add(stage.x, stage.y, stage.width, stage.height)
    for (const lb of layoutLabels || []) { const pad = (lb.fontSize || 14) * 2; add(lb.x - pad * 2, lb.y - pad, pad * 4, pad * 2) }
    // Placed images draw unrotated at their own box (see 3c in draw)
    for (const li of layoutImages || []) add(li.x, li.y, li.width, li.height)
    if (backgroundImageUrl && bgImageSize) {
      const bg = backgroundImageRect(layout, bgImageSize.w, bgImageSize.h)
      const b = rotatedBox(bg.cx, bg.cy, bg.w, bg.h, bg.rotation)
      add(b.x, b.y, b.w, b.h)
    }
    if (!Number.isFinite(minX) || maxX <= minX || maxY <= minY) return { x: 0, y: 0, w: layout.width, h: layout.height }
    const pad = Math.max(maxX - minX, maxY - minY) * 0.02
    return { x: minX - pad, y: minY - pad, w: maxX - minX + pad * 2, h: maxY - minY + pad * 2 }
  }, [layout, decorativeShapes, stage, layoutLabels, layoutImages, backgroundImageUrl, bgImageSize])
  // Everything a paint can put on the canvas (the content plus the venue
  // shape drawn under it): the gesture bitmap holds no more than this.
  const paintBox = useMemo(() => {
    if (!venueShape) return contentBox
    const x0 = Math.min(contentBox.x, venueShape.x), y0 = Math.min(contentBox.y, venueShape.y)
    const x1 = Math.max(contentBox.x + contentBox.w, venueShape.x + venueShape.width)
    const y1 = Math.max(contentBox.y + contentBox.h, venueShape.y + venueShape.height)
    return [x0, y0, x1, y1].every(Number.isFinite) ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : contentBox
  }, [contentBox, venueShape])
  // Height left for the map above bottom UI (e.g. the phone's Best Available panel).
  const viewH = Math.max(1, dimensions.height - (bottomOffset || 0))
  // Narrow (phone portrait) screens get the zoom controls as one row along
  // the bottom edge: the old vertical stack sat over the chart's right edge
  // (East Lawn's label) at the overview. A wide chart on a phone is
  // width-bound, so reserving that strip costs it nothing.
  // (Not beside the desktop state key, which owns the bottom-left corner.)
  const narrowControls = dimensions.width > 0 && dimensions.width < 640 && legendH === 0
  // The overview additionally keeps clear of the desktop state key and of
  // the phone's zoom row.
  const fitH = Math.max(1, viewH - (legendH > 0 ? legendH + 16 : 0) - (narrowControls ? 64 : 0))
  // Wider screens keep the vertical stack on the right edge, so the overview
  // is framed in the width left of it (it clipped East Lawn's corner).
  const fitW = Math.max(1, dimensions.width - (narrowControls ? 0 : 64))

  // Fit scale (overview level)
  const fitScale = useMemo(() => {
    if (contentBox.w <= 0 || contentBox.h <= 0) return 1
    const scaleX = fitW / contentBox.w
    const scaleY = fitH / contentBox.h
    return Math.min(scaleX, scaleY) * 0.96
  }, [contentBox.w, contentBox.h, fitW, fitH])
  // Zoom-out floor: the overview itself (with a little slack). A fixed 0.1
  // stopped phones above their overview (fit 0.034-0.106) and let desktop
  // shrink the chart to a speck below it.
  const minScale = Math.min(1, fitScale * 0.9)
  // Deepest zoom: a seat about finger-sized (15x let seats grow to giant
  // balls). Never below twice the overview.
  const MAX_SCALE = useMemo(() => {
    const r = medianSeatRadius(layout.sections)
    const cap = r ? MAX_SEAT_SCREEN_PX / (2 * r) : 4
    return Math.max(Math.min(15, cap), fitScale * 2)
  }, [layout.sections, fitScale])
  const clampScale = useCallback((s: number) => Math.max(minScale, Math.min(MAX_SCALE, s)), [minScale, MAX_SCALE])

  // The transform that frames the whole chart above the bottom UI.
  const overviewTransform = useMemo(() => ({
    x: (fitW - contentBox.w * fitScale) / 2 - contentBox.x * fitScale,
    y: (fitH - contentBox.h * fitScale) / 2 - contentBox.y * fitScale,
    scale: fitScale,
  }), [fitW, contentBox, fitScale, fitH])

  const isZoomedIn = transform.scale > fitScale * 1.5

  // Tell the parent when the view leaves the overview (zoomed in or panned
  // away) — it hangs the phone Overview button and the minimap on this, and
  // its own estimate compared against the designer's whole canvas.
  const zoomedAwayRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (!onZoomedChange || !hasInitialFitRef.current) return
    const panned = Math.hypot(transform.x - overviewTransform.x, transform.y - overviewTransform.y)
    const zoomed = transform.scale > fitScale * 1.15 || panned > Math.max(40, Math.min(dimensions.width, viewH) * 0.12)
    if (zoomedAwayRef.current !== zoomed) {
      zoomedAwayRef.current = zoomed
      onZoomedChange(zoomed)
    }
  }, [transform, overviewTransform, fitScale, dimensions.width, viewH, onZoomedChange])

  // Focus is only cleared by: clicking Overview button, clicking a different section,
  // or clicking empty space. Never auto-cleared by zoom level.

  // Section data maps
  const sectionDataMap = useMemo(
    () => new Map(sections.map(s => [s.sectionKey, s])),
    [sections]
  )
  const categoryMap = useMemo(
    () => new Map(categories.map(c => [c.id, c])),
    [categories]
  )
  const layoutSectionMap = useMemo(
    () => new Map(layout.sections.map(s => [s.key, s])),
    [layout.sections]
  )
  // Label priority order (largest section first) — fixed for a layout, so it
  // is sorted once rather than on every frame. Stable sort, same input order.
  const sectionsBySize = useMemo(
    () => Array.from(layoutSectionMap.entries()).sort(([, a], [, b]) => {
      const aB = getSectionBounds(a), bB = getSectionBounds(b)
      return (bB.w * bB.h) - (aB.w * aB.h)
    }),
    [layoutSectionMap]
  )
  // Hit-test order: SEATED before TABLE/BOOTH before GA (checked from the end)
  const sectionHitOrder = useMemo(
    () => [...layout.sections].sort((a, b) => {
      const aType = a.sectionType || sectionDataMap.get(a.key)?.sectionType || 'SEATED'
      const bType = b.sectionType || sectionDataMap.get(b.key)?.sectionType || 'SEATED'
      const priority = (t: string) => t === 'GA' ? 0 : t === 'TABLE' || t === 'BOOTH' ? 1 : 2
      return priority(aType) - priority(bType)
    }),
    [layout.sections, sectionDataMap]
  )

  // Section seat cache with debounced redraw to prevent flicker on bulk loads
  const renderDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debouncedRenderTick = useCallback(() => {
    seatDataVersionRef.current++
    if (renderDebounceRef.current) clearTimeout(renderDebounceRef.current)
    renderDebounceRef.current = setTimeout(() => {
      setRenderTick(t => t + 1)
      // Also directly trigger a draw frame — React re-render alone may not
      // trigger requestAnimationFrame if the component doesn't re-render fast enough
      requestAnimationFrame(() => drawRef.current?.())
    }, 16)
  }, [])

  const cacheRef = useRef<SectionSeatCache | null>(null)
  if (!cacheRef.current || cacheRef.current['eventId'] !== eventId) {
    cacheRef.current = new SectionSeatCache(
      eventId,
      layout.sections,
      categories,
      debouncedRenderTick,
      onError,
      sectionFetchUrlPrefix,
      sectionFetchQuery
    )
  }
  const cache = cacheRef.current

  // Pre-populate cache from bundled data (instant, no API calls)
  // Then eagerly preload any remaining sections
  const eagerPreloadedRef = useRef(false)
  const prePopulatedRef = useRef<unknown>(null)
  // Read by the lazy loader's timer. Cleared HERE, after the bundle has been
  // put in the cache — not during render: on a slow phone a lazy-load timer
  // could fire between the render that ended the wait and this effect, see
  // "not pending" with the cache still empty, and fetch every visible
  // section one by one anyway.
  const seatBundlePendingRef = useRef(seatBundlePending)
  useEffect(() => {
    // A bundle can arrive after mount (the storefront fetches it once the
    // page is up); sections already loaded on their own are kept.
    if (bundledSeatData && prePopulatedRef.current !== bundledSeatData) {
      prePopulatedRef.current = bundledSeatData
      cache.prePopulate(bundledSeatData as Parameters<typeof cache.prePopulate>[0])
    }
    seatBundlePendingRef.current = seatBundlePending
    if (seatBundlePending || eagerPreloadedRef.current) return
    eagerPreloadedRef.current = true
    // Load any sections not in the bundle
    for (const ls of layout.sections) {
      if (cache.shouldLoadSeats(ls.key) && !cache.has(ls.key)) {
        cache.request(ls.key)
      }
    }
  }, [layout.sections, cache, bundledSeatData, seatBundlePending])

  // CSS design system colors — reads from CSS variables to respect dark/light mode
  const colorsRef = useRef({
    primary: '#3b82f6',
    primaryRGB: '59 130 246',
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textInverse: '#ffffff',
    success: '#22c55e',
    warning: '#f59e0b',
    surfaceBorder: '#e5e7eb',
    surfaceBg: '#f8fafc',
    surface: '#0f172a',
  })

  useEffect(() => {
    const readColors = () => {
      try {
        // Read from the nearest container we own so callers can override the
        // palette by setting CSS variables on a wrapping element (e.g. admin
        // surfaces forcing a light palette instead of the always-dark root).
        // Fall back to document root if the container isn't mounted yet.
        const scope = containerRef.current ?? document.documentElement
        const root = getComputedStyle(scope)
        const rgb = (name: string, fallback: string) => {
          const v = root.getPropertyValue(name).trim()
          return v || fallback
        }
        const toHex = (triplet: string) => {
          const parts = triplet.split(' ').map(Number)
          if (parts.length !== 3) return '#888888'
          return `#${parts.map(n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('')}`
        }
        const primaryRGB = rgb('--color-primary', '59 130 246')
        colorsRef.current = {
          primary: toHex(primaryRGB),
          primaryRGB,
          textPrimary: toHex(rgb('--color-text-primary', '17 24 39')),
          textSecondary: toHex(rgb('--color-text-secondary', '107 114 128')),
          textInverse: toHex(rgb('--color-text-inverse', '255 255 255')),
          success: toHex(rgb('--color-success', '34 197 94')),
          warning: toHex(rgb('--color-warning', '245 158 11')),
          surfaceBorder: toHex(rgb('--color-surface-border', '229 231 235')),
          surfaceBg: toHex(rgb('--color-surface-secondary', '247 248 250')),
          surface: toHex(rgb('--color-surface', '15 23 42')),
        }
      } catch {
        // Use fallback values (dark defaults)
        colorsRef.current = {
          primary: '#3b82f6',
          primaryRGB: '59 130 246',
          textPrimary: '#f1f5f9',
          textSecondary: '#cbd5e1',
          textInverse: '#0f172a',
          success: '#22c55e',
          warning: '#f59e0b',
          surfaceBorder: '#334155',
          surfaceBg: '#020617',
          surface: '#0f172a',
        }
      }
    }

    readColors()

    // Re-read when branding/theme changes (DynamicStyles injects new CSS)
    const observer = new MutationObserver(() => { readColors() })
    observer.observe(document.head, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  // --- Resize observer ---
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const observer = new ResizeObserver(entries => {
      const e = entries[0]
      if (e) setDimensions({ width: e.contentRect.width, height: e.contentRect.height })
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  // Reset initial fit when the layout changes (new chart loaded)
  const prevLayoutRef = useRef({ w: layout.width, h: layout.height })
  useEffect(() => {
    if (prevLayoutRef.current.w !== layout.width || prevLayoutRef.current.h !== layout.height) {
      hasInitialFitRef.current = false
      prevLayoutRef.current = { w: layout.width, h: layout.height }
    }
  }, [layout.width, layout.height])

  // --- Auto-fit: center map on load, preserve zoom on resize ---
  // On initial load (or new layout), fit the map centered in the container.
  // On subsequent container resizes (e.g. mobile browser chrome show/hide,
  // dvh changes, orientation), keep the user's current view stable.
  const prevFitScaleRef = useRef(0)
  const prevViewRef = useRef({ w: 0, h: 0, viewH: 0 })
  const prevOverviewRef = useRef(overviewTransform)
  useEffect(() => {
    if (layout.width <= 0 || layout.height <= 0 || dimensions.width <= 0) return

    const prev = prevViewRef.current
    prevViewRef.current = { w: dimensions.width, h: dimensions.height, viewH }
    const prevOverview = prevOverviewRef.current
    prevOverviewRef.current = overviewTransform

    if (!hasInitialFitRef.current) {
      // First time: fit to view
      hasInitialFitRef.current = true
      prevFitScaleRef.current = fitScale
      setTransform(overviewTransform)
      return
    }

    const oldFitScale = prevFitScaleRef.current
    prevFitScaleRef.current = fitScale
    const containerResized = prev.w !== dimensions.width || prev.h !== dimensions.height
    const viewHDelta = viewH - prev.viewH
    const overviewMoved = prevOverview.x !== overviewTransform.x || prevOverview.y !== overviewTransform.y || prevOverview.scale !== overviewTransform.scale
    if (oldFitScale <= 0 || (!overviewMoved && !containerResized && viewHDelta === 0)) return

    // A zoom animation in flight (e.g. Best Available picked seats and the
    // panel collapsed in the same tick) lands where the visible area now is.
    if (animRef.current && !containerResized) {
      animRef.current.toY += viewHDelta / 2
      return
    }

    setTransform(t => {
      // If user is at or near overview, just re-center cleanly
      if (t.scale <= oldFitScale * 1.2) {
        return overviewTransform
      }

      // Only the bottom UI changed (the phone panel shrinking to the seat
      // bar once a seat is picked) or the framed content grew (background
      // image loaded): keep the zoom, and move the view by half the change so
      // what was centred in the visible area stays centred in it.
      if (!containerResized) {
        return viewHDelta === 0 ? t : { ...t, y: t.y + viewHDelta / 2 }
      }

      // Container resized while zoomed in: maintain the world-space centre of
      // the visible area and scale proportionally so the zoom ratio stays.
      const zoomRatio = t.scale / oldFitScale
      const newScale = fitScale * zoomRatio
      const oldCx = prev.w / 2
      const oldCy = prev.viewH / 2
      const worldX = (oldCx - t.x) / t.scale
      const worldY = (oldCy - t.y) / t.scale
      const newCx = dimensions.width / 2
      const newCy = viewH / 2
      return {
        scale: newScale,
        x: newCx - worldX * newScale,
        y: newCy - worldY * newScale,
      }
    })
  }, [layout.width, layout.height, dimensions.width, dimensions.height, fitScale, overviewTransform, viewH])

  // --- Notify parent of transform changes (for minimap sync) ---
  useEffect(() => {
    onTransformChange?.(transform)
  }, [transform, onTransformChange])

  // Load placed images
  useEffect(() => {
    if (!layoutImages || layoutImages.length === 0) return
    for (const li of layoutImages) {
      if (!imageCacheRef.current.has(li.url)) {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => { imageCacheRef.current.set(li.url, img) }
        img.onerror = () => {
          // CORS fallback
          const fb = new Image()
          fb.onload = () => { imageCacheRef.current.set(li.url, fb) }
          fb.src = li.url
        }
        img.src = sameOrigin(li.url)
      }
    }
  }, [layoutImages])

  // Load shape logos and stage logo
  useEffect(() => {
    const urls: string[] = []
    if (decorativeShapes) {
      for (const shape of decorativeShapes) {
        if (shape.logoUrl) urls.push(shape.logoUrl)
      }
    }
    if (stage?.logoUrl) urls.push(stage.logoUrl)
    for (const url of urls) {
      if (!imageCacheRef.current.has(url)) {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          imageCacheRef.current.set(url, img)
          requestAnimationFrame(() => drawRef.current?.())
        }
        img.onerror = () => {
          // CORS fallback
          const fb = new Image()
          fb.onload = () => {
            imageCacheRef.current.set(url, fb)
            requestAnimationFrame(() => drawRef.current?.())
          }
          fb.src = url
        }
        img.src = sameOrigin(url)
      }
    }
  }, [decorativeShapes, stage])

  // Load background image
  const bgImageRef = useRef<HTMLImageElement | null>(null)
  useEffect(() => {
    if (!backgroundImageUrl) { bgImageRef.current = null; setBgImageSize(null); return }
    const loaded = (im: HTMLImageElement) => {
      bgImageRef.current = im
      const w = im.naturalWidth || im.width, h = im.naturalHeight || im.height
      setBgImageSize(w > 0 && h > 0 ? { w, h } : null)
    }
    if (imageCacheRef.current.has(backgroundImageUrl)) {
      loaded(imageCacheRef.current.get(backgroundImageUrl)!)
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      loaded(img)
      imageCacheRef.current.set(backgroundImageUrl, img)
      requestAnimationFrame(() => drawRef.current?.())
    }
    img.onerror = () => {
      // CORS rejected — retry without crossOrigin (image will taint canvas but at least renders)
      const fallback = new Image()
      fallback.onload = () => {
        loaded(fallback)
        imageCacheRef.current.set(backgroundImageUrl, fallback)
        requestAnimationFrame(() => drawRef.current?.())
      }
      fallback.src = backgroundImageUrl
    }
    img.src = sameOrigin(backgroundImageUrl)
  }, [backgroundImageUrl])

  // --- Lazy-load sections that are visible at detail zoom (debounced) ---
  // Triggers on both zoom AND pan so newly-visible sections load promptly
  const lazyLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lazyLoadSections = useCallback(() => {
    // Every section's seats are on their way in one request; fetching the
    // visible ones separately as well would only duplicate it.
    if (seatBundlePendingRef.current) return
    for (const ls of layout.sections) {
      if (!cache.shouldLoadSeats(ls.key)) continue
      let sectionScreenW = ls.width * transform.scale
      if (ls.shape === 'arc' && ls.arcConfig) {
        const bounds = getSectionBounds(ls)
        sectionScreenW = bounds.w * transform.scale
      }
      if (sectionScreenW >= DETAIL_PRELOAD_PX) {
        cache.request(ls.key)
      }
    }
  }, [transform.scale, layout.sections, cache])

  useEffect(() => {
    if (lazyLoadTimerRef.current) clearTimeout(lazyLoadTimerRef.current)
    lazyLoadTimerRef.current = setTimeout(lazyLoadSections, 30) // 30ms debounce — fast preload
    return () => { if (lazyLoadTimerRef.current) clearTimeout(lazyLoadTimerRef.current) }
  }, [transform.scale, transform.x, transform.y, lazyLoadSections])

  // --- Zoom animation frame ---
  const animateFrame = useCallback((timestamp: number) => {
    const anim = animRef.current
    if (!anim) return

    const elapsed = timestamp - anim.startTime
    const progress = Math.min(elapsed / anim.duration, 1)
    const eased = easeOutCubic(progress)

    setTransform({
      x: anim.fromX + (anim.toX - anim.fromX) * eased,
      y: anim.fromY + (anim.toY - anim.fromY) * eased,
      scale: anim.fromScale + (anim.toScale - anim.fromScale) * eased,
    })

    if (progress < 1) {
      requestAnimationFrame(animateFrame)
    } else {
      animRef.current = null
      setIsAnimating(false)
    }
  }, [])

  // --- Zoom to section ---
  // `focus` (world coords) is the tap point on touch screens, where fitting
  // the whole section leaves 3px seats: there we zoom until a seat is about
  // 8 css px wide, centred on the tap and kept inside the section.
  const zoomToSection = useCallback((sectionKey: string, focus?: { x: number; y: number }) => {
    const section = layoutSectionMap.get(sectionKey)
    if (!section) return

    const bounds = getSectionBounds(section)
    const padFactor = 0.08  // Tighter padding — fill more of the screen
    const padX = bounds.w * padFactor
    const padY = bounds.h * padFactor
    const targetX = bounds.x - padX
    const targetY = bounds.y - padY
    const targetW = bounds.w + padX * 2
    const targetH = bounds.h + padY * 2

    // Fit into the VISIBLE area (above the phone's bottom panel), not the
    // whole canvas — centring on the full height parked the section behind it.
    // Below lg the page floats an Overview button over the top of the map,
    // and phones the zoom row along the bottom: frame the section between
    // them (a tapped table's top and bottom seats sat under both).
    const insetTop = dimensions.width < 1024 ? 56 : 0
    const insetBottom = narrowControls ? 64 : 0
    const availH = Math.max(1, viewH - insetTop - insetBottom)
    const scaleX = (dimensions.width * 0.92) / targetW
    const scaleY = (availH * 0.92) / targetH
    // Cap the focus zoom so clicking a small section doesn't slam to ~1000% and
    // lose the reference frame (B3). 4x keeps seats readable with context.
    let targetScale = Math.min(scaleX, scaleY, 4, MAX_SCALE)
    let centerX = targetX + targetW / 2
    let centerY = targetY + targetH / 2

    if (isMobileRef.current) {
      const loaded = cache.get(sectionKey)
      const seatR = loaded?.[0]?.radius
        || section.rows?.[0]?.seats?.[0]?.radius
        || (section as any).seatSize
        || section.tableConfig?.seatSize
        || 0
      if (seatR > 0) {
        const touchScale = Math.min(MAX_SCALE, 4 / seatR) // diameter * scale >= 8px
        if (touchScale > targetScale) {
          targetScale = touchScale
          const halfW = dimensions.width / 2 / targetScale
          const halfH = availH / 2 / targetScale
          const fx = focus?.x ?? centerX
          const fy = focus?.y ?? centerY
          // Keep the view inside the section: never pan past its edges, and
          // centre it on an axis where it is smaller than the view.
          centerX = bounds.w <= halfW * 2 ? bounds.x + bounds.w / 2
            : Math.max(bounds.x + halfW, Math.min(bounds.x + bounds.w - halfW, fx))
          centerY = bounds.h <= halfH * 2 ? bounds.y + bounds.h / 2
            : Math.max(bounds.y + halfH, Math.min(bounds.y + bounds.h - halfH, fy))
        }
      }
    }

    const toX = dimensions.width / 2 - centerX * targetScale
    const toY = insetTop + availH / 2 - centerY * targetScale

    cache.requestPriority(sectionKey)
    cache.pin(sectionKey) // prevent LRU eviction while focused
    cache.preloadNeighbors(sectionKey)

    setFocusedSectionKey(sectionKey)
    onSectionFocus?.(sectionKey)

    // Skip animation if already close to target (e.g. clicking seats in same section)
    const scaleDiff = Math.abs(transform.scale - targetScale) / targetScale
    const posDiff = Math.abs(transform.x - toX) + Math.abs(transform.y - toY)
    if (scaleDiff < 0.05 && posDiff < 15) return

    animRef.current = {
      fromX: transform.x, fromY: transform.y, fromScale: transform.scale,
      toX, toY, toScale: targetScale,
      startTime: performance.now(), duration: 500,  // Smooth 500ms zoom
    }
    setIsAnimating(true)
    requestAnimationFrame(animateFrame)
  }, [layoutSectionMap, dimensions, viewH, narrowControls, transform, cache, animateFrame, onSectionFocus, MAX_SCALE])

  // --- Zoom to overview ---
  const zoomToOverview = useCallback(() => {
    // Clear focus and unpin cache
    setFocusedSectionKey(null)
    onSectionFocus?.(null)
    cache.unpin()

    animRef.current = {
      fromX: transform.x, fromY: transform.y, fromScale: transform.scale,
      toX: overviewTransform.x, toY: overviewTransform.y, toScale: overviewTransform.scale,
      startTime: performance.now(), duration: 350,
    }
    setIsAnimating(true)
    requestAnimationFrame(animateFrame)
  }, [overviewTransform, transform, animateFrame, onSectionFocus, cache])

  // --- Pan (and optionally zoom) to center a seat after selection ---
  const panToSeat = useCallback((seat: ComputedSeatPosition) => {
    // Determine a good target zoom level — ensure seat circles are large enough to read labels
    // screenRadius = seat.radius * scale  → target ~18px screen radius for readable labels
    const seatWorldRadius = seat.radius || 6
    const minScreenRadius = 16
    const idealScale = minScreenRadius / seatWorldRadius
    // Only zoom in, never zoom out; and cap at 10x
    const targetScale = Math.min(Math.max(transform.scale, idealScale), MAX_SCALE)
    const shouldZoom = targetScale > transform.scale * 1.05

    // Where is the seat on screen right now?
    const canvasX = seat.absX * transform.scale + transform.x
    const canvasY = seat.absY * transform.scale + transform.y

    // If already centered and no zoom needed, skip
    const marginX = dimensions.width * 0.25
    const marginY = viewH * 0.25
    const isCentered = canvasX > marginX && canvasX < dimensions.width - marginX &&
      canvasY > marginY && canvasY < viewH - marginY
    if (isCentered && !shouldZoom) return

    // Animate pan (and zoom if needed) to center on the seat
    const finalScale = shouldZoom ? targetScale : transform.scale
    const toX = dimensions.width / 2 - seat.absX * finalScale
    const toY = viewH / 2 - seat.absY * finalScale

    animRef.current = {
      fromX: transform.x, fromY: transform.y, fromScale: transform.scale,
      toX, toY, toScale: finalScale,
      startTime: performance.now(), duration: shouldZoom ? 350 : 250,
    }
    setIsAnimating(true)
    requestAnimationFrame(animateFrame)
  }, [transform, dimensions, viewH, animateFrame, MAX_SCALE])

  // --- One-time seat path warm-up (big charts) ---
  // Seats are not drawn at all until a zoom makes them visible. On a chart of
  // thousands of seats the FIRST big seat path a canvas fills costs Chromium
  // far more than every later one (one ~370 ms frame at 6x CPU throttle on
  // Africa Gift Box's 6,500 seats, then ~50 ms frames) — a cost the old draw
  // paid at load, when it filled every seat at alpha 0 on every frame. Pay it
  // once, in idle time after the seats load, the same way: one path of the
  // loaded seats filled at alpha 0 on the map canvas. Nothing visible changes.
  const seatWarmStartedRef = useRef(false)
  const unmountedRef = useRef(false)
  useEffect(() => () => { unmountedRef.current = true }, [])
  useEffect(() => {
    if (seatWarmStartedRef.current || typeof window === 'undefined') return
    let total = 0
    for (const seats of cache.getAllLoaded().values()) total += seats.length
    if (total < 2000) return
    seatWarmStartedRef.current = true
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }
    const later = (fn: () => void) => (w.requestIdleCallback ? w.requestIdleCallback(fn, { timeout: 2000 }) : window.setTimeout(fn, 200))
    later(() => {
      const ctx = canvasRef.current?.getContext('2d')
      if (unmountedRef.current || !ctx) return
      const seatsAll: Array<{ x: number; y: number; r: number }> = []
      for (const seats of cache.getAllLoaded().values()) for (const s of seats) seatsAll.push({ x: s.absX, y: s.absY, r: s.radius })
      ctx.save()
      drawSeatBatch(ctx, new Map([['#000000', seatsAll]]), 0)
      ctx.restore()
    })
  }, [renderTick, cache])

  // --- Imperative handle ---
  useImperativeHandle(ref, () => ({
    zoomToOverview,
    zoomToSection,
    applyStatusChanges: (changes) => {
      cache.applyStatusChanges(changes)
      seatDataVersionRef.current++
      // A status change may not repaint the map (that waits for its next
      // paint), but the gesture bitmaps must not show the old states.
      scheduleGesturePrep()
      setRenderTick(t => t + 1)
    },
    setOwnHolds: (seatIds) => {
      cache.setOwnHolds(seatIds)
      seatDataVersionRef.current++
      scheduleGesturePrep()
      setRenderTick(t => t + 1)
    },
    refreshSection: (sectionKey: string) => {
      cache.invalidate(sectionKey)
      seatDataVersionRef.current++
      cache.request(sectionKey)
      setRenderTick(t => t + 1)
    },
    navigateTo: (x: number, y: number) => {
      // The minimap calls this on every move of a drag across it.
      markGesture()
      setTransform(prev => ({ ...prev, x, y }))
    },
    isZoomedIn: () => isZoomedIn,
  }), [zoomToOverview, zoomToSection, cache, isZoomedIn, markGesture, scheduleGesturePrep])

  // --- Should show seats for a section? ---
  // Hysteresis: once we start showing seats for a section, keep showing until
  // the section shrinks well below the threshold. Prevents flashing during zoom.
  const showingSectionsRef = useRef(new Set<string>())

  const shouldShowSeats = useCallback((section: LayoutSection): boolean => {
    if (!cache.shouldLoadSeats(section.key)) return false
    const bounds = getSectionBounds(section)
    // Use larger dimension — narrow tall sections (like sideline seats) need height, not width
    const screenSize = Math.max(bounds.w, bounds.h) * transform.scale
    const wasShowing = showingSectionsRef.current.has(section.key)
    const preloadPx = (section.sectionType === 'TABLE') ? TABLE_PRELOAD_PX : DETAIL_PRELOAD_PX
    // Hysteresis: 80% of threshold to stop showing (prevents flicker at boundary)
    const threshold = wasShowing ? preloadPx * 0.8 : preloadPx
    const show = screenSize >= threshold
    if (show) {
      showingSectionsRef.current.add(section.key)
    } else {
      showingSectionsRef.current.delete(section.key)
    }
    return show
  }, [transform.scale, cache])

  /** Returns 0..1 for how opaque individual seats should be (cross-fade zone) */
  // Seated sections fade from overview to seats together, at the pace of a
  // typical section of the map (the median size). Each section used to fade
  // by its own size, so at one zoom the big blocks had lost their fill while
  // the small ones kept it, and the overview read as random light and dark
  // sections of one price.
  const sharedSeatRadius = useMemo(() => medianSeatRadius(layout.sections.filter(sharesSeatFade)), [layout.sections])
  const sharedFadeSize = useMemo(() => {
    const sizes = layout.sections
      .filter(sharesSeatFade)
      .map(ls => { const b = getSectionBounds(ls); return Math.max(b.w, b.h) })
      .sort((a, b) => a - b)
    return sizes.length ? sizes[Math.floor(sizes.length / 2)] : null
  }, [layout.sections])

  const seatTransitionAlpha = useCallback((section: LayoutSection): number => {
    if (sharedSeatRadius != null && sharesSeatFade(section)) {
      const seatPx = 2 * sharedSeatRadius * transform.scale
      return Math.max(0, Math.min(1, (seatPx - SEAT_FADE_FROM_PX) / (SEAT_FADE_TO_PX - SEAT_FADE_FROM_PX)))
    }
    const bounds = getSectionBounds(section)
    const screenSize = (sharedFadeSize != null && sharesSeatFade(section)
      ? sharedFadeSize
      : Math.max(bounds.w, bounds.h)) * transform.scale
    const isTable = section.sectionType === 'TABLE'
    const fullPx = isTable ? TABLE_DETAIL_PX : DETAIL_SECTION_WIDTH_PX
    const minPx = isTable ? TABLE_PRELOAD_PX : DETAIL_PRELOAD_PX
    if (screenSize >= fullPx) return 1
    if (screenSize <= minPx) return 0
    return (screenSize - minPx) / (fullPx - minPx)
  }, [transform.scale, sharedFadeSize, sharedSeatRadius])

  // --- Hit test ---
  const hitTest = useCallback((canvasX: number, canvasY: number):
    { type: 'seat'; seat: ComputedSeatPosition } |
    { type: 'section'; sectionKey: string } |
    null => {
    let worldX = (canvasX - transform.x) / transform.scale
    let worldY = (canvasY - transform.y) / transform.scale
    // Invert the Canvas 2D tilt (Y-axis compression anchored at bottom)
    if (perspectiveEnabled) {
      const t = perspectiveAngle * 0.006
      const h = (layout.height || dimensions.height) / transform.scale
      worldY = (worldY - h) / (1 - t) + h
    }

    // First: check loaded seats in visible detail sections (active floor only).
    // Seats are hoverable as soon as they're meaningfully visible — the old
    // extra gates (300px section size + full detail width) left dots drawn
    // on screen but inert, which QA read as "no tooltips at all".
    const focused = focusedSectionKeyRef.current
    for (const [sectionKey, seats] of cache.getAllLoaded()) {
      if (focused && sectionKey !== focused) continue  // Only test seats in the focused section
      const section = layoutSectionMap.get(sectionKey)
      if (!section || !shouldShowSeats(section)) continue
      if (!focused && seatTransitionAlpha(section) < 0.35) continue  // seats still faded out
      const sectionOnActiveFloor = activeFloor == null || section.floor == null || section.floor === activeFloor
      if (!sectionOnActiveFloor) continue

      for (const seat of seats) {
        const dx = worldX - seat.absX
        const dy = worldY - seat.absY
        // Generous hit radius — larger on mobile for easier tapping
        const isMobile = isMobileRef.current
        const minHitWorld = isMobile ? 6 : 3  // minimum hit radius in world units
        const hitR = Math.max(seat.radius + 2, minHitWorld)
        if (dx * dx + dy * dy <= hitR * hitR) {
          return { type: 'seat', seat }
        }
      }
    }

    // Second: check section shapes (all floors - clicking dimmed section switches floor)
    // Sort so SEATED sections are checked BEFORE GA/TABLE/BOOTH — prevents GA from
    // intercepting clicks on adjacent seated sections. Since we iterate in reverse,
    // GA (priority 0) goes first in array so SEATED (priority 2) is at the end = checked first.
    const sectionCheckOrder = sectionHitOrder
    for (let i = sectionCheckOrder.length - 1; i >= 0; i--) {
      const ls = sectionCheckOrder[i]
      const sd = sectionDataMap.get(ls.key)
      const sType = ls.sectionType || sd?.sectionType
      if (sType === 'STAGE') continue

      // Any part of the section counts: a GA zone drawn in three places is one
      // clickable zone, and clicking any of its blocks selects the whole thing.
      if (pointInSection(worldX, worldY, ls)) {
        return { type: 'section', sectionKey: ls.key }
      }
    }

    return null
  }, [transform, cache, layoutSectionMap, sectionDataMap, sectionHitOrder, shouldShowSeats, seatTransitionAlpha, activeFloor, perspectiveEnabled, perspectiveAngle, dimensions.height, layout.height])

  // --- Paint ---
  // Paints the map for the view `transform` onto a canvas of cw x ch CSS px
  // at dpr device px per CSS px. `layers`: 'all' is the map as it shows at
  // rest; 'base' everything except the section labels and the keyboard /
  // selection overlays (the gesture bitmap); 'labels' only those, drawn live
  // over the bitmap during a gesture.
  // `originY` moves the whole paint down that many device px (a bitmap band).
  const paintScene = useCallback((ctx: CanvasRenderingContext2D, transform: Transform, cw: number, ch: number, dpr: number, layers: PaintLayers, originY = 0) => {
    // Read volatile values from refs (avoids draw recreation on hover/select changes)
    const hoveredSeatId = hoveredSeatIdRef.current
    const hoveredSectionKey = hoveredSectionKeyRef.current
    const focusedSectionKey = focusedSectionKeyRef.current
    const selectedSeatIds = selectedSeatIdsRef.current
    const selectedSectionKeys = selectedSectionKeysRef.current

    ctx.setTransform(dpr, 0, 0, dpr, 0, originY)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    const colors = colorsRef.current

    // Visual config — clean flat defaults matching seats.io aesthetic
    const vc: MapVisualConfig = layout.visualConfig || {}
    const vcFill = vc.sectionFillOpacity ?? 0.85
    const vcFillSoldOut = vc.sectionFillSoldOutOpacity ?? 0.20
    const vcBorder = vc.sectionBorderWidth ?? 1.0
    const vcLabelStyle = vc.labelStyle ?? 'auto'
    const vcLabelScale = vc.labelFontScale ?? 1.0
    const vcSubScale = vc.subtitleFontScale ?? 1.0
    const vcDotOpacity = vc.dotOpacity ?? 0.4
    const vcDotVisible = vc.dotVisible !== false
    const vcHoverBright = vc.hoverBrightness ?? 0.15
    const vcSeatGradient = vc.seatGradient !== false // enabled by default for polished 3D look
    const vcGradient = vc.sectionGradient !== false // default true
    const vcShadow = vc.sectionShadow !== false // default true
    const vcShadowIntensity = vc.sectionShadowIntensity ?? 0.15

    // Background — clean flat fill
    if (layers !== 'labels') {
      ctx.fillStyle = colors.surfaceBg
      ctx.fillRect(0, 0, cw, ch)
    }

    ctx.save()
    ctx.translate(transform.x, transform.y)
    ctx.scale(transform.scale, transform.scale)

    // Canvas 2D perspective tilt: compress Y-axis toward top to simulate looking down
    if (perspectiveEnabled) {
      const t = perspectiveAngle * 0.006 // 8deg → 0.048
      const h = (layout.height || ch) / transform.scale
      ctx.translate(0, h)
      ctx.transform(1, 0, 0, 1 - t, 0, 0)
      ctx.translate(0, -h)
    }

    // Viewport bounds for culling — expand generously when perspective is on
    // because the Y-axis compression shifts seat positions outside naive bounds
    // During animation, use generous viewport padding to prevent section pop-in/out
    // (a gesture bitmap already holds a margin round the view)
    const animPad = isAnimating && layers === 'all' ? (Math.max(cw, ch) / transform.scale) * 0.5 : 0
    const perspectivePad = perspectiveEnabled ? (ch / transform.scale) * 0.5 : 0
    const totalPad = Math.max(animPad, perspectivePad)
    const vpLeft = -transform.x / transform.scale - totalPad
    const vpTop = -transform.y / transform.scale - totalPad
    const vpRight = vpLeft + cw / transform.scale + totalPad * 2
    const vpBottom = vpTop + ch / transform.scale + totalPad * 2

    // A gesture frame draws only the labels and overlays, live, over the
    // bitmap (paintLabels, at the end, is the very same code a rest paint ends on).
    if (layers === 'labels') {
      paintLabels()
      ctx.restore()
      return
    }

    // 1. Venue shape (decorative layer)
    if (venueShape) {
      drawVenueShape(ctx, venueShape)
    }

    // 1a2. Background image (below sections, visible to buyers)
    if (bgImageRef.current) {
      ctx.save()
      // Read from backgroundTransform (new) or visualConfig (legacy)
      const img = bgImageRef.current
      const imgW = img.naturalWidth || img.width || layout.width
      const imgH = img.naturalHeight || img.height || layout.height
      const bg = backgroundImageRect(layout, imgW, imgH)
      ctx.globalAlpha = bg.opacity
      ctx.translate(bg.cx, bg.cy)
      if (bg.rotation) ctx.rotate(bg.rotation * Math.PI / 180)
      ctx.scale(bg.flipX ? -1 : 1, bg.flipY ? -1 : 1)
      ctx.drawImage(img, -bg.w / 2, -bg.h / 2, bg.w, bg.h)
      ctx.restore()
    }

    // 1b. Stage from chart designer (skip if explicitly hidden)
    if (stage && stage.visible !== false) {
      ctx.fillStyle = stage.color || '#1e293b'
      if (stage.shape === 'circle' && stage.cx != null && stage.cy != null && stage.radius != null) {
        // Circle stage (center ring / arena venues)
        ctx.beginPath()
        ctx.arc(stage.cx, stage.cy, stage.radius, 0, Math.PI * 2)
        ctx.fill()
      } else {
        // Rounded rectangle stage (default)
        const r = 8
        ctx.beginPath()
        ctx.moveTo(stage.x + r, stage.y)
        ctx.lineTo(stage.x + stage.width - r, stage.y)
        ctx.arcTo(stage.x + stage.width, stage.y, stage.x + stage.width, stage.y + r, r)
        ctx.lineTo(stage.x + stage.width, stage.y + stage.height - r)
        ctx.arcTo(stage.x + stage.width, stage.y + stage.height, stage.x + stage.width - r, stage.y + stage.height, r)
        ctx.lineTo(stage.x + r, stage.y + stage.height)
        ctx.arcTo(stage.x, stage.y + stage.height, stage.x, stage.y + stage.height - r, r)
        ctx.lineTo(stage.x, stage.y + r)
        ctx.arcTo(stage.x, stage.y, stage.x + r, stage.y, r)
        ctx.closePath()
        ctx.fill()
      }
      if (stage.logoUrl) {
        const logoImg = imageCacheRef.current.get(stage.logoUrl)
        if (logoImg) {
          const scx = stage.x + stage.width / 2
          const scy = stage.y + stage.height / 2
          const maxW = stage.width * 0.7, maxH = stage.height * 0.7
          const scale = Math.min(maxW / logoImg.width, maxH / logoImg.height)
          const lw = logoImg.width * scale, lh = logoImg.height * scale
          ctx.drawImage(logoImg, scx - lw / 2, scy - lh / 2, lw, lh)
        }
      }
      if (stage.label) {
        const stCx = stage.cx ?? (stage.x + stage.width / 2)
        const stCy = stage.cy ?? (stage.y + stage.height / 2)
        const fontSize = Math.min(stage.fontSize || Math.min(stage.width / 8, 16), MAX_VENUE_TEXT_PX / transform.scale)
        const fontWeight = stage.fontWeight || 'bold'
        ctx.fillStyle = stage.textColor || 'rgba(255,255,255,0.7)'
        ctx.font = `${fontWeight} ${fontSize}px ${FONT_FAMILY}`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        const labelY = stage.logoUrl ? stCy + stage.height * 0.35 : stCy
        ctx.fillText(stage.label, stCx, labelY)
      }
    }

    // Compute zoom state from transform directly (avoids stale isZoomedIn closure)
    const drawIsZoomedIn = transform.scale > fitScale * 1.5

    // Read-only view of the loaded seats (cache.get would also re-order the
    // LRU list, allocating a new array per section per frame).
    const loadedSeats = cache.getAllLoaded()

    // 2. Draw sections (flat 2D, opacity-based floor filtering)
    for (const ls of layout.sections) {
      const sd = sectionDataMap.get(ls.key)
      const secTypeCheck = ls.sectionType || sd?.sectionType
      if (secTypeCheck === 'STAGE') continue

      // Viewport culling
      const bounds = getSectionBounds(ls)
      if (bounds.x + bounds.w < vpLeft || bounds.x > vpRight ||
          bounds.y + bounds.h < vpTop || bounds.y > vpBottom) continue

      // Floor-based opacity: active floor = full, others = dimmed
      const isOnActiveFloor = activeFloor == null || ls.floor == null || ls.floor === activeFloor
      const floorDimFactor = isOnActiveFloor ? 1.0 : 0.15

      // Category color: try overview data, then layout section, then default gray
      const catId = sd?.categoryId || (ls as any).categoryId
      const cat = catId ? categoryMap.get(catId) : null
      const secType = ls.sectionType || sd?.sectionType || 'SEATED'
      const isTableOrBooth = secType === 'TABLE' || secType === 'BOOTH'
      const isSoldOut = sd ? (isTableOrBooth
        ? (sd.capacity > 0 && sd.available === 0 && (sd.booked + sd.held) >= sd.capacity)
        : (allowHeldSeatInteraction ? (sd.available === 0 && sd.held === 0) : sd.available === 0)
      ) : false
      const isSectionSelected = selectedSectionKeys?.has(ls.key) ?? false
      const baseSectionColor = (ls as any).fillColor || cat?.color || '#94a3b8'
      // Keep section's own color when selected — just adjust opacity/border, don't replace with primary
      const sectionColor = baseSectionColor
      const isHovered = hoveredSectionKey === ls.key
      const isFocused = focusedSectionKey === ls.key
      // Keep loaded sections fresh in LRU — priority for focused section
      if (cache.has(ls.key)) {
        cache.touch(ls.key)
      } else if (isFocused) {
        // Focused section was evicted — re-request immediately
        cache.requestPriority(ls.key)
      }
      // Overlay alpha follows zoom level, but only once the section's seats
      // are actually in the cache: fading the overview out on zoom alone left
      // a section blank (no fill, no dots, no seats) until its fetch landed.
      const isFocusedHere = focusedSectionKey === ls.key
      const seatsReady = !cache.shouldLoadSeats(ls.key) || (loadedSeats.get(ls.key)?.length ?? 0) > 0
      // A small section fades with the rest even before it is big enough to
      // load seats on its own (when its seats are already here).
      const showsSeats = shouldShowSeats(ls)
        || (sharesSeatFade(ls) && cache.shouldLoadSeats(ls.key) && (loadedSeats.get(ls.key)?.length ?? 0) > 0)
      const zoomAlpha = seatsReady && showsSeats ? seatTransitionAlpha(ls) : 0
      const effectiveTransAlpha = isFocusedHere && seatsReady ? 1 : zoomAlpha

      // Determine section type early (needed for fill logic)
      const sType = ls.sectionType || sd?.sectionType || 'SEATED'
      const isUnitSection = sType === 'GA' || sType === 'BOOTH' ||
        (sType === 'TABLE' && ls.tableConfig?.bookingMode !== 'by-seat')

      // Section fill — interaction-driven opacity:
      // 1. Default (overview): solid overlay at baseFill — hides individual seats
      // 2. Hover: semi-transparent (50%) — peek at seats behind
      // 3. Focused (clicked/zoomed): fade out using transAlpha as zoom progresses
      // Only highlight GA/booth/table sections when selected — never cover seated sections
      const sectionSelectedHighlight = isSectionSelected && isUnitSection
      const soldOutFill = isTableOrBooth ? Math.max(vcFillSoldOut, 0.5) : vcFillSoldOut
      // SEATED/arc sections show individual seats, so their fill should be a light
      // tint — a full-opacity fill on a dark category color (e.g. navy "VIP Bench")
      // renders as a dark box over the seats. GA/table/booth keep the solid fill.
      const baseFill = sectionSelectedHighlight
        ? (vcFill + 0.15)
        : (isSoldOut ? soldOutFill
          : sType === 'GA' ? vcFill * 0.7
          : !isUnitSection ? (sharedSeatRadius != null && sharesSeatFade(ls) ? Math.min(vcFill, 0.7) : Math.min(vcFill, 0.3))
          : vcFill)
      // Overlay fades out as seats become visible
      const isFocusedSection = focusedSectionKey === ls.key
      let effectiveAlpha: number
      if (sectionSelectedHighlight) {
        effectiveAlpha = baseFill
      } else if ((isFocusedSection && seatsReady) || effectiveTransAlpha >= 1) {
        // Focused or fully transitioned: hide overlay completely
        effectiveAlpha = 0
      } else if (effectiveTransAlpha > 0) {
        // Zoom-based fade: overlay fades as zoom increases
        effectiveAlpha = baseFill * (1 - effectiveTransAlpha)
      } else {
        effectiveAlpha = baseFill
      }

      // Hover: brighten fill + add glow (seats.io style)
      const hoverActive = isHovered && !isSoldOut && !isSectionSelected && !isFocusedSection && isOnActiveFloor && effectiveTransAlpha < 0.5
      // A sold-out/held table kept its category colour and only changed fill
      // opacity, which is indistinguishable from available on a phone-sized
      // map. Desaturate it so unavailability reads at a glance, at any zoom.
      const drawColor = hoverActive
        ? lightenColor(sectionColor, 0.15)
        : (isSoldOut && isTableOrBooth ? '#94a3b8' : sectionColor)
      if (hoverActive) {
        effectiveAlpha = Math.max(effectiveAlpha, baseFill * 0.9)
      }
      // Explicit fillColor = the admin deliberately styled this section in
      // the designer, where the fill is always visible under the seats.
      // The seat-zoom fade-to-zero made that color NEVER appear on the
      // storefront ("white boxes"). Keep a persistent tint so the builder
      // look carries over; seats draw on top at full opacity and stay
      // readable. Sections without an explicit fill keep the clean fade.
      const hasExplicitFill = !!(ls as any).fillColor && (ls as any).hideBackground !== true
      if (hasExplicitFill && !isSoldOut) {
        effectiveAlpha = Math.max(effectiveAlpha, 0.28)
      }
      // Every section draws inside its own save/restore, and the alpha is set
      // AFTER the save. Setting it before (and saving only for rotated
      // sections) meant the restore brought the faded alpha back, so after a
      // rotated, faded last section the ring and venue labels drew at alpha 0.
      ctx.save()
      ctx.globalAlpha = effectiveAlpha * floorDimFactor

      // Apply section rotation — matches DesignerCanvas behavior
      const sectionRotated = !!ls.rotation
      if (sectionRotated) {
        const rcx = ls.x + ls.width / 2
        const rcy = ls.y + ls.height / 2
        ctx.translate(rcx, rcy)
        ctx.rotate((ls.rotation! * Math.PI) / 180)
        ctx.translate(-rcx, -rcy)
      }

      // Canvas shadows are blurred over the whole shape, every frame: on a
      // phone, zoomed in, that was a full-screen blur per section on screen,
      // under a fill that was not even visible. Only where the fill shows and
      // the section is small enough for a 3px shadow to be seen at all.
      const fillVisible = effectiveAlpha * floorDimFactor > 0.02 || hoverActive
      const sectionScreenPx = Math.max(getSectionBounds(ls).w, getSectionBounds(ls).h) * transform.scale
      // Section shadow — small constant values (shadow props are screen-space, not affected by transform)
      if (vcShadow && !isSoldOut && (!isFocusedSection || effectiveTransAlpha < 0.5) && effectiveTransAlpha === 0 && fillVisible && sectionScreenPx < 500) {
        ctx.shadowColor = `rgba(0,0,0,${Math.min(vcShadowIntensity, 0.3)})`
        ctx.shadowBlur = 3
        ctx.shadowOffsetY = 1
      }

      // Section fill — gradient or flat (matches DesignerCanvas)
      // Skip fill entirely for "seats only" sections (hideBackground).
      // The flag ALWAYS wins — it used to be suppressed by an explicit
      // fillColor, which made the toggle a silent no-op on any section
      // that ever had a color set. Designer applies the same rule.
      const sectionHidesBg = (ls as any).hideBackground === true
      const useGradient = vcGradient && drawColor.startsWith('#') && !isSoldOut && (!isFocusedSection || effectiveTransAlpha < 0.5)
      ctx.fillStyle = drawColor

      // Hover glow effect
      if (hoverActive) {
        ctx.shadowColor = lightenColor(sectionColor, 0.3)
        ctx.shadowBlur = 6
      }

      const isTableLike = sType === 'TABLE' || sType === 'BOOTH'
      const tableCx = ls.x + ls.width / 2
      const tableCy = ls.y + ls.height / 2
      // TABLE shape type and body radius — matches DesignerCanvas exactly
      // Designer: tableRadius = Math.min(w,h)/2 - 15 for the table body
      const tableShape = ls.tableConfig?.tableShape || (sType === 'BOOTH' ? 'rectangle' : 'round')
      const tableBodyRadius = Math.min(ls.width, ls.height) / 2 - 15

      // Shared geometry input for traceSectionPath — built by the same helper the
      // designer uses so every outline field (points, edgeCurves, edgeSkew, arcConfig)
      // reaches the storefront. Hand-building it here is what dropped the curves.
      // One input per PART: a section drawn in several places (multi-place GA
      // zone) fills and strokes each block with the same colour and state.
      const sectionShapeInputs = getSectionShapeInputs(ls, sType)
      const applyPartGradient = (input: (typeof sectionShapeInputs)[number]) => {
        if (!useGradient) return
        // A gradient is plain geometry + colour stops, interpreted under the
        // transform at fill time, so one built for this part and colour is
        // reused on every later frame instead of rebuilt per frame.
        const cachedGradient = getCachedGradient(input, sectionColor)
        if (cachedGradient) { ctx.fillStyle = cachedGradient; return }
        const before = ctx.fillStyle
        applyPartGradientUncached(input)
        if (ctx.fillStyle !== before && typeof ctx.fillStyle !== 'string') setCachedGradient(input, sectionColor, ctx.fillStyle as CanvasGradient)
      }
      const applyPartGradientUncached = (input: (typeof sectionShapeInputs)[number]) => {
        if (input.shape === 'arc' && input.arcConfig) {
          const ab = input.arcConfig
          const grad = ctx.createLinearGradient(0, ab.cy - ab.outerRadius, 0, ab.cy + ab.outerRadius)
          grad.addColorStop(0, lightenColor(sectionColor, 0.20)); grad.addColorStop(0.4, lightenColor(sectionColor, 0.05)); grad.addColorStop(1, darkenColor(sectionColor, 0.12))
          ctx.fillStyle = grad
        } else if (input.shape === 'polygon' && input.points && input.points.length >= 3) {
          const ys = input.points.map(p => p[1]); const minY = Math.min(...ys), maxY = Math.max(...ys)
          const grad = ctx.createLinearGradient(0, minY, 0, maxY)
          grad.addColorStop(0, lightenColor(sectionColor, 0.20)); grad.addColorStop(0.4, lightenColor(sectionColor, 0.05)); grad.addColorStop(1, darkenColor(sectionColor, 0.12))
          ctx.fillStyle = grad
        } else if (sType === 'TABLE' && ls.tableConfig && tableShape === 'round') {
          const grad = ctx.createRadialGradient(tableCx - tableBodyRadius * 0.3, tableCy - tableBodyRadius * 0.3, tableBodyRadius * 0.1, tableCx, tableCy, tableBodyRadius)
          grad.addColorStop(0, lightenColor(sectionColor, 0.25)); grad.addColorStop(0.5, sectionColor); grad.addColorStop(1, darkenColor(sectionColor, 0.08))
          ctx.fillStyle = grad
        } else if (sType === 'TABLE' && ls.tableConfig) {
          const tx = input.x + 15, ty = input.y + 15, th = input.height - 30
          const grad = ctx.createLinearGradient(tx, ty, tx, ty + th)
          grad.addColorStop(0, lightenColor(sectionColor, 0.20)); grad.addColorStop(0.4, lightenColor(sectionColor, 0.05)); grad.addColorStop(1, darkenColor(sectionColor, 0.12))
          ctx.fillStyle = grad
        } else {
          ctx.fillStyle = createSectionGradient(ctx, input.x, input.y, input.height, sectionColor)
        }
      }

      if (sectionHidesBg || !fillVisible) {
        // Seats-only mode (or a fill faded out under the seats): no section
        // fill, just the outline and seats
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
      } else if (USE_SHARED_SECTION_GEOMETRY) {
        // Unified path: per-part gradient fillStyle, shared outline
        for (const input of sectionShapeInputs) {
          applyPartGradient(input)
          traceSectionPath(ctx, input)
          ctx.fill()
        }
      } else if (ls.shape === 'arc' && ls.arcConfig) {
        if (useGradient) {
          const ab = ls.arcConfig
          const grad = ctx.createLinearGradient(0, ab.cy - ab.outerRadius, 0, ab.cy + ab.outerRadius)
          grad.addColorStop(0, lightenColor(sectionColor, 0.20))
          grad.addColorStop(0.4, lightenColor(sectionColor, 0.05))
          grad.addColorStop(1, darkenColor(sectionColor, 0.12))
          ctx.fillStyle = grad
        }
        drawArcShape(ctx, ls.arcConfig)
        ctx.fill()
      } else if (ls.shape === 'polygon' && ls.points && ls.points.length >= 3) {
        drawRoundedPolygon(ctx, ls.points, 0, ls.edgeCurves, ls.edgeSkew)
        if (useGradient) {
          const ys = ls.points.map(p => p[1])
          const minY = Math.min(...ys), maxY = Math.max(...ys)
          const grad = ctx.createLinearGradient(0, minY, 0, maxY)
          grad.addColorStop(0, lightenColor(sectionColor, 0.20))
          grad.addColorStop(0.4, lightenColor(sectionColor, 0.05))
          grad.addColorStop(1, darkenColor(sectionColor, 0.12))
          ctx.fillStyle = grad
        }
        ctx.fill()
      } else if (sType === 'TABLE' && ls.tableConfig) {
        if (tableShape === 'round') {
          ctx.beginPath()
          ctx.arc(tableCx, tableCy, tableBodyRadius, 0, Math.PI * 2)
          if (useGradient) {
            const grad = ctx.createRadialGradient(tableCx - tableBodyRadius * 0.3, tableCy - tableBodyRadius * 0.3, tableBodyRadius * 0.1, tableCx, tableCy, tableBodyRadius)
            grad.addColorStop(0, lightenColor(sectionColor, 0.25))
            grad.addColorStop(0.5, sectionColor)
            grad.addColorStop(1, darkenColor(sectionColor, 0.08))
            ctx.fillStyle = grad
          }
          ctx.fill()
        } else {
          const tx = ls.x + 15, ty = ls.y + 15, tw = ls.width - 30, th = ls.height - 30
          ctx.beginPath()
          ctx.roundRect(tx, ty, tw, th, 8)
          if (useGradient) {
            const grad = ctx.createLinearGradient(tx, ty, tx, ty + th)
            grad.addColorStop(0, lightenColor(sectionColor, 0.20))
            grad.addColorStop(0.4, lightenColor(sectionColor, 0.05))
            grad.addColorStop(1, darkenColor(sectionColor, 0.12))
            ctx.fillStyle = grad
          }
          ctx.fill()
        }
      } else if (sType === 'BOOTH') {
        ctx.beginPath()
        ctx.roundRect(ls.x, ls.y, ls.width, ls.height, 12)
        if (useGradient) {
          ctx.fillStyle = createSectionGradient(ctx, ls.x, ls.y, ls.height, sectionColor)
        }
        ctx.fill()
      } else {
        // Seated section — sharp corners to match the chart designer (which draws
        // these with strokeRect, not rounded).
        if (useGradient) {
          ctx.fillStyle = createSectionGradient(ctx, ls.x, ls.y, ls.height, sectionColor)
        }
        ctx.beginPath()
        ctx.rect(ls.x, ls.y, ls.width, ls.height)
        ctx.fill()
      }

      // Reset shadow after fill (before stroke)
      ctx.shadowColor = 'transparent'
      ctx.shadowBlur = 0
      ctx.shadowOffsetY = 0

      // Section border — highlighted on hover to indicate clickability
      // Skip border for seats-only sections
      if (sectionHidesBg) {
        ctx.globalAlpha = 1
      } else {
      ctx.globalAlpha = (hoverActive ? 1 : floorDimFactor)
      const borderColor = isSectionSelected ? colors.primary
        : isFocused ? colors.primary
        : hoverActive ? lightenColor(sectionColor, 0.3)
        : sectionColor
      ctx.strokeStyle = borderColor
      // Screen-constant stroke: divide by zoom so the border stays a thin, consistent
      // line instead of ballooning into a bold black box when a section is zoomed into.
      const px = 1 / transform.scale
      ctx.lineWidth = (isSectionSelected ? 1.5 : (isFocused ? 1.25 : (hoverActive ? vcBorder + 0.75 : vcBorder))) * px
      // Sharp corners to match the designer (round joins made seated/polygon
      // sections look rounded on the front end when they weren't in setup).
      ctx.lineJoin = 'miter'
      ctx.lineCap = 'butt'
      // Subtle glow on borders (screen-space; kept light so it reads clean, not bold)
      if (!isSoldOut && (hoverActive || isSectionSelected || isFocused) && sectionScreenPx < 500) {
        ctx.shadowColor = borderColor
        ctx.shadowBlur = 2
      }
      if (USE_SHARED_SECTION_GEOMETRY) {
        for (const input of sectionShapeInputs) {
          traceSectionPath(ctx, input)
          ctx.stroke()
        }
      } else if (ls.shape === 'arc' && ls.arcConfig) {
        drawArcShape(ctx, ls.arcConfig)
        ctx.stroke()
      } else if (ls.shape === 'polygon' && ls.points && ls.points.length >= 3) {
        drawRoundedPolygon(ctx, ls.points, 0, ls.edgeCurves, ls.edgeSkew)
        ctx.stroke()
      } else if (sType === 'TABLE' && ls.tableConfig) {
        if (tableShape === 'round') {
          ctx.beginPath()
          ctx.arc(tableCx, tableCy, tableBodyRadius, 0, Math.PI * 2)
          ctx.stroke()
        } else {
          ctx.beginPath()
          ctx.roundRect(ls.x + 15, ls.y + 15, ls.width - 30, ls.height - 30, 8)
          ctx.stroke()
        }
      } else if (sType === 'BOOTH') {
        ctx.beginPath()
        ctx.roundRect(ls.x, ls.y, ls.width, ls.height, 12)
        ctx.stroke()
      } else {
        ctx.beginPath()
        ctx.rect(ls.x, ls.y, ls.width, ls.height)
        ctx.stroke()
      }
      // (GA dashed border removed)
      ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0
      ctx.lineJoin = 'miter'; ctx.lineCap = 'butt'
      } // end of !sectionHidesBg border block

      // Edge highlights — beveled look (matches DesignerCanvas for GA, rect, and rectangle tables)
      if (!sectionHidesBg && vcGradient && !isSectionSelected && !isSoldOut && (!isFocusedSection || effectiveTransAlpha < 0.5) && ls.shape !== 'arc' && ls.shape !== 'polygon') {
        const isRectTable = sType === 'TABLE' && ls.tableConfig && tableShape !== 'round'
        const edgeX = isRectTable ? ls.x + 15 : ls.x
        const edgeY = isRectTable ? ls.y + 15 : ls.y
        const edgeW = isRectTable ? ls.width - 30 : ls.width
        const edgeH = isRectTable ? ls.height - 30 : ls.height
        if (!isTableLike || isRectTable) {
          ctx.globalAlpha = 0.25 * floorDimFactor
          ctx.strokeStyle = '#ffffff'
          ctx.lineWidth = 1 / transform.scale
          ctx.beginPath()
          ctx.moveTo(edgeX, edgeY)
          ctx.lineTo(edgeX + edgeW, edgeY)
          ctx.stroke()
          ctx.globalAlpha = 0.15 * floorDimFactor
          ctx.strokeStyle = '#000000'
          ctx.beginPath()
          ctx.moveTo(edgeX, edgeY + edgeH)
          ctx.lineTo(edgeX + edgeW, edgeY + edgeH)
          ctx.stroke()
          ctx.globalAlpha = 1
        }
      }

      // TABLE: seat circles around perimeter (show at smaller zoom for better visibility)
      if (sType === 'TABLE' && ls.tableConfig && isOnActiveFloor && bounds.w * transform.scale > TABLE_PRELOAD_PX) {
        const tableCap = ls.tableConfig.capacity || sd?.capacity || 0
        if (tableCap > 0 && tableBodyRadius > 0) {
          // Shared geometry — the designer, the saved layout and this map all
          // call the same function so a table can't be drawn one way and sold
          // another.
          const tablePts = computeTableSeats({
            x: ls.x, y: ls.y, width: ls.width, height: ls.height,
            shape: tableShape === 'rectangle' ? 'rectangle' : 'round',
            capacity: tableCap,
            seatsPerSide: ls.tableConfig?.seatsPerSide ?? null,
            seatSize: ls.tableConfig?.seatSize ?? null,
          })
          ctx.globalAlpha = (isSoldOut ? 0.3 : 0.7) * floorDimFactor
          ctx.fillStyle = sectionColor
          ctx.beginPath()
          for (const pt of tablePts) {
            ctx.moveTo(pt.x + pt.radius, pt.y)
            ctx.arc(pt.x, pt.y, pt.radius, 0, Math.PI * 2)
          }
          ctx.fill()
          ctx.globalAlpha = 1
        }
      }

      // Unavailable TABLE/BOOTH: diagonal slash so status never depends on colour
      // alone. The per-seat slash below is gated on detail zoom, which left a
      // fully-held table looking bookable on mobile.
      if (isSoldOut && isTableOrBooth && isOnActiveFloor) {
        const insetX = Math.min(12, ls.width * 0.18)
        const insetY = Math.min(12, ls.height * 0.18)
        ctx.save()
        ctx.globalAlpha = 0.75 * floorDimFactor
        ctx.strokeStyle = 'rgba(15,23,42,0.65)'
        ctx.lineWidth = Math.max(1.5, 2.5 / transform.scale)
        ctx.beginPath()
        ctx.moveTo(ls.x + insetX, ls.y + insetY)
        ctx.lineTo(ls.x + ls.width - insetX, ls.y + ls.height - insetY)
        ctx.stroke()
        ctx.restore()
      }

      // Overview dots: visible at overview zoom, fade out as seats appear via transAlpha
      const showDots = vcDotVisible && (sd?.capacity || 0) > 0 && isOnActiveFloor && !isUnitSection
        && effectiveTransAlpha < 1 && !(sharedSeatRadius != null && sharesSeatFade(ls))
      if (showDots && sd) {
        const dotAlpha = effectiveTransAlpha > 0 ? vcDotOpacity * (1 - effectiveTransAlpha) : vcDotOpacity
        if (dotAlpha > 0.01) {
          const dotsPath = getOverviewDotsPath(ls, sd.capacity, sd.rowCount)
          if (dotsPath) {
            ctx.globalAlpha = dotAlpha * floorDimFactor
            ctx.fillStyle = cat?.color || sectionColor
            ctx.fill(dotsPath)
            ctx.globalAlpha = 1
          }
        }
      }


      // Restore alpha and rotation (balanced with the save at the top)
      ctx.restore()
    }

    // 3. Decorative shapes from chart designer (rendered AFTER sections to match designer)
    if (decorativeShapes && decorativeShapes.length > 0) {
      for (const shape of decorativeShapes) {
        if (shape.x + shape.width < vpLeft || shape.x > vpRight || shape.y + shape.height < vpTop || shape.y > vpBottom) continue
        const fill = shape.fillColor || '#94a3b8'
        const fillOpacity = shape.fillOpacity ?? 0.15
        const shapeRgba = (hex: string, alpha: number) => {
          if (!hex.startsWith('#') || hex.length < 7) return hex
          const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16)
          return `rgba(${r},${g},${b},${alpha})`
        }
        ctx.globalAlpha = 1
        ctx.fillStyle = shapeRgba(fill, fillOpacity)
        ctx.strokeStyle = shape.borderColor || '#64748b'
        ctx.lineWidth = 1.5
        const scx = shape.x + shape.width / 2
        const scy = shape.y + shape.height / 2
        ctx.save()
        if (shape.rotation) {
          ctx.translate(scx, scy)
          ctx.rotate((shape.rotation * Math.PI) / 180)
          ctx.translate(-scx, -scy)
        }
        if (shape.shapeType === 'ellipse') {
          ctx.beginPath()
          ctx.ellipse(scx, scy, shape.width / 2, shape.height / 2, 0, 0, Math.PI * 2)
          ctx.fill(); ctx.stroke()
        } else if (shape.shapeType === 'ring') {
          // Improved boxing ring — matches designer canvas
          const ringSize = Math.min(shape.width, shape.height)
          const rcx = scx, rcy = scy
          // (no blurred shadow once the ring is big on screen: a blur over most of the canvas each frame)
          if (ringSize * transform.scale < 500) { ctx.shadowColor = 'rgba(0,0,0,0.25)'; ctx.shadowBlur = 5; ctx.shadowOffsetY = 2 }
          ctx.beginPath(); ctx.roundRect(shape.x, shape.y, shape.width, shape.height, ringSize * 0.025)
          ctx.fill()
          ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0
          // Mat
          const matPad = ringSize * 0.1
          const mx = shape.x + matPad, my = shape.y + matPad
          const mw = shape.width - matPad * 2, mh = shape.height - matPad * 2
          const matFill = (shape as any).matColor || (fill.startsWith('#') && fill.length >= 7
            ? `#${Math.min(255,parseInt(fill.slice(1,3),16)+80).toString(16).padStart(2,'0')}${Math.min(255,parseInt(fill.slice(3,5),16)+80).toString(16).padStart(2,'0')}${Math.min(255,parseInt(fill.slice(5,7),16)+80).toString(16).padStart(2,'0')}`
            : '#e8dcc8')
          ctx.fillStyle = shapeRgba(matFill, Math.min(fillOpacity * 1.5, 1))
          ctx.beginPath(); ctx.roundRect(mx, my, mw, mh, ringSize * 0.012)
          ctx.fill(); ctx.strokeStyle = shapeRgba(matFill, 0.5); ctx.lineWidth = 0.8; ctx.stroke()
          // Center circle
          const logoR = Math.min(mw, mh) * 0.18
          const borderCol = shape.borderColor || '#dc2626'
          ctx.beginPath(); ctx.arc(rcx, rcy, logoR, 0, Math.PI * 2)
          ctx.fillStyle = `${borderCol}12`; ctx.fill()
          ctx.strokeStyle = `${borderCol}35`; ctx.lineWidth = Math.max(1, ringSize * 0.005); ctx.stroke()
          if (shape.label) {
            const fs = Math.max(7, Math.min(logoR * 0.65, 20))
            ctx.fillStyle = shape.labelColor || 'rgba(30,30,30,0.5)'
            ctx.font = `800 ${fs}px Inter, system-ui, sans-serif`
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
            ctx.fillText(shape.label, rcx, rcy)
          }
          // Ropes
          const ropeIn = ringSize * 0.075
          const rx = shape.x + ropeIn, ry = shape.y + ropeIn
          const rww = shape.width - ropeIn * 2, rhh = shape.height - ropeIn * 2
          const ropeTh = Math.max(1, ringSize * 0.006)
          const ropeGap = ropeTh * 3.5
          const rColors = [borderCol, '#f5f5f4', borderCol]
          for (let ri = 0; ri < 3; ri++) {
            const off = (ri - 1) * ropeGap
            ctx.strokeStyle = rColors[ri]; ctx.lineWidth = ropeTh
            ctx.globalAlpha = ri === 1 ? 0.8 : 0.65; ctx.lineCap = 'round'
            ctx.beginPath(); ctx.moveTo(rx, ry + off); ctx.lineTo(rx + rww, ry + off); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(rx, ry + rhh - off); ctx.lineTo(rx + rww, ry + rhh - off); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(rx + off, ry); ctx.lineTo(rx + off, ry + rhh); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(rx + rww - off, ry); ctx.lineTo(rx + rww - off, ry + rhh); ctx.stroke()
          }
          ctx.globalAlpha = 1; ctx.lineCap = 'butt'
          // Corner posts
          const pR = Math.max(2.5, ringSize * 0.018)
          const postCol = borderCol
          for (const [ppx, ppy] of [[rx,ry],[rx+rww,ry],[rx+rww,ry+rhh],[rx,ry+rhh]] as [number,number][]) {
            ctx.beginPath(); ctx.arc(ppx, ppy, pR * 1.2, 0, Math.PI * 2)
            ctx.fillStyle = shapeRgba(postCol, 0.5); ctx.fill()
            ctx.beginPath(); ctx.arc(ppx, ppy, pR, 0, Math.PI * 2)
            ctx.fillStyle = shapeRgba(postCol, 0.8); ctx.fill()
            ctx.strokeStyle = shapeRgba(postCol, 0.4); ctx.lineWidth = 0.5; ctx.stroke()
          }
        } else if (shape.shapeType === 'octagon') {
          ctx.beginPath()
          for (let i = 0; i < 8; i++) {
            const angle = (Math.PI / 4) * i - Math.PI / 8
            const px = scx + (shape.width / 2) * Math.cos(angle)
            const py = scy + (shape.height / 2) * Math.sin(angle)
            i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
          }
          ctx.closePath(); ctx.fill(); ctx.stroke()
        } else if (shape.shapeType === 'runway') {
          const rr = Math.min(shape.width, shape.height) / 2
          ctx.beginPath()
          if (shape.width >= shape.height) {
            ctx.arc(shape.x + rr, scy, rr, Math.PI / 2, (3 * Math.PI) / 2)
            ctx.arc(shape.x + shape.width - rr, scy, rr, (3 * Math.PI) / 2, Math.PI / 2)
          } else {
            ctx.arc(scx, shape.y + rr, rr, Math.PI, 0)
            ctx.arc(scx, shape.y + shape.height - rr, rr, 0, Math.PI)
          }
          ctx.closePath(); ctx.fill(); ctx.stroke()
        } else if (shape.shapeType === 'polygon' && shape.points && shape.points.length >= 3) {
          ctx.beginPath()
          ctx.moveTo(shape.x + shape.points[0][0], shape.y + shape.points[0][1])
          for (let i = 1; i < shape.points.length; i++) ctx.lineTo(shape.x + shape.points[i][0], shape.y + shape.points[i][1])
          ctx.closePath(); ctx.fill(); ctx.stroke()
        } else {
          const rr = 4
          ctx.beginPath()
          ctx.moveTo(shape.x + rr, shape.y)
          ctx.lineTo(shape.x + shape.width - rr, shape.y)
          ctx.arcTo(shape.x + shape.width, shape.y, shape.x + shape.width, shape.y + rr, rr)
          ctx.lineTo(shape.x + shape.width, shape.y + shape.height - rr)
          ctx.arcTo(shape.x + shape.width, shape.y + shape.height, shape.x + shape.width - rr, shape.y + shape.height, rr)
          ctx.lineTo(shape.x + rr, shape.y + shape.height)
          ctx.arcTo(shape.x, shape.y + shape.height, shape.x, shape.y + shape.height - rr, rr)
          ctx.lineTo(shape.x, shape.y + rr)
          ctx.arcTo(shape.x, shape.y, shape.x + rr, shape.y, rr)
          ctx.closePath(); ctx.fill(); ctx.stroke()
        }
        // Logo overlay — ring/octagon: centered on mat area, others: centered in shape
        if (shape.logoUrl) {
          const logoImg = imageCacheRef.current.get(shape.logoUrl)
          if (logoImg) {
            const logoScale = (shape as any).logoScale ?? 1
            const logoOffsetX = (shape as any).logoOffsetX ?? 0
            const logoOffsetY = (shape as any).logoOffsetY ?? 0
            const isRingLike = shape.shapeType === 'ring' || shape.shapeType === 'octagon'
            let lw: number, lh: number, lx: number, ly: number
            if (isRingLike) {
              // Match designer: logo area = 35% of ring size, centered on mat
              const ringSize = Math.min(shape.width, shape.height)
              const logoArea = ringSize * 0.35 * logoScale
              const imgScale = Math.min(logoArea / logoImg.width, logoArea / logoImg.height)
              lw = logoImg.width * imgScale
              lh = logoImg.height * imgScale
              lx = scx - lw / 2 + logoOffsetX
              ly = scy - lh / 2 + logoOffsetY
            } else {
              const hasLabel = !!shape.label
              const maxW = shape.width * 0.65 * logoScale
              const maxH = (hasLabel ? shape.height * 0.8 : shape.height) * 0.65 * logoScale
              const imgScale = Math.min(maxW / logoImg.width, maxH / logoImg.height)
              lw = logoImg.width * imgScale
              lh = logoImg.height * imgScale
              lx = scx - lw / 2 + logoOffsetX
              ly = scy - lh / 2 + logoOffsetY
            }
            ctx.drawImage(logoImg, lx, ly, lw, lh)
          }
        }
        // Label (a ring draws its own on the mat, above; drawing it here too
        // printed the ring's name twice)
        if (shape.label && shape.shapeType !== 'ring') {
          const labelSize = Math.min(shape.labelSize || 14, MAX_VENUE_TEXT_PX / transform.scale)
          ctx.fillStyle = shape.labelColor || '#ffffff'
          ctx.font = `bold ${labelSize}px ${FONT_FAMILY}`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          const labelY = shape.logoUrl ? shape.y + shape.height * 0.85 : scy
          const labelRotation = (shape as any).labelRotation
          if (labelRotation) {
            ctx.save()
            ctx.translate(scx, labelY)
            ctx.rotate((labelRotation * Math.PI) / 180)
            ctx.translate(-scx, -labelY)
            ctx.fillText(shape.label, scx, labelY)
            ctx.restore()
          } else {
            ctx.fillText(shape.label, scx, labelY)
          }
        }
        ctx.restore()
      }
      ctx.globalAlpha = 1
    }

    // 3b. Labels from chart designer
    if (layoutLabels && layoutLabels.length > 0) {
      for (const lbl of layoutLabels) {
        ctx.globalAlpha = 1
        const fontSize = lbl.fontSize || 14
        const weight = lbl.bold !== false ? 'bold ' : ''
        ctx.fillStyle = lbl.color || colors.textSecondary
        // Capped on screen; a capped label keeps the centre of its full-size text
        const capped = MAX_VENUE_TEXT_PX / transform.scale
        let ox = 0, oy = 0
        if (fontSize > capped) {
          ctx.font = `${weight}${fontSize}px ${FONT_FAMILY}`
          ox = ctx.measureText(lbl.text).width / 2
          oy = fontSize / 2
          ctx.font = `${weight}${capped}px ${FONT_FAMILY}`
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
        } else {
          ctx.font = `${weight}${fontSize}px ${FONT_FAMILY}`
          ctx.textAlign = 'left'
          ctx.textBaseline = 'top'
        }
        if (lbl.rotation) {
          ctx.save()
          ctx.translate(lbl.x, lbl.y)
          ctx.rotate((lbl.rotation * Math.PI) / 180)
          ctx.fillText(lbl.text, ox, oy)
          ctx.restore()
        } else {
          ctx.fillText(lbl.text, lbl.x + ox, lbl.y + oy)
        }
      }
    }

    // 3c. Placed images from chart designer
    if (layoutImages && layoutImages.length > 0) {
      for (const li of layoutImages) {
        if (li.x + li.width < vpLeft || li.x > vpRight || li.y + li.height < vpTop || li.y > vpBottom) continue
        const cached = imageCacheRef.current.get(li.url)
        if (cached) {
          ctx.save()
          ctx.globalAlpha = li.opacity ?? 1
          ctx.drawImage(cached, li.x, li.y, li.width, li.height)
          ctx.restore()
        }
      }
    }

    // 4. Draw loaded seats (flat 2D, opacity-based floor filtering)
    // ONE COLOR = ONE CATEGORY (seats.io semantics): a seat's fill always
    // means its price tier. State is expressed by treatment, never by a
    // different hue — available = solid category color; unavailable = light
    // neutral gray; yours (selected or held) = category color + primary ring
    // + checkmark. The old scheme gave "your hold" amber and "selected" the
    // brand primary, which collided with category colors (amber hold vs an
    // orange tier) and read as unavailable (near-black primary).
    const UNAVAILABLE_COLOR = '#e2e8f0' // light neutral — sold / others' holds
    const SELECTED_COLOR = colors.primary // fallback fill when a seat has no category
    const AVAILABLE_COLOR = colors.success // fallback for category-less seats
    const ADA_COLOR = '#2563eb'

    // Separate buckets by transition alpha (full-opacity vs fading-in sections)
    type SeatBucket = Array<{ x: number; y: number; r: number; seatId: string }>
    const activeBuckets = new Map<string, SeatBucket>()
    const dimmedBuckets = new Map<string, SeatBucket>()
    const nonSelectedFreeBuckets = new Map<string, SeatBucket>()
    const fadingBuckets = new Map<string, SeatBucket>()  // sections in transition zone
    const fadingFreeBuckets = new Map<string, SeatBucket>()
    const adaSeats: Array<{ x: number; y: number; r: number }> = []
    let fadingSectionAlpha = 1 // min alpha of fading sections
    // Per-section seat visibility — the yours-ring overlay only draws on
    // sections whose individual seats are actually visible at this zoom.
    const sectionSeatAlpha = new Map<string, number>()

    // Pass 1: every loaded section's seat alpha. Fading sections all draw at
    // the LOWEST fading alpha, so it must be known before any seat is placed.
    const seatPasses: Array<{ seats: ComputedSeatPosition[]; isFocusedSec: boolean; isOnActiveFloor: boolean; isFading: boolean }> = []
    for (const [sectionKey, seats] of cache.getAllLoaded()) {
      const ls = layoutSectionMap.get(sectionKey)
      if (!ls) continue

      const isFocusedSec = focusedSectionKey === sectionKey
      const isOnActiveFloor = activeFloor == null || ls.floor == null || ls.floor === activeFloor
      const secAlpha = isFocusedSec ? 1 : seatTransitionAlpha(ls)
      sectionSeatAlpha.set(sectionKey, isOnActiveFloor ? secAlpha : 0)
      const isFading = secAlpha < 1 && isOnActiveFloor
      if (isFading) fadingSectionAlpha = Math.min(fadingSectionAlpha, secAlpha)
      seatPasses.push({ seats, isFocusedSec, isOnActiveFloor, isFading })
    }

    // Pass 2: bucket the seats. Fading seats are painted at fadingSectionAlpha
    // (and half of it), so at 0 — the overview, where any loaded section is
    // still below the seat threshold — they are invisible and are not walked.
    // ADA seats are still collected: their ring is drawn at any alpha.
    for (const { seats, isFocusedSec, isOnActiveFloor, isFading } of seatPasses) {
      const invisibleFading = isFading && fadingSectionAlpha <= 0
      if (!isFocusedSec) {
        const box = getSeatBox(seats)
        if (box.x1 < vpLeft || box.x0 > vpRight || box.y1 < vpTop || box.y0 > vpBottom) continue
      }

      for (const seat of seats) {
        // Skip viewport culling for focused section — always draw its seats
        if (!isFocusedSec && (seat.absX + seat.radius < vpLeft || seat.absX - seat.radius > vpRight ||
            seat.absY + seat.radius < vpTop || seat.absY - seat.radius > vpBottom)) continue

        const isSelected = selectedSeatIds.has(seat.seatId)
        const isADA = seat.seatType === 'WHEELCHAIR' || seat.seatType === 'COMPANION'
        if (invisibleFading) {
          // Its fill would be painted at alpha 0; only an available ADA seat's
          // ring (drawn regardless of alpha) needs it.
          if (isADA && seat.status === 'FREE' && !isSelected) adaSeats.push({ x: seat.absX, y: seat.absY, r: seat.radius })
          continue
        }
        const isYourHold = isOwnHold(seat, sessionId)
        let color: string

        if (isSelected || isYourHold) {
          // Yours — category fill; the ring + checkmark overlay marks state
          color = seat.category?.color || SELECTED_COLOR
        } else if (seat.status !== 'FREE') {
          color = UNAVAILABLE_COLOR  // Light gray - others' holds and booked seats
        } else if (isADA) {
          color = ADA_COLOR
          if (isOnActiveFloor) adaSeats.push({ x: seat.absX, y: seat.absY, r: seat.radius })
        } else {
          // Available — color by the seat's PRICING CATEGORY (seats.io style) so the map
          // matches the price tiers. Per-category colors are set on the pricing categories;
          // falls back to the green availability color when a seat has no category.
          color = seat.category?.color || AVAILABLE_COLOR
        }

        if (!isOnActiveFloor) {
          const bucket = dimmedBuckets.get(color) || []
          bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
          dimmedBuckets.set(color, bucket)
        } else if (isFading && !isSelected && seat.status === 'FREE') {
          const bucket = fadingFreeBuckets.get(color) || []
          bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
          fadingFreeBuckets.set(color, bucket)
        } else if (isFading) {
          const bucket = fadingBuckets.get(color) || []
          bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
          fadingBuckets.set(color, bucket)
        } else if (!isSelected && seat.status === 'FREE') {
          // Legend hover: fade FREE seats outside the highlighted category so
          // the hovered price tier pops (interaction-only; no effect at rest)
          if (highlightCategoryId && (seat.categoryId || null) !== highlightCategoryId) {
            const bucket = dimmedBuckets.get(color) || []
            bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
            dimmedBuckets.set(color, bucket)
          } else {
            const bucket = nonSelectedFreeBuckets.get(color) || []
            bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
            nonSelectedFreeBuckets.set(color, bucket)
          }
        } else {
          const bucket = activeBuckets.get(color) || []
          bucket.push({ x: seat.absX, y: seat.absY, r: seat.radius, seatId: seat.seatId })
          activeBuckets.set(color, bucket)
        }
      }
    }

    // 3D sprites are perf-sensitive — only enable below a seat-count ceiling.
    let totalBucketSeats = 0
    for (const m of [nonSelectedFreeBuckets, activeBuckets]) {
      for (const arr of m.values()) totalBucketSeats += arr.length
    }
    const seat3D = SEAT_3D_ENABLED && totalBucketSeats <= SEAT_3D_MAX_SEATS

    // Helper: draw a bucket of seats. 3D beveled sprites for the main visible buckets
    // when enabled+under the perf ceiling; flat circles otherwise (and always for
    // dimmed/fading buckets, where the sprite cost isn't worth it).
    const drawSeatBucket = (buckets: Map<string, SeatBucket>, alpha: number, allow3D = false) => {
      if (buckets.size === 0) return
      const use3D = allow3D && seat3D
      // Flag-gated: shared primitive (identical flat output, or 3D sprites when enabled)
      if (USE_SHARED_SEAT_RENDERER || use3D) {
        drawSeatBatch(ctx, buckets as Map<string, { x: number; y: number; r: number }[]>, alpha, { showGradient: use3D })
        return
      }
      ctx.globalAlpha = alpha
      for (const [color, seats] of buckets) {
        ctx.fillStyle = color
        ctx.beginPath()
        for (const { x, y, r } of seats) {
          ctx.moveTo(x + r, y)
          ctx.arc(x, y, r, 0, Math.PI * 2)
        }
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    // Draw dimmed (non-active floor) seats first
    drawSeatBucket(dimmedBuckets, 0.12)

    // Draw fading-in sections (transition zone) at partial alpha
    drawSeatBucket(fadingFreeBuckets, 0.5 * fadingSectionAlpha)
    drawSeatBucket(fadingBuckets, fadingSectionAlpha)

    // Draw non-selected FREE seats at reduced opacity (50%)
    drawSeatBucket(nonSelectedFreeBuckets, 0.75, true)

    // Draw active floor seats at full opacity - selected and unavailable
    drawSeatBucket(activeBuckets, 1, true)

    // Sections with any seat (or the rings, slashes and glows drawn round
    // one) inside the view. The passes below draw nothing visible for the
    // rest, so they no longer walk every seat of the venue each frame.
    const overlayPad = 2 + 4 / transform.scale
    const onscreenLoaded: Array<[string, ComputedSeatPosition[]]> = []
    for (const entry of cache.getAllLoaded()) {
      const b = getSeatBox(entry[1])
      if (b.x1 + overlayPad < vpLeft || b.x0 - overlayPad > vpRight || b.y1 + overlayPad < vpTop || b.y0 - overlayPad > vpBottom) continue
      onscreenLoaded.push(entry)
    }

    // Colorblind-safe cue on unavailable seats — a short slash through
    // sold/held-by-others dots so status doesn't rely on color alone.
    // (Was behind the vizPreview flag; now default at detail zoom.)
    if (drawIsZoomedIn) {
      ctx.strokeStyle = 'rgba(255,255,255,0.85)'
      ctx.lineWidth = Math.max(1, 1.5 / transform.scale)
      ctx.beginPath()
      for (const [key, seats] of onscreenLoaded) {
        // Only where the seats themselves show: over a solid section the
        // slashes were stray white specks
        if ((sectionSeatAlpha.get(key) ?? 0) < 0.5) continue
        for (const seat of seats) {
          if (seat.status === 'FREE' || selectedSeatIds.has(seat.seatId)) continue
          if (isOwnHold(seat, sessionId)) continue
          if (seat.absX + seat.radius < vpLeft || seat.absX - seat.radius > vpRight ||
              seat.absY + seat.radius < vpTop || seat.absY - seat.radius > vpBottom) continue
          const r = seat.radius * 0.5
          ctx.moveTo(seat.absX - r, seat.absY + r)
          ctx.lineTo(seat.absX + r, seat.absY - r)
        }
      }
      ctx.stroke()
    }

    // 5. ADA seat ring indicators (visible when zoomed in enough)
    if (adaSeats.length > 0 && transform.scale > 1.2) {
      ctx.strokeStyle = ADA_COLOR
      ctx.lineWidth = 1.5 / transform.scale
      ctx.beginPath()
      for (const { x, y, r } of adaSeats) {
        ctx.moveTo(x + r + 1.5, y)
        ctx.arc(x, y, r + 1.5, 0, Math.PI * 2)
      }
      ctx.stroke()
    }

    // 6. "Yours" rings with glow + checkmark (proportional to seat size).
    // Covers UI-selected seats AND seats held by this session (in cart) —
    // both are "your seats"; the fill stays category-colored, so the ring is
    // what distinguishes yours from merely-available. Drawn at any zoom
    // where the section's seats are visible (was deep-zoom only, which left
    // selected seats indistinguishable at mid zoom).
    const isYours = (seat: { seatId: string; status: string; mine?: boolean; holdSessionId?: string | null }) =>
      selectedSeatIds.has(seat.seatId) || isOwnHold(seat, sessionId)
    const ringSectionVisible = (sectionKey: string) => (sectionSeatAlpha.get(sectionKey) ?? 0) >= 0.5
    {
      const primaryRGB = colorsRef.current.primaryRGB.replace(/ /g, ', ')
      const screenR0 = (cache.getAllLoaded().values().next().value?.[0]?.radius || 8) * transform.scale
      // Screen-constant ring widths so selection rings stay thin instead of turning
      // into bold blobs when a section is zoomed into.
      const sw = 1 / transform.scale

      // Highlighted seats (best available preview) — amber pulsing glow
      if (highlightedSeatIds && highlightedSeatIds.size > 0) {
        // Outer amber glow
        ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)'
        ctx.lineWidth = 2.5 * sw
        ctx.beginPath()
        for (const [, seats] of onscreenLoaded) {
          for (const seat of seats) {
            if (highlightedSeatIds.has(seat.seatId)) {
              const glowR = seat.radius * 1.3
              ctx.moveTo(seat.absX + glowR, seat.absY)
              ctx.arc(seat.absX, seat.absY, glowR, 0, Math.PI * 2)
            }
          }
        }
        ctx.stroke()

        // Inner amber ring
        ctx.strokeStyle = '#f59e0b'
        ctx.lineWidth = 2 * sw
        ctx.beginPath()
        for (const [, seats] of onscreenLoaded) {
          for (const seat of seats) {
            if (highlightedSeatIds.has(seat.seatId)) {
              const ringR = seat.radius * 1.1
              ctx.moveTo(seat.absX + ringR, seat.absY)
              ctx.arc(seat.absX, seat.absY, ringR, 0, Math.PI * 2)
            }
          }
        }
        ctx.stroke()
      }

      // Outer glow — tight to avoid overlapping neighbors
      ctx.strokeStyle = `rgba(${primaryRGB}, 0.3)`
      ctx.lineWidth = 2 * sw
      ctx.beginPath()
      for (const [sectionKey, seats] of onscreenLoaded) {
        if (!ringSectionVisible(sectionKey)) continue
        for (const seat of seats) {
          if (isYours(seat)) {
            const glowR = seat.radius * 1.15
            ctx.moveTo(seat.absX + glowR, seat.absY)
            ctx.arc(seat.absX, seat.absY, glowR, 0, Math.PI * 2)
          }
        }
      }
      ctx.stroke()

      // Inner ring
      ctx.strokeStyle = colors.primary
      ctx.lineWidth = 1.5 * sw
      ctx.beginPath()
      for (const [sectionKey, seats] of onscreenLoaded) {
        if (!ringSectionVisible(sectionKey)) continue
        for (const seat of seats) {
          if (isYours(seat)) {
            const ringR = seat.radius * 1.05
            ctx.moveTo(seat.absX + ringR, seat.absY)
            ctx.arc(seat.absX, seat.absY, ringR, 0, Math.PI * 2)
          }
        }
      }
      ctx.stroke()

      // Checkmark inside your seats (when large enough to see)
      if (screenR0 >= 4) {
        for (const [sectionKey, seats] of onscreenLoaded) {
          if (!ringSectionVisible(sectionKey)) continue
          for (const seat of seats) {
            if (isYours(seat)) {
              drawSeatCheckmark(ctx, seat.absX, seat.absY, seat.radius)
            }
          }
        }
      }
    }

    // 6b. Seat numbers inside dots (focused section only, when zoomed in enough)
    const focusedSeats = focusedSectionKey ? cache.get(focusedSectionKey) : null
    const seatScreenR = focusedSeats?.[0] ? focusedSeats[0].radius * transform.scale : 0
    if (focusedSectionKey && seatScreenR >= 7) {
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // Font and text colour repeat across a section's seats: set the font
      // only when it changes and work each fill's contrast out once.
      const numberTextColor = new Map<string, string>()
      let lastFont = ''
      for (const [sectionKey, seats] of cache.getAllLoaded()) {
        if (sectionKey !== focusedSectionKey) continue
        for (const seat of seats) {
          if (isYours(seat)) continue // don't draw on your seats (they have checkmarks)
          // Off screen: its number would land outside the view
          const m = seat.radius * 2
          if (seat.absX + m < vpLeft || seat.absX - m > vpRight || seat.absY + m < vpTop || seat.absY - m > vpBottom) continue
          const seatFill = seat.status !== 'FREE' ? UNAVAILABLE_COLOR : (seat.category?.color || AVAILABLE_COLOR)
          let textColor = numberTextColor.get(seatFill)
          if (textColor === undefined) {
            textColor = getRelativeLuminance(seatFill.startsWith('#') ? seatFill : '#94a3b8') > 0.4 ? '#374151' : '#ffffff'
            numberTextColor.set(seatFill, textColor)
          }
          const fontSize = seat.radius * 0.85
          const font = `600 ${fontSize}px ${FONT_FAMILY}`
          if (font !== lastFont) { ctx.font = font; lastFont = font }
          ctx.globalAlpha = seat.status === 'FREE' ? 0.8 : 0.4
          ctx.fillStyle = textColor
          ctx.fillText(seat.seatNumber, seat.absX, seat.absY)
        }
      }
      ctx.globalAlpha = 1
    }

    // 7. Hovered seat with drop shadow + enlarged 3D pop-up
    if (hoveredSeatId && drawIsZoomedIn) {
      for (const [, seats] of cache.getAllLoaded()) {
        const seat = seats.find(s => s.seatId === hoveredSeatId)
        if (seat) {
          const isHoveredSelected = isYours(seat)
          const isHoveredFree = seat.status === 'FREE'
          const hoverR = seat.radius * 1.08
          const seatColor = isHoveredSelected || isHoveredFree
            ? (seat.category?.color || (isHoveredSelected ? SELECTED_COLOR : AVAILABLE_COLOR))
            : UNAVAILABLE_COLOR

          // Drop shadow for "lifted" hover effect (shadow props are screen-space constants)
          ctx.save()
          ctx.shadowColor = 'rgba(0,0,0,0.2)'
          ctx.shadowBlur = 3
          ctx.shadowOffsetY = 1

          // Draw enlarged seat on hover
          ctx.fillStyle = seatColor
          ctx.beginPath()
          ctx.arc(seat.absX, seat.absY, hoverR, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()

          // Ring indicator
          const ringColor = isHoveredFree || isHoveredSelected
            ? `rgba(${colorsRef.current.primaryRGB.replace(/ /g, ', ')}, 0.6)`
            : 'rgba(100, 116, 139, 0.5)'
          ctx.strokeStyle = ringColor
          ctx.lineWidth = 2 / transform.scale
          ctx.beginPath()
          ctx.arc(seat.absX, seat.absY, seat.radius * 1.15, 0, Math.PI * 2)
          ctx.stroke()
          break
        }
      }
    }

    // 7.4 Row labels — focused section at deep zoom (seats.io-style), honoring
    // the builder's per-section Row Label Position so the storefront matches
    // the designer: inside (default), left/right end, outside, or hidden.
    if (focusedSectionKey && seatScreenR >= 7) {
      const seats = cache.get(focusedSectionKey)
      const focusedLayoutSec = layoutSectionMap.get(focusedSectionKey)
      const rowLabelPos = ((focusedLayoutSec as { rowLabelPosition?: string } | undefined)?.rowLabelPosition) || 'inside'
      // A table or booth has one designer-filler "row" (every Africa table's
      // is "T1"), so a focused T20 was captioned "T1". Its name is enough.
      const isTableLike = focusedLayoutSec?.sectionType === 'TABLE' || focusedLayoutSec?.sectionType === 'BOOTH'
      if (seats && seats.length > 0 && rowLabelPos !== 'hidden' && !isTableLike) {
        const rowGroups = new Map<string, typeof seats>()
        for (const s of seats) {
          const arr = rowGroups.get(s.rowLabel) || []
          arr.push(s)
          rowGroups.set(s.rowLabel, arr)
        }
        // Section seat bounds — 'outside' projects labels past these.
        let bMinX = Infinity, bMaxX = -Infinity, bMinY = Infinity, bMaxY = -Infinity
        for (const s of seats) {
          if (s.absX < bMinX) bMinX = s.absX
          if (s.absX > bMaxX) bMaxX = s.absX
          if (s.absY < bMinY) bMinY = s.absY
          if (s.absY > bMaxY) bMaxY = s.absY
        }
        ctx.textBaseline = 'middle'
        ctx.fillStyle = 'rgba(71, 85, 105, 0.8)'
        for (const [label, rowSeats] of rowGroups) {
          rowSeats.sort((a, b) => (a.seatIndex ?? 0) - (b.seatIndex ?? 0))
          const first = rowSeats[0]
          const last = rowSeats[rowSeats.length - 1]
          const r = first.radius
          // Continue the row axis beyond `tip`, away from `from` — works for
          // any row orientation (same math as the designer canvas).
          const beyond = (tip: typeof first, from: typeof first, pad: number, toBounds: boolean) => {
            let dx = tip.absX - from.absX, dy = tip.absY - from.absY
            if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) { dx = -1; dy = 0 }
            const len = Math.hypot(dx, dy) || 1
            const ux = dx / len, uy = dy / len
            let dist = tip.radius + pad
            if (toBounds) {
              const tx = ux > 0.0001 ? (bMaxX - tip.absX) / ux : ux < -0.0001 ? (bMinX - tip.absX) / ux : Infinity
              const ty = uy > 0.0001 ? (bMaxY - tip.absY) / uy : uy < -0.0001 ? (bMinY - tip.absY) / uy : Infinity
              const candidates = [tx, ty].filter(v => Number.isFinite(v) && v >= 0)
              dist = (candidates.length ? Math.min(...candidates) : 0) + tip.radius + pad
            }
            return {
              x: tip.absX + ux * dist,
              y: tip.absY + uy * dist,
              align: (Math.abs(ux) >= Math.abs(uy)
                ? (ux >= 0 ? 'left' : 'right')
                : 'center') as CanvasTextAlign,
            }
          }
          let lx: number, ly: number, align: CanvasTextAlign
          if (rowLabelPos === 'left') {
            const p = beyond(first, last, r * 0.9, false)
            lx = p.x; ly = p.y; align = p.align
          } else if (rowLabelPos === 'right') {
            const p = beyond(last, first, r * 0.9, false)
            lx = p.x; ly = p.y; align = p.align
          } else if (rowLabelPos === 'outside') {
            const p = beyond(first, last, r * 0.9, true)
            lx = p.x; ly = p.y; align = p.align
          } else {
            // 'inside' default — original look: left of the row's leftmost seat
            let leftmost = first
            for (const s of rowSeats) if (s.absX < leftmost.absX) leftmost = s
            lx = leftmost.absX - leftmost.radius * 1.9; ly = leftmost.absY; align = 'right'
          }
          ctx.font = `600 ${Math.min(r * 0.9, 11)}px ${FONT_FAMILY}`
          ctx.textAlign = align
          ctx.fillText(label, lx, ly)
        }
      }
    }

    if (layers === 'all') paintLabels()

    ctx.restore()

    // Section labels and the keyboard / selection overlays: drawn last, and
    // live over the bitmap in a gesture frame. (A declaration, so the
    // labels-only paint above can call it.)
    function paintLabels() {
      // 7.5 Section pill labels
      {
        // Screen-space boxes of the pills already drawn (rotation included)
        const drawnPills: Array<{ sx: number; sy: number; sw: number; sh: number }> = []

        // Sections by size, largest first (larger sections get label priority)
        for (const [key, ls] of sectionsBySize) {
          const bounds = getSectionBounds(ls)

          // Only hide the label for the focused (clicked) section
          if (focusedSectionKey === key) continue

          // Viewport culling (with generous padding for labels near edges)
          const labelPad = 100 / transform.scale
          if (bounds.x + bounds.w < vpLeft - labelPad || bounds.x > vpRight + labelPad ||
              bounds.y + bounds.h < vpTop - labelPad || bounds.y > vpBottom + labelPad) continue

          const pillFadeAlpha = 1

          const isOnActiveFloor = activeFloor == null || ls.floor == null || ls.floor === activeFloor
          if (!isOnActiveFloor) continue

          const sd = sectionDataMap.get(key)
          // Fallback: use layout section data if overview data is missing
          const sectionName = sd?.name || ls.name || key
          if (!sectionName) continue

          const sType = ls.sectionType || sd?.sectionType || 'SEATED'
          // (table overlap check removed — collision detection handles all overlaps)

          const hasLoadedSeats = cache.has(key) && shouldShowSeats(ls)
          const isArc = !!(ls.shape === 'arc' && ls.arcConfig)

          const isTableLabel = sType === 'TABLE' || sType === 'BOOTH'
          // Table numbers mean nothing until the table itself is visible: a
          // phone overview drew "T35" pills bigger than the tables they named.
          if (isTableLabel && Math.min(bounds.w, bounds.h) * transform.scale < 20) continue

          // Pill font: scale with zoom — smaller at overview, larger when zoomed in.
          // A phone overview (scale < 0.15) may drop to 7px so zone names fit.
          const fontFloor = transform.scale < 0.15 ? 7 : 9
          const targetScreenPx = Math.min(13, Math.max(fontFloor, 7 + transform.scale * 3)) * vcLabelScale
          const pillFontSize = targetScreenPx / transform.scale
          const screenFontPx = targetScreenPx
          if (screenFontPx < 5) continue
          const pillText = sectionName

          ctx.font = `700 ${pillFontSize}px ${FONT_FAMILY}`
          const textW = ctx.measureText(pillText).width
          const pillH = pillFontSize * 1.6 // positioning only; each spot sizes its own pill
          // The pill turns with the section (kept upright, below)
          let labelRot = 0
          if (ls.rotation) {
            labelRot = ((ls.rotation % 360) + 360) % 360
            if (labelRot > 90 && labelRot < 270) labelRot -= 180
          }

          // Position pill: ABOVE section when focused (seats visible), centered otherwise
          let labelCx: number
          let labelCy: number
          const isSectionFocused = focusedSectionKey === key

          if (isSectionFocused) {
            if (isArc) {
              // Arc sections: place label at outer midpoint + offset outward
              const a = ls.arcConfig!
              const midAngle = ((a.startAngle + a.endAngle) / 2) * Math.PI / 180
              const outerR = a.outerRadius + pillH * 0.8
              labelCx = a.cx + outerR * Math.cos(midAngle)
              labelCy = a.cy + outerR * Math.sin(midAngle)
            } else {
              // Rect/polygon: label above the section top edge
              labelCx = bounds.x + bounds.w / 2
              labelCy = bounds.y - pillH * 0.8
              // Fallback: if would go off viewport top, place inside with higher opacity
              if (labelCy - pillH / 2 < vpTop) {
                labelCy = bounds.y + pillH * 0.8
              }
            }
          } else if (sharesSeatFade(ls) && seatTransitionAlpha(ls) >= 0.5) {
            // Seats showing: the number sits just above the section, off its
            // seats (centred, it covered the seats in the middle)
            if (isArc) {
              const a = ls.arcConfig!
              const midAngle = ((a.startAngle + a.endAngle) / 2) * Math.PI / 180
              const outerR = a.outerRadius + pillH * 0.8
              labelCx = a.cx + outerR * Math.cos(midAngle)
              labelCy = a.cy + outerR * Math.sin(midAngle)
            } else {
              labelCx = bounds.x + bounds.w / 2
              labelCy = bounds.y - pillH * 0.8
              if (labelCy - pillH / 2 < vpTop) labelCy = bounds.y + pillH * 0.8
            }
          } else {
            // Overview: centered in section (no seats visible)
            const center = getSectionCenter(ls)
            labelCx = center.cx + ((ls as any).labelOffsetX || 0)
            labelCy = center.cy + ((ls as any).labelOffsetY || 0)
          }

          // A section can be drawn in SEVERAL places (a GA zone spread round a
          // ground). Name every block: one pill on the biggest piece leaves the
          // rest looking nameless, which is exactly how a merged zone read —
          // six blocks, one "GA".
          const labelSpots = hasMultipleParts(ls)
            ? sectionParts(ls).map(part => {
                const c = partCenter(part)
                const b = partBounds(part)
                return { cx: c.cx + ((ls as any).labelOffsetX || 0), cy: c.cy + ((ls as any).labelOffsetY || 0), w: b.w, h: b.h }
              })
            : [{ cx: labelCx, cy: labelCy, w: bounds.w, h: bounds.h }]

          for (const spot of labelSpots) {
            // Skip a block too small to carry the name legibly on screen.
            if (labelSpots.length > 1 && Math.min(spot.w, spot.h) * transform.scale < 18) continue
            // Fit check: the name must sit comfortably inside its block (1.3x its
            // width; a table number just has to fit), measured along the way it
            // is written — a rotated section's own length. Oversized pills at a
            // phone overview spilled across neighbouring sections.
            let fs = pillFontSize, tw = textW, rot = labelRot
            const single = labelSpots.length === 1
            const along = (rot !== 0 && single ? ls.width : spot.w) * transform.scale
            const across = (rot !== 0 && single ? ls.height : spot.h) * transform.scale
            const fits = (w: number, room: number) => isTableLabel ? w <= room : w * 1.3 <= room
            if (!fits(tw * transform.scale, along)) {
              if (isTableLabel) continue
              // Shrink to the 7px floor, then try the block's long side: a tall,
              // narrow block (Section A, a standing area) carries its name
              // vertically, the way a rotated grandstand already does.
              const minFs = Math.min(fs, (7 * vcLabelScale) / transform.scale)
              const minTw = tw * minFs / fs
              if (fits(minTw * transform.scale, along)) {
                fs = minFs; tw = minTw
              } else if (rot === 0 && single && !isArc && across > along && fits(minTw * transform.scale, across)) {
                rot = -90
                if (!fits(tw * transform.scale, across)) { fs = minFs; tw = minTw }
              } else {
                continue
              }
            }
            const pillW = tw + fs * 1.2
            const pillH = fs * 1.6
            let anchorX = spot.cx
            let anchorY = spot.cy
            const pillX = anchorX - pillW / 2
            let pillY = anchorY - pillH / 2

            // Screen-space collision against the pills already drawn. The box is
            // the ROTATED pill's bounds: an axis-aligned box for a vertical
            // label ("VIP West") was the wrong shape and clipped its neighbours.
            const sPW = pillW * transform.scale
            const sPH = pillH * transform.scale
            const box = rotatedBox(anchorX * transform.scale + transform.x, anchorY * transform.scale + transform.y, sPW, sPH, rot)
            // Padding between pills for breathing room
            const pad = 2
            const hits = (x: number, y: number) => drawnPills.some(d =>
              x - pad < d.sx + d.sw && x + box.w + pad > d.sx &&
              y - pad < d.sy + d.sh && y + box.h + pad > d.sy
            )

            // Section names may be nudged above/below their natural spot, but
            // only while the pill stays inside their own block — a free nudge
            // landed names on the ring and in neighbouring sections. Table and
            // booth numbers never move: on a collision the lower-priority label
            // (sections are visited largest first) is hidden instead.
            const step = box.h + pad + 2
            const spotTop = (spot.cy - spot.h / 2) * transform.scale + transform.y
            const spotBottom = (spot.cy + spot.h / 2) * transform.scale + transform.y
            const offsets = isTableLabel || rot !== 0 ? [0] : [0, -step, step, -step * 2, step * 2]
            let placedY: number | null = null
            for (const dy of offsets) {
              const y = box.y + dy
              if (dy !== 0 && (y < spotTop || y + box.h > spotBottom)) continue
              if (!hits(box.x, y)) { placedY = y; break }
            }
            if (placedY === null) continue
            if (placedY !== box.y) {
              // Move the pill AND the text anchor together, or the name separates
              // from its background. Screen delta -> world delta.
              const worldDelta = (placedY - box.y) / transform.scale
              anchorY += worldDelta
              pillY += worldDelta
            }

            // Apply section rotation so the pill aligns with the section, but keep the
            // text upright — flip 180° when the rotation would render it upside down.
            if (rot !== 0) {
              ctx.save()
              ctx.translate(anchorX, anchorY)
              ctx.rotate((rot * Math.PI) / 180)
              ctx.translate(-anchorX, -anchorY)
            }

            // Background pill — style matches builder labelStyle
            // Auto mode: pick label style based on section color luminance
            const sectionCat = sd?.categoryId ? categoryMap.get(sd.categoryId) : null
            const secColor = sectionCat?.color || '#94a3b8'
            const useLightLabel = vcLabelStyle === 'light' ? true
              : vcLabelStyle === 'dark' ? false
              : getRelativeLuminance(secColor) < 0.5 // auto: dark sections get light labels
            ctx.globalAlpha = (isSectionFocused ? 0.9 : 0.8) * pillFadeAlpha
            ctx.fillStyle = useLightLabel ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.92)'
            ctx.beginPath()
            ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2)
            ctx.fill()

            // Label text
            ctx.globalAlpha = pillFadeAlpha
            ctx.fillStyle = useLightLabel ? '#ffffff' : '#374151'
            ctx.font = `700 ${fs}px ${FONT_FAMILY}`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(pillText, anchorX, anchorY)

            // Availability subtitle — only show "Sold Out" or low stock warning
            // Don't show "Available" — it clutters the map
            const screenW = spot.w * transform.scale
            const showSub = sd && screenW > 80 && !isSectionFocused && sType !== 'TABLE' && sType !== 'BOOTH' && sType !== 'GA'
              && (sd.available <= 0 || (showRemainingQuantity && sd.capacity > 0 && sd.available / sd.capacity < 0.2))
            if (showSub) {
              const subFontSize = fs * 0.65
              ctx.font = `600 ${subFontSize}px ${FONT_FAMILY}`

              let subText: string
              let subColor: string
              let subHalo: string
              if (sd.available <= 0) {
                subText = 'Sold Out'
                // Strong red on a light halo — legible regardless of the (often faint) sold-out fill
                subColor = '#b91c1c'
                subHalo = 'rgba(255,255,255,0.92)'
              } else {
                const pctRemaining = sd.available / sd.capacity
                subText = pctRemaining < 0.1 ? 'Almost Gone' : 'Selling Fast'
                subColor = pctRemaining < 0.1
                  ? (useLightLabel ? '#fcd34d' : '#d97706')
                  : (useLightLabel ? 'rgba(255,255,255,0.85)' : '#6b7280')
                subHalo = useLightLabel ? 'rgba(0,0,0,0.40)' : 'rgba(255,255,255,0.7)'
              }

              const subY = anchorY + pillH / 2 + subFontSize * 0.9
              ctx.textAlign = 'center'
              ctx.textBaseline = 'middle'
              ctx.globalAlpha = 0.95 * pillFadeAlpha
              // Contrast halo so the label is readable on any section fill
              ctx.lineWidth = Math.max(2, subFontSize * 0.22)
              ctx.lineJoin = 'round'
              ctx.strokeStyle = subHalo
              ctx.strokeText(subText, anchorX, subY)
              ctx.fillStyle = subColor
              ctx.fillText(subText, anchorX, subY)

              // VIZ PREVIEW: seats.io-style "from $X" under the pill so buyers
              // can compare section prices at overview without hovering each one
              if (vizPreview && sd.available > 0 && ((sd as any).allInMinPrice ?? (sd as any).minPrice) > 0) {
                const priceY = subY + subFontSize * 1.15
                const priceText = `from $${Math.round((sd as any).allInMinPrice ?? (sd as any).minPrice)}`
                ctx.font = `700 ${subFontSize}px ${FONT_FAMILY}`
                ctx.strokeStyle = subHalo
                ctx.strokeText(priceText, anchorX, priceY)
                ctx.fillStyle = useLightLabel ? 'rgba(255,255,255,0.95)' : '#111827'
                ctx.fillText(priceText, anchorX, priceY)
              }
              ctx.globalAlpha = 1
            }

            if (rot !== 0) ctx.restore()

            drawnPills.push({ sx: box.x, sy: placedY, sw: box.w, sh: box.h })
          }
        }

      }

      // 8. Seat labels — REMOVED (seat info only in tooltip)

      // 9. Row labels — REMOVED (seat info only in tooltip)

      // 10. Section name labels when zoomed in — REMOVED
      // Pill labels (step 7.5) handle section names at overview. When zoomed in,
      // individual seat labels (row+seat) are sufficient. No floating section name needed.

      // 11. Keyboard focus indicator (dashed border around focused section)
      const keyboardIdx = keyboardFocusedSectionIdxRef.current
      if (keyboardIdx >= 0 && keyboardMode) {
        const visibleSections = layout.sections.filter(ls => {
          const sd = sectionDataMap.get(ls.key)
          if (!sd || sd.sectionType === 'STAGE') return false
          const isOnActiveFloor = activeFloor == null || ls.floor == null || ls.floor === activeFloor
          return isOnActiveFloor
        })

        if (keyboardIdx < visibleSections.length) {
          const ls = visibleSections[keyboardIdx]
          const bounds = getSectionBounds(ls)

          // Draw dashed border
          ctx.strokeStyle = colors.primary
          ctx.lineWidth = 3 / transform.scale
          ctx.setLineDash([8 / transform.scale, 4 / transform.scale])
          ctx.strokeRect(bounds.x - 2 / transform.scale, bounds.y - 2 / transform.scale, bounds.w + 4 / transform.scale, bounds.h + 4 / transform.scale)
          ctx.setLineDash([])
        }
      }

      // 12. Multi-select overlays (box and circle selection)
      if (dragBoxState) {
        const minX = Math.min(dragBoxState.startX, dragBoxState.endX)
        const maxX = Math.max(dragBoxState.startX, dragBoxState.endX)
        const minY = Math.min(dragBoxState.startY, dragBoxState.endY)
        const maxY = Math.max(dragBoxState.startY, dragBoxState.endY)

        ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)'
        ctx.lineWidth = 2 / transform.scale
        ctx.fillRect(minX, minY, maxX - minX, maxY - minY)
        ctx.strokeRect(minX, minY, maxX - minX, maxY - minY)
      }

      if (circleState) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)'
        ctx.lineWidth = 2 / transform.scale
        ctx.beginPath()
        ctx.arc(circleState.centerX, circleState.centerY, circleState.radius, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
      }
    }
    // Every prop read above is a dependency: the view comes in as an argument,
    // so nothing re-creates this on every pan frame and a missing one paints stale.
  }, [fitScale, layout, sectionDataMap, categoryMap, layoutSectionMap, sectionsBySize,
      cache, shouldShowSeats, seatTransitionAlpha, venueShape, stage, decorativeShapes, layoutLabels, layoutImages, activeFloor, keyboardMode, dragBoxState, circleState, allowHeldSeatInteraction, vizPreview, highlightCategoryId,
      highlightedSeatIds, sessionId, showRemainingQuantity, isAnimating, perspectiveEnabled, perspectiveAngle, sharedSeatRadius])

  // --- Gesture bitmaps ---
  // What a bitmap was painted from besides the view. Any change (a seat
  // sold, a hover, a selection, the theme, an image arriving) and it is
  // painted again — keep in step with what paintScene reads.
  const gestureInputs = useCallback((cw: number, ch: number, dpr: number): unknown[] => [
    cw, ch, dpr, paintBox, fitScale, layout, sectionDataMap, categoryMap, layoutSectionMap, sectionsBySize, cache,
    venueShape, stage, decorativeShapes, layoutLabels, layoutImages, activeFloor, allowHeldSeatInteraction, vizPreview,
    highlightCategoryId, highlightedSeatIds, sessionId,
    hoveredSeatIdRef.current, hoveredSectionKeyRef.current, focusedSectionKeyRef.current,
    selectedSeatIdsRef.current, selectedSectionKeysRef.current,
    colorsRef.current, bgImageRef.current, imageCacheRef.current.size, seatDataVersionRef.current,
  ], [paintBox, fitScale, layout, sectionDataMap, categoryMap, layoutSectionMap, sectionsBySize, cache, venueShape, stage,
      decorativeShapes, layoutLabels, layoutImages, activeFloor, allowHeldSeatInteraction, vizPreview, highlightCategoryId,
      highlightedSeatIds, sessionId])

  /** A blank bitmap laid out for the view `t` (null when no map is near it). */
  const newGestureLayer = useCallback((t: Transform, cw: number, ch: number, dpr: number, inputs: unknown[], plan0: GesturePlanOptions, canvas?: HTMLCanvasElement | null): GestureLayer | null => {
    const plan = planGestureCache(cw, ch, t, paintBox, dpr, plan0)
    if (!plan) return null
    const c = canvas ?? document.createElement('canvas')
    const ctx = c.getContext('2d')
    if (!ctx) return null
    if (c.width !== plan.width || c.height !== plan.height) {
      c.width = plan.width
      c.height = plan.height
    } else {
      resetCanvasContext(ctx)
      ctx.clearRect(0, 0, c.width, c.height)
    }
    // CSS size from the whole-pixel backing store: exactly plan.dpr device px per CSS px.
    return { canvas: c, inputs, viewKey: viewKey(t, cw, ch, dpr), transform: plan.transform, cssW: plan.width / plan.dpr, cssH: plan.height / plan.dpr, dpr: plan.dpr, margin: plan.margin }
  }, [paintBox])

  /** Paint device rows [y0, y1) of a bitmap: the map's static layers. */
  const paintGestureRows = useCallback((layer: GestureLayer, y0: number, y1: number) => {
    const ctx = layer.canvas.getContext('2d')
    if (!ctx) return
    if (y0 <= 0 && y1 >= layer.canvas.height) {
      paintScene(ctx, layer.transform, layer.cssW, layer.cssH, layer.dpr, 'base')
      return
    }
    // A band: painted as if it were the whole bitmap moved up to its rows,
    // a few px taller on each side (so whatever crosses its edges is drawn)
    // and clipped to its own rows.
    const pad = Math.ceil(GESTURE_BAND_PAD_PX * layer.dpr)
    const top = y0 - pad
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.beginPath()
    ctx.rect(0, y0, layer.canvas.width, y1 - y0)
    ctx.clip()
    paintScene(ctx, { ...layer.transform, y: layer.transform.y - top / layer.dpr }, layer.cssW, (y1 - y0 + pad * 2) / layer.dpr, layer.dpr, 'base', top)
    ctx.restore()
  }, [paintScene])

  /** Paint the next band of the bitmap in progress; true once it is complete. */
  const stepGestureJob = useCallback((job: GestureJob, budgetMs: number): boolean => {
    const t0 = performance.now()
    const height = job.layer.canvas.height
    const y1 = Math.min(height, job.nextRow + job.rows)
    paintGestureRows(job.layer, job.nextRow, y1)
    // Rasterize the band now, so its cost lands in this frame (Chromium would
    // otherwise rasterize every band at once, the first time the bitmap is drawn).
    rasterizeCanvasNow(job.layer.canvas)
    const took = performance.now() - t0
    job.ms += took
    job.rows = nextBandRows(y1 - job.nextRow, took, budgetMs, height)
    job.nextRow = y1
    return y1 >= height
  }, [paintGestureRows])

  /**
   * One frame of a gesture: the bitmaps moved and scaled to the current view
   * — the most detailed one that covers the view, under any more detailed
   * one that covers part of it — with the labels drawn live on top. Before
   * the main bitmap stops fitting the gesture (panned past its margin, zoomed
   * too far, its inputs changed, or not what a zoom animation ends on), its
   * successor is painted a band per frame and takes over once complete; until
   * then the overview bitmap fills anything the main one does not hold. Only
   * when nothing at all can show the view (a gesture straight after load) is
   * a bitmap painted in one go. False when no map is near the view.
   * `zoomStep`: this frame's scale over the previous frame's. `firstFrame`:
   * the gesture started just now, on bitmaps.
   */
  const paintGestureFrame = useCallback((ctx: CanvasRenderingContext2D, cw: number, ch: number, dpr: number, zoomStep: number, firstFrame: boolean): boolean => {
    const L = gestureLayersRef.current
    const inputs = gestureInputs(cw, ch, dpr)
    const anim = animRef.current
    // Where the gesture is heading: a zoom animation's last view, else on from here.
    const target: Transform = anim ? { x: anim.toX, y: anim.toY, scale: anim.toScale } : transform
    // `lookAhead`: whether it will still fit once the gesture has used up
    // half the bitmap's margin, or zoomed on a fifth.
    const fits = (l: GestureLayer | null, lookAhead: boolean): boolean => !!l && sameInputs(l.inputs, inputs) && (anim
      ? gestureCacheUsable(l, target, cw, ch, paintBox, dpr)
      : gestureCacheUsable(l, transform, cw, ch, paintBox, dpr, lookAhead ? { px: (l.margin ?? 0) * 0.5, zoom: 0.2 } : {}))
    const covers = (l: GestureLayer | null) => !!l && gestureCacheCovers(l, transform, cw, ch, paintBox)
    let started = false
    const zoomingOut = !anim && zoomStep < 0.995
    if (!fits(L.main, true)) {
      // A bitmap in progress is kept while it would still fit now (dropping
      // it on the look-ahead too could restart it forever on a quick
      // gesture), and while zooming out, where even part of it helps.
      if (L.job && (L.job.role !== 'main' || !sameInputs(L.job.layer.inputs, inputs) || (!zoomingOut && !fits(L.job.layer, false)))) {
        keepSpareCanvas(L, L.job.layer.canvas)
        L.job = null
      }
      // Zooming out, the overview bitmap behind sharpens by itself: a new
      // bitmap would be outgrown before it was done — unless the gesture
      // starts with none of this view (not painted ahead yet, or out of
      // date): then one of the view it starts from, or it would carry on from
      // the overview bitmap alone, seats turning into blurred blocks.
      const skip = zoomingOut && covers(L.backdrop) && !(firstFrame && !covers(L.main))
      if (!L.job && !skip) {
        // Its margin: none for an animation's last view (nothing pans it),
        // a little while zooming, the full one for a pan.
        const margin = anim ? 0 : Math.abs(zoomStep - 1) > 0.005 ? GESTURE_ZOOM_MARGIN : undefined
        const layer = newGestureLayer(target, cw, ch, dpr, inputs, { supersample: 1, margin }, takeSpareCanvas(L))
        // A small first band: the next ones are sized from what it took.
        if (layer) {
          L.job = { layer, role: 'main', nextRow: 0, rows: Math.max(32, Math.ceil(layer.canvas.height / 12)), ms: 0 }
          started = true
        }
      }
    }
    // One band of the bitmap in progress; it takes over once complete.
    const stepJob = () => {
      const job = L.job
      if (!job || !stepGestureJob(job, GESTURE_JOB_BUDGET_MS)) return
      if (job.role === 'main') setMainLayer(L, job.layer)
      else setBackdropLayer(L, job.layer)
      L.job = null
      // Back to normal frames once the gesture has reached a part of the map
      // this device paints quickly: what painting the bitmap took says what
      // painting the view would. A zoom-in animation tries them as soon as
      // its last view is ready (the frames left are close to it): a scaled
      // full-view bitmap costs a slow phone more than a sparse, zoomed-in
      // view does, and two slow frames send it back here.
      const viewPaintMs = job.ms * (cw * ch * dpr * dpr) / (job.layer.canvas.width * job.layer.canvas.height)
      const zoomInAnimation = !!anim && anim.toScale > transform.scale
      if (job.role === 'main' && (zoomInAnimation || viewPaintMs < GESTURE_LIVE_PAINT_MS)) {
        gestureModeRef.current.mode = 'live'
        gestureModeRef.current.slowLiveFrames = 0
      }
    }
    if (L.job && !covers(L.main) && !covers(L.backdrop)) {
      // Nothing yet that can show the view: paint the rest of the bitmap now.
      L.job.rows = L.job.layer.canvas.height - L.job.nextRow
      stepJob()
    }
    const layers = [L.backdrop, L.main]
      .filter((l, i, all): l is GestureLayer => !!l && all.indexOf(l) === i)
      .sort((a, b) => a.transform.scale - b.transform.scale)
    let first = -1
    for (let i = layers.length - 1; i >= 0 && first < 0; i--) if (covers(layers[i])) first = i
    if (first < 0) return false
    const W = ctx.canvas.width, H = ctx.canvas.height
    const dests = layers.map(l => gestureCacheDest(l, transform, dpr))
    const top = dests[layers.length - 1]
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'low'
    // (The bitmaps are opaque: no background under one that fills the canvas.)
    if (!(top.x <= 0 && top.y <= 0 && top.x + top.w >= W && top.y + top.h >= H)) {
      ctx.fillStyle = colorsRef.current.surfaceBg
      ctx.fillRect(0, 0, W, H)
    }
    for (let i = first; i < layers.length - 1; i++) {
      // A bitmap under the top one is drawn only where the top one does not
      // reach, strip by strip (a scaled full-view draw is most of a frame on
      // a slow phone; a clip path would cost a full-view mask). The strips
      // overlap the top bitmap by a pixel, which is drawn over them.
      const d = dests[i], c = layers[i].canvas
      const x0 = Math.max(0, Math.floor(top.x) + 1), y0 = Math.max(0, Math.floor(top.y) + 1)
      const x1 = Math.min(W, Math.ceil(top.x + top.w) - 1), y1 = Math.min(H, Math.ceil(top.y + top.h) - 1)
      const strips = x1 <= x0 || y1 <= y0
        ? [[0, 0, W, H]]
        : [[0, 0, W, y0], [0, y1, W, H - y1], [0, y0, x0, y1 - y0], [x1, y0, W - x1, y1 - y0]]
      for (const [sx, sy, sw, sh] of strips) {
        if (sw <= 0 || sh <= 0) continue
        const kx = c.width / d.w, ky = c.height / d.h
        ctx.drawImage(c, (sx - d.x) * kx, (sy - d.y) * ky, sw * kx, sh * ky, sx, sy, sw, sh)
      }
    }
    ctx.drawImage(layers[layers.length - 1].canvas, top.x, top.y, top.w, top.h)
    paintScene(ctx, transform, cw, ch, dpr, 'labels')
    gestureFrameShownRef.current = true
    // After the frame is drawn, and not in the frame that started it: the
    // first frame of a gesture (a tap's zoom) stays light.
    if (!started) stepJob()
    // A wheel or a finger held still has no end event: look again once the
    // hold has passed, and paint normally when the gesture is over.
    if (gestureSettleTimerRef.current) clearTimeout(gestureSettleTimerRef.current)
    const check = () => {
      if (inGesture()) { gestureSettleTimerRef.current = setTimeout(check, GESTURE_HOLD_MS / 2); return }
      gestureSettleTimerRef.current = null
      settleGesture()
    }
    gestureSettleTimerRef.current = setTimeout(check, GESTURE_HOLD_MS + 16)
    return true
  }, [gestureInputs, transform, paintBox, newGestureLayer, stepGestureJob, paintScene, inGesture, settleGesture])

  // At rest, in idle time: the main bitmap for this view (with zoom
  // headroom), so the next gesture starts on it, then the overview bitmap
  // behind it — each painted a band per idle slot, so a tap never waits on a
  // whole paint. A view this device paints quickly needs no main bitmap (its
  // gestures start with normal frames) and only an overview bitmap to fall
  // back on. A gesture takes over (and may reuse) the bitmap in progress.
  // Returns whether there is more to paint.
  const stepGesturePrep = useCallback((budgetMs: number): boolean => {
    if (perspectiveEnabled || inGesture() || !canvasRef.current) return false
    const L = gestureLayersRef.current
    const { width: cw, height: ch } = dimensions
    const dpr = mapCanvasDpr(isMobileRef.current)
    const inputs = gestureInputs(cw, ch, dpr)
    const fresh = (l: GestureLayer | null) => !!l && sameInputs(l.inputs, inputs)
    const key = viewKey(transform, cw, ch, dpr)
    const quickView = (gestureModeRef.current.lastLiveFrameMs ?? Infinity) <= GESTURE_LIVE_FRAME_MS
    const mainFresh = !!L.main && fresh(L.main) && L.main.viewKey === key
    const mainHoldsMap = mainFresh && gestureCacheHolds(L.main!, paintBox)
    if (mainHoldsMap && L.backdrop !== L.main) {
      // It holds the whole map (at the overview it is the map): the backdrop too.
      setBackdropLayer(L, L.main!)
    }
    if (quickView && L.main && !mainFresh && L.main !== L.backdrop) {
      // Out of date and not needed: a gesture from here must not draw it.
      keepSpareCanvas(L, L.main.canvas)
      L.main = null
    }
    const role: GestureJob['role'] | null = !(mainFresh || quickView) ? 'main'
      : (!mainHoldsMap && !(quickView ? L.backdrop : fresh(L.backdrop))) ? 'backdrop'
      : null
    const wanted = role === 'main' ? key : role === 'backdrop' ? viewKey(overviewTransform, cw, ch, dpr) : ''
    if (L.job && !(L.job.role === role && L.job.layer.viewKey === wanted && fresh(L.job.layer))) {
      keepSpareCanvas(L, L.job.layer.canvas)
      L.job = null
    }
    if (!role) {
      // At rest only the two bitmaps are kept.
      if (L.spare) { releaseCanvas(L.spare); L.spare = null }
      return false
    }
    if (!L.job) {
      const layer = newGestureLayer(role === 'main' ? transform : overviewTransform, cw, ch, dpr, inputs, {}, takeSpareCanvas(L))
      if (!layer) return false
      // Idle bands start small (a first guess can overshoot) and grow from what they take.
      L.job = { layer, role, nextRow: 0, rows: 32, ms: 0 }
    }
    if (stepGestureJob(L.job, budgetMs)) {
      if (L.job.role === 'main') setMainLayer(L, L.job.layer)
      else setBackdropLayer(L, L.job.layer)
      L.job = null
    }
    return true
  }, [perspectiveEnabled, inGesture, dimensions, gestureInputs, transform, paintBox, overviewTransform, newGestureLayer, stepGestureJob])
  useEffect(() => { gesturePrepRef.current = stepGesturePrep }, [stepGesturePrep])

  // Timers off and the bitmaps' memory back on unmount (WebKit holds a
  // canvas's backing store until it is collected otherwise).
  useEffect(() => () => {
    if (gestureSettleTimerRef.current) clearTimeout(gestureSettleTimerRef.current)
    if (gesturePrepTimerRef.current) clearTimeout(gesturePrepTimerRef.current)
    const L = gestureLayersRef.current
    for (const c of [L.backdrop?.canvas, L.main?.canvas, L.job?.layer.canvas, L.spare]) if (c) releaseCanvas(c)
    gestureLayersRef.current = { backdrop: null, main: null, job: null, spare: null }
  }, [])

  // --- Draw ---
  const draw = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { width: cw, height: ch } = dimensions
    const dpr = mapCanvasDpr(isMobileRef.current)
    // Resize the backing store only when its size actually changes. Assigning
    // canvas.width — even the same value — throws the bitmap away and
    // allocates a new one, which this did on EVERY frame of every pan and
    // pinch. Otherwise the context is put back to the exact state a fresh one
    // has, so each frame still starts from the same blank, default canvas.
    const backingW = Math.trunc(cw * dpr)
    const backingH = Math.trunc(ch * dpr)
    if (canvas.width !== backingW || canvas.height !== backingH) {
      canvas.width = backingW
      canvas.height = backingH
    } else {
      resetCanvasContext(ctx)
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    const cssW = `${cw}px`, cssH = `${ch}px`
    if (canvas.style.width !== cssW) canvas.style.width = cssW
    if (canvas.style.height !== cssH) canvas.style.height = cssH

    // Mid-gesture, on a map that is slow to paint: the bitmaps, moved (the
    // tilt is not a plain move: never). A map this device paints in under a
    // frame is painted normally throughout, as sharp as at rest; a gesture
    // switches to the bitmaps once normal frames turn slow (zooming out to a
    // busy overview).
    const G = gestureModeRef.current
    if (!GESTURE_BITMAPS || perspectiveEnabled || !inGesture()) {
      G.mode = null
      lastGestureViewRef.current = null
    } else {
      const prevView = lastGestureViewRef.current
      lastGestureViewRef.current = transform
      const prevMode = G.mode
      if (G.mode === null || (G.mode === 'live' && G.slowLiveFrames >= 2)) {
        G.mode = G.mode === null && (G.lastLiveFrameMs ?? Infinity) <= GESTURE_LIVE_FRAME_MS ? 'live' : 'bitmap'
      }
      if (G.mode === 'bitmap' && paintGestureFrame(ctx, cw, ch, dpr, prevView ? transform.scale / prevView.scale : 1, prevMode === null)) return
    }

    const paintStart = performance.now()
    paintScene(ctx, transform, cw, ch, dpr, 'all')
    gestureFrameShownRef.current = false
    // How long this frame takes on this device, through to the next one.
    const inGestureNow = G.mode !== null
    requestAnimationFrame(() => {
      const ms = performance.now() - paintStart
      G.lastLiveFrameMs = ms
      G.slowLiveFrames = inGestureNow && ms > GESTURE_LIVE_SLOW_FRAME_MS ? G.slowLiveFrames + 1 : 0
    })
    scheduleGesturePrep()
  }, [dimensions, transform, perspectiveEnabled, inGesture, paintGestureFrame, paintScene, scheduleGesturePrep])

  useEffect(() => { drawRef.current = draw }, [draw])

  // Redraw on any visual state change. draw() reads volatile hover/selection from refs,
  // so it doesn't need to be recreated for those — we just trigger a new rAF.
  useEffect(() => {
    requestAnimationFrame(draw)
  }, [draw, hoveredSeatId, hoveredSectionKey, focusedSectionKey, selectedSeatIds, selectedSectionKeys, keyboardFocusedSectionIdx, keyboardMode])

  // --- Mouse interaction ---
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return
    // Disable keyboard mode when user interacts with mouse
    if (keyboardMode) {
      setKeyboardMode(false)
      setKeyboardFocusedSectionIdx(-1)
    }
    mouseDownRef.current = true
    isDraggingRef.current = false
    dragStartRef.current = { x: e.clientX, y: e.clientY, tx: transform.x, ty: transform.y }

    // Multi-select drawing modes
    if (allowMultiSelect && (selectionMode === 'box' || selectionMode === 'circle')) {
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return
      const cx = e.clientX - rect.left, cy = e.clientY - rect.top
      const worldX = (cx - transform.x) / transform.scale
      const worldY = (cy - transform.y) / transform.scale
      selectionStartRef.current = { x: worldX, y: worldY }
      setIsDrawingSelection(true)
    }
  }, [transform, isAnimating, keyboardMode, allowMultiSelect, selectionMode])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isAnimating) return
    const rect = canvasRef.current?.getBoundingClientRect()
    if (!rect) return

    if (mouseDownRef.current) {
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      if (Math.abs(dx) > CLICK_THRESHOLD || Math.abs(dy) > CLICK_THRESHOLD) {
        isDraggingRef.current = true

        // If drawing selection, update overlay instead of panning
        if (isDrawingSelection && selectionStartRef.current) {
          const selRect = canvasRef.current?.getBoundingClientRect()
          if (!selRect) return
          const scx = e.clientX - selRect.left, scy = e.clientY - selRect.top
          const worldX = (scx - transform.x) / transform.scale
          const worldY = (scy - transform.y) / transform.scale

          if (selectionMode === 'box') {
            setDragBoxState({
              startX: selectionStartRef.current.x,
              startY: selectionStartRef.current.y,
              endX: worldX,
              endY: worldY,
            })
          } else if (selectionMode === 'circle') {
            const radius = Math.sqrt(
              Math.pow(worldX - selectionStartRef.current.x, 2) +
              Math.pow(worldY - selectionStartRef.current.y, 2)
            )
            setCircleState({
              centerX: selectionStartRef.current.x,
              centerY: selectionStartRef.current.y,
              radius,
            })
          }
        } else {
          // Normal panning
          markGesture()
          setTransform(prev => ({
            ...prev,
            x: dragStartRef.current.tx + dx,
            y: dragStartRef.current.ty + dy,
          }))
          if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing'
        }
      }
      return
    }

    // Hover
    const clientX = e.clientX
    const clientY = e.clientY
    if (pendingHoverRef.current) cancelAnimationFrame(pendingHoverRef.current)
    pendingHoverRef.current = requestAnimationFrame(() => {
      const hRect = canvasRef.current?.getBoundingClientRect()
      if (!hRect) return
      const hx = clientX - hRect.left, hy = clientY - hRect.top
      const result = hitTest(hx, hy)

      if (result?.type === 'seat') {
        setHoveredSeatId(result.seat.seatId)
        setHoveredSeat(result.seat)
        setHoveredSectionKey(null)
        setHoverPos(null)
        // Position tooltip at the seat's top edge (not cursor) for accurate alignment
        const t = transformRef.current
        const seatScreenX = result.seat.absX * t.scale + t.x + hRect.left
        const seatTopY = (result.seat.absY - result.seat.radius) * t.scale + t.y + hRect.top
        setSeatHoverPos(allowHeldSeatInteraction ? { x: seatScreenX - hRect.left, y: seatTopY - hRect.top } : null)
        onSeatHover(result.seat, seatScreenX, seatTopY)

        // Update mobile context when zoomed in on mobile
        const isMobile = isMobileRef.current
        if (isMobile && isZoomedIn) {
          const sectionData = sectionDataMap.get(result.seat.sectionKey)
          setMobileContext({
            sectionName: sectionData?.name || result.seat.sectionKey,
            rowLabel: result.seat.rowLabel,
          })
        }

        if (canvasRef.current) {
          const seatHoverable = result.seat.status === 'FREE'
            || selectedSeatIds.has(result.seat.seatId)
            || (allowHeldSeatInteraction && result.seat.status === 'HELD')
          canvasRef.current.style.cursor = seatHoverable ? 'pointer' : 'not-allowed'
        }
      } else if (result?.type === 'section') {
        setHoveredSeatId(null)
        setHoveredSeat(null)
        setHoveredSectionKey(result.sectionKey)
        setHoverPos({ x: clientX - rect.left, y: clientY - rect.top })
        setSeatHoverPos(null)
        onSeatHover(null, 0, 0)

        // Clear mobile context when not on a seat
        setMobileContext(null)

        if (canvasRef.current) {
          const sd = sectionDataMap.get(result.sectionKey)
          const sectionClickable = sd && (sd.available > 0 || (allowHeldSeatInteraction && sd.held > 0))
          canvasRef.current.style.cursor = sectionClickable ? 'pointer' : 'not-allowed'
        }
      } else {
        setHoveredSeatId(null)
        setHoveredSeat(null)
        setHoveredSectionKey(null)
        setHoverPos(null)
        setSeatHoverPos(null)
        onSeatHover(null, 0, 0)
        setMobileContext(null)
        if (canvasRef.current) canvasRef.current.style.cursor = 'default'
      }
    })
  }, [isAnimating, hitTest, onSeatHover, selectedSeatIds, sectionDataMap, allowHeldSeatInteraction, markGesture])

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    mouseDownRef.current = false
    endGesture()
    if (isAnimating) return

    // Complete multi-select drawing
    if (isDrawingSelection && isDraggingRef.current) {
      if (selectionMode === 'box' && dragBoxState && onBulkSelect) {
        // Get all loaded seats
        const allSeats: ComputedSeatPosition[] = []
        for (const [, seats] of cache.getAllLoaded()) {
          allSeats.push(...seats)
        }
        // Filter seats in box
        const seatsInBox = getSeatsInBox(dragBoxState, allSeats)
        if (seatsInBox.length > 0) {
          onBulkSelect(seatsInBox)
        }
      } else if (selectionMode === 'circle' && circleState && onBulkSelect) {
        // Get all loaded seats
        const allSeats: ComputedSeatPosition[] = []
        for (const [, seats] of cache.getAllLoaded()) {
          allSeats.push(...seats)
        }
        // Filter seats in circle
        const seatsInCircle = getSeatsInCircle(circleState, allSeats)
        if (seatsInCircle.length > 0) {
          onBulkSelect(seatsInCircle)
        }
      }

      // Clear drawing state
      setIsDrawingSelection(false)
      setDragBoxState(null)
      setCircleState(null)
      selectionStartRef.current = null
      isDraggingRef.current = false
      if (canvasRef.current) canvasRef.current.style.cursor = 'default'
      return
    }

    if (!isDraggingRef.current) {
      const rect = canvasRef.current?.getBoundingClientRect()
      if (rect) {
        const cx = e.clientX - rect.left, cy = e.clientY - rect.top
        const result = hitTest(cx, cy)
        if (result?.type === 'seat') {
          const seatClickable = result.seat.status === 'FREE'
            || selectedSeatIds.has(result.seat.seatId)
            || (allowHeldSeatInteraction && result.seat.status === 'HELD')
          if (seatClickable) {
            // Re-read from cache in case polling updated status since last render.
            // We only want to suppress the click for true race conditions — a seat
            // that was FREE at paint time but got booked/held by another session
            // in between. When the caller opts into HELD interaction (e.g. the
            // box office sale override flow), we MUST let HELD clicks fall
            // through to onSeatClick so the parent can surface an override UX.
            const freshSeats = cache.get(result.seat.sectionKey)
            const freshSeat = freshSeats?.find(s => s.seatId === result.seat.seatId)
            if (freshSeat && freshSeat.status !== 'FREE' && !selectedSeatIds.has(freshSeat.seatId)) {
              const heldInteractable = allowHeldSeatInteraction && freshSeat.status === 'HELD'
              if (!heldInteractable) {
                setRenderTick(t => t + 1) // Force re-render to show updated colors
                return
              }
              // Keep the fresh reference so downstream handlers see current status
              result.seat = freshSeat
            }

            // Row mode: select all seats in the row (works from overview too — also zoom in)
            if (selectionMode === 'row' && allowMultiSelect && onBulkSelect) {
              const sectionData = cache.get(result.seat.sectionKey)
              if (sectionData) {
                const seatsInRow = getSeatsInRow(sectionData, result.seat.rowLabel)
                if (seatsInRow.length > 0) {
                  onBulkSelect(seatsInRow)
                }
              }
              if (!isZoomedIn) {
                zoomToSection(result.seat.sectionKey)
              }
              return
            }

            // At overview zoom level in click mode, just zoom to the section — don't select.
            // Users should zoom in first to see seat details before picking one.
            if (!isZoomedIn && !selectedSeatIds.has(result.seat.seatId)) {
              zoomToSection(result.seat.sectionKey)
              return
            }

            {
              onSeatClick(result.seat)
              // Only auto-zoom if not already viewing this section
              if (focusedSectionKey !== result.seat.sectionKey) {
                zoomToSection(result.seat.sectionKey)
              }
            }
          }
        } else if (result?.type === 'section') {
          const sd = sectionDataMap.get(result.sectionKey)
          const ls = layoutSectionMap.get(result.sectionKey)
          // If clicking a dimmed (non-active floor) section, switch to that floor
          if (activeFloor != null && ls?.floor != null && ls.floor !== activeFloor) {
            onFloorSwitch?.(ls.floor)
          } else {
            const hasAvailability = sd ? (sd.available > 0 || (allowHeldSeatInteraction && sd.held > 0)) : true
            // Route by section type
            const sType = ls?.sectionType || sd?.sectionType || 'SEATED'
            if (hasAvailability && sType === 'GA' && onSectionClick) {
              onSectionClick(result.sectionKey, 'GA')
            } else if (hasAvailability && sType === 'BOOTH' && onSectionClick) {
              onSectionClick(result.sectionKey, 'BOOTH')
            } else if (hasAvailability && sType === 'TABLE' && ls?.tableConfig?.bookingMode !== 'by-seat' && onSectionClick) {
              onSectionClick(result.sectionKey, 'WHOLE_TABLE')
            } else {
              // Always zoom to section on click — let user explore the map
              // (the click point only matters on touch screens)
              zoomToSection(result.sectionKey, {
                x: (cx - transform.x) / transform.scale,
                y: (cy - transform.y) / transform.scale,
              })
            }
          }
        }
      }
    }

    isDraggingRef.current = false
    if (canvasRef.current) canvasRef.current.style.cursor = 'default'
  }, [isAnimating, transform, hitTest, selectedSeatIds, onSeatClick, panToSeat, sectionDataMap, layoutSectionMap, zoomToSection, activeFloor, onFloorSwitch, onSectionClick, isDrawingSelection, selectionMode, dragBoxState, circleState, onBulkSelect, cache, allowMultiSelect, allowHeldSeatInteraction, isZoomedIn, focusedSectionKey, endGesture])

  const handleMouseLeave = useCallback(() => {
    mouseDownRef.current = false
    isDraggingRef.current = false
    endGesture()
    setHoveredSeatId(null)
    setHoveredSeat(null)
    setHoveredSectionKey(null)
    setHoverPos(null)
    setSeatHoverPos(null)
    onSeatHover(null, 0, 0)
    if (canvasRef.current) canvasRef.current.style.cursor = 'default'
  }, [onSeatHover, endGesture])

  // --- Wheel zoom / trackpad pan ---
  const handleWheel = useCallback((e: WheelEvent) => {
    if (isAnimating) return
    e.preventDefault()
    markGesture()

    // Trackpad pinch: ctrlKey is set by the browser for pinch-to-zoom gestures
    // Mouse wheel: always zoom (no ctrlKey but deltaMode is usually 0)
    // Trackpad two-finger scroll: pan (no ctrlKey, deltaX/deltaY for direction)
    const isPinchZoom = e.ctrlKey
    const isMouseWheel = e.deltaMode === 1 || (Math.abs(e.deltaY) >= 50 && !e.ctrlKey)

    if (isPinchZoom || isMouseWheel) {
      // Zoom
      const rect = canvasRef.current?.getBoundingClientRect()
      if (!rect) return
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top
      const zoomFactor = e.deltaY < 0 ? 1.12 : 0.88

      // Don't clear focus on zoom — let user zoom in/out while keeping section focused
      // Focus is only cleared by clicking overview button or clicking a different section

      setTransform(prev => {
        const newScale = clampScale(prev.scale * zoomFactor)
        return {
          scale: newScale,
          x: mouseX - (mouseX - prev.x) * (newScale / prev.scale),
          y: mouseY - (mouseY - prev.y) * (newScale / prev.scale),
        }
      })
    } else {
      // Trackpad two-finger scroll → pan
      setTransform(prev => ({
        ...prev,
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }))
    }
  }, [isAnimating, onSectionFocus, clampScale, markGesture])

  // Stable wheel ref to avoid tearing down/re-adding the listener on every zoom frame
  const handleWheelRef = useRef(handleWheel)
  handleWheelRef.current = handleWheel

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const handler = (e: WheelEvent) => handleWheelRef.current(e)
    canvas.addEventListener('wheel', handler, { passive: false })
    return () => canvas.removeEventListener('wheel', handler)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Screen reader announcement helper (must be before handleKeyDown)
  const announceToScreenReader = (message: string) => {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.setAttribute('aria-atomic', 'true')
    announcement.style.position = 'absolute'
    announcement.style.left = '-10000px'
    announcement.style.width = '1px'
    announcement.style.height = '1px'
    announcement.style.overflow = 'hidden'
    announcement.textContent = message
    document.body.appendChild(announcement)
    setTimeout(() => document.body.removeChild(announcement), 1000)
  }

  // Zoom out handler — keep focus, only clear via overview button.
  // Pivots on the centre of the VISIBLE area (above the phone's bottom panel).
  // Stepping out past the overview lands exactly on it rather than on a
  // shrunken, off-centre chart.
  const handleZoomOut = useCallback(() => {
    setTransform(prev => {
      const newScale = clampScale(prev.scale / 1.3)
      if (newScale <= fitScale) return overviewTransform
      const cx = dimensions.width / 2
      const cy = viewH / 2
      return { scale: newScale, x: cx - (cx - prev.x) * (newScale / prev.scale), y: cy - (cy - prev.y) * (newScale / prev.scale) }
    })
  }, [dimensions, viewH, fitScale, overviewTransform, clampScale])

  // --- Keyboard navigation ---
  // Seat-level keyboard navigation: once zoomed into a section, arrows walk
  // individual seats (visual left/right within row, up/down across rows),
  // Enter toggles selection. Section-level keyboard alone could never PICK
  // a seat — the core accessibility gap vs the industry standard.
  const keyboardSeatIdxRef = useRef<number>(-1)
  const navigateSeats = useCallback((key: string): boolean => {
    const sectionKey = focusedSectionKeyRef.current
    if (!sectionKey) return false
    const seats = cache.get(sectionKey)
    if (!seats || seats.length === 0) return false
    // Order rows top→bottom, seats left→right for spatial arrow semantics
    const ordered = [...seats].sort((a, b) => (a.absY - b.absY) || (a.absX - b.absX))
    let idx = keyboardSeatIdxRef.current
    const current = idx >= 0 ? ordered.find((_, i) => i === idx) : null

    if (key === 'ArrowLeft' || key === 'ArrowRight') {
      idx = idx < 0 ? 0 : Math.max(0, Math.min(ordered.length - 1, idx + (key === 'ArrowRight' ? 1 : -1)))
    } else if (key === 'ArrowUp' || key === 'ArrowDown') {
      if (!current) {
        idx = 0
      } else {
        // Nearest seat in the adjacent row (closest x)
        const dir = key === 'ArrowDown' ? 1 : -1
        const rowTol = current.radius * 1.5
        const candidates = ordered
          .map((s, i) => ({ s, i }))
          .filter(({ s }) => dir > 0 ? s.absY > current.absY + rowTol : s.absY < current.absY - rowTol)
        if (candidates.length === 0) return true // edge of section — consume key
        const nextRowY = dir > 0
          ? Math.min(...candidates.map(c => c.s.absY))
          : Math.max(...candidates.map(c => c.s.absY))
        const rowSeats = candidates.filter(c => Math.abs(c.s.absY - nextRowY) <= rowTol)
        idx = rowSeats.reduce((best, c) =>
          Math.abs(c.s.absX - current.absX) < Math.abs(ordered[best].absX - current.absX) ? c.i : best,
          rowSeats[0].i)
      }
    } else {
      return false
    }

    keyboardSeatIdxRef.current = idx
    const seat = ordered[idx]
    if (seat) {
      setHoveredSeatId(seat.seatId)
      panToSeat(seat)
      const state = selectedSeatIds.has(seat.seatId) ? 'selected'
        : seat.status === 'FREE' ? 'available' : 'unavailable'
      announceToScreenReader(
        `Row ${seat.rowLabel}, seat ${seat.seatNumber}, ${seat.category?.name || 'standard'}, ${seat.price != null ? `$${seat.price}` : ''}, ${state}`,
      )
    }
    return true
  }, [cache, selectedSeatIds, panToSeat])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Get visible sections (respect active floor if set)
    const visibleSections = sections.filter(s => {
      if (activeFloor === undefined || activeFloor === null) return true
      return s.floor === activeFloor || s.floor === undefined || s.floor === null
    })

    if (visibleSections.length === 0) return

    // Enable keyboard mode on first keypress
    if (!keyboardMode && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'Escape'].includes(e.key)) {
      setKeyboardMode(true)
    }

    // When a section is focused, arrows walk seats and Enter toggles the
    // hovered seat; Escape falls through to zoom out (and resets the cursor).
    if (focusedSectionKeyRef.current && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      if (navigateSeats(e.key)) { e.preventDefault(); return }
    }
    if (focusedSectionKeyRef.current && (e.key === 'Enter' || e.key === ' ') && keyboardSeatIdxRef.current >= 0) {
      e.preventDefault()
      const seats = cache.get(focusedSectionKeyRef.current)
      if (seats) {
        const ordered = [...seats].sort((a, b) => (a.absY - b.absY) || (a.absX - b.absX))
        const seat = ordered[keyboardSeatIdxRef.current]
        if (seat && (seat.status === 'FREE' || selectedSeatIds.has(seat.seatId))) {
          onSeatClick(seat)
          announceToScreenReader(
            selectedSeatIds.has(seat.seatId)
              ? `Removed row ${seat.rowLabel} seat ${seat.seatNumber}`
              : `Selected row ${seat.rowLabel} seat ${seat.seatNumber}`,
          )
        } else if (seat) {
          announceToScreenReader(`Row ${seat.rowLabel} seat ${seat.seatNumber} is unavailable`)
        }
      }
      return
    }
    if (e.key === 'Escape') keyboardSeatIdxRef.current = -1

    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault()
        setKeyboardFocusedSectionIdx(prev => {
          const newIdx = prev <= 0 ? visibleSections.length - 1 : prev - 1
          // Announce section name for screen readers
          const section = visibleSections[newIdx]
          if (section) {
            const sectionData = sectionDataMap.get(section.sectionKey)
            const announcement = showRemainingQuantity ? `${sectionData?.name || section.sectionKey}, ${section.available} seats available` : `${sectionData?.name || section.sectionKey}, seats available`
            announceToScreenReader(announcement)
          }
          return newIdx
        })
        break
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault()
        setKeyboardFocusedSectionIdx(prev => {
          const newIdx = prev >= visibleSections.length - 1 ? 0 : prev + 1
          // Announce section name for screen readers
          const section = visibleSections[newIdx]
          if (section) {
            const sectionData = sectionDataMap.get(section.sectionKey)
            const announcement = showRemainingQuantity ? `${sectionData?.name || section.sectionKey}, ${section.available} seats available` : `${sectionData?.name || section.sectionKey}, seats available`
            announceToScreenReader(announcement)
          }
          return newIdx
        })
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (keyboardFocusedSectionIdx >= 0 && keyboardFocusedSectionIdx < visibleSections.length) {
          const section = visibleSections[keyboardFocusedSectionIdx]
          zoomToSection(section.sectionKey)
          announceToScreenReader(`Zoomed to ${sectionDataMap.get(section.sectionKey)?.name || section.sectionKey}`)
        }
        break
      case 'Escape':
        e.preventDefault()
        if (isZoomedIn) {
          handleZoomOut()
          announceToScreenReader('Zoomed out to full venue view')
        } else {
          setKeyboardMode(false)
          setKeyboardFocusedSectionIdx(-1)
        }
        break
    }
  }, [sections, activeFloor, keyboardMode, keyboardFocusedSectionIdx, sectionDataMap, isZoomedIn, handleZoomOut, zoomToSection, announceToScreenReader, navigateSeats, cache, onSeatClick, selectedSeatIds])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.addEventListener('keydown', handleKeyDown as any)
    return () => canvas.removeEventListener('keydown', handleKeyDown as any)
  }, [handleKeyDown])

  // --- Touch events ---
  const touchStartRef = useRef<{ touches: Array<{ x: number; y: number }>; scale: number; x: number; y: number }>({
    touches: [], scale: 1, x: 0, y: 0,
  })
  const touchDraggingRef = useRef(false)
  // Double-tap zoom + pan momentum (standard map gestures; their absence
  // made the map feel dead next to seats.io)
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null)
  const panVelocityRef = useRef<{ vx: number; vy: number; lastX: number; lastY: number; lastT: number }>({ vx: 0, vy: 0, lastX: 0, lastY: 0, lastT: 0 })
  const momentumRafRef = useRef<number | null>(null)

  const cancelMomentum = useCallback(() => {
    if (momentumRafRef.current != null) {
      cancelAnimationFrame(momentumRafRef.current)
      momentumRafRef.current = null
    }
  }, [])

  const startMomentum = useCallback(() => {
    const v = panVelocityRef.current
    let vx = v.vx, vy = v.vy
    const speed = Math.hypot(vx, vy)
    if (speed < 0.25) return // too slow — no glide
    const step = () => {
      vx *= 0.93
      vy *= 0.93
      if (Math.hypot(vx, vy) < 0.05) { momentumRafRef.current = null; settleGesture(); return }
      setTransform(prev => ({ ...prev, x: prev.x + vx * 16, y: prev.y + vy * 16 }))
      momentumRafRef.current = requestAnimationFrame(step)
    }
    momentumRafRef.current = requestAnimationFrame(step)
  }, [settleGesture])

  /** Zoom x2 toward a canvas-space point (double-tap / double-click). At deep
   *  zoom it returns to the overview (it used to reset to scale 1 at the
   *  top-left corner of the designer canvas). */
  const zoomTowardPoint = useCallback((cx: number, cy: number) => {
    cancelMomentum()
    if (transformRef.current.scale >= MAX_SCALE * 0.9) {
      zoomToOverview()
      return
    }
    setTransform(prev => {
      const newScale = Math.min(MAX_SCALE, prev.scale * 2)
      return {
        scale: newScale,
        x: cx - (cx - prev.x) * (newScale / prev.scale),
        y: cy - (cy - prev.y) * (newScale / prev.scale),
      }
    })
  }, [cancelMomentum, zoomToOverview, MAX_SCALE])

  /** Start (or restart) a touch gesture from these fingers and this view.
   *  Fingers are kept in canvas coordinates: the pinch centre is a point on
   *  the canvas, and the canvas does not start at the top of the screen (it
   *  used to be taken in screen coordinates, so a pinch zoomed toward a point
   *  a header's height below the fingers). */
  const baselineTouches = useCallback((list: React.TouchList, t: Transform) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    const ox = rect?.left ?? 0, oy = rect?.top ?? 0
    const touches = Array.from(list).map(p => ({ x: p.clientX - ox, y: p.clientY - oy }))
    touchStartRef.current = { touches, scale: t.scale, x: t.x, y: t.y }
    panVelocityRef.current = { vx: 0, vy: 0, lastX: touches[0]?.x ?? 0, lastY: touches[0]?.y ?? 0, lastT: performance.now() }
    return touches
  }, [])

  /** The view a touch move put on screen, also read by the next baseline
   *  before React has rendered it. */
  const applyTouchTransform = useCallback((next: Transform) => {
    transformRef.current = next
    setTransform(next)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    cancelMomentum()
    // A second finger makes it a pinch, never a tap
    touchDraggingRef.current = e.touches.length > 1
    baselineTouches(e.touches, transformRef.current)
  }, [cancelMomentum, baselineTouches])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    // No preventDefault: React listens to touchmove passively, so it never
    // took effect and only logged a browser warning on every move of every
    // drag. `touch-action: none` on the canvas is what stops page scrolling.
    const rect = canvasRef.current?.getBoundingClientRect()
    const ox = rect?.left ?? 0, oy = rect?.top ?? 0
    const touches = Array.from(e.touches).map(t => ({ x: t.clientX - ox, y: t.clientY - oy }))
    let start = touchStartRef.current
    if (touches.length !== start.touches.length) return

    // Require intentional drag (15px dead zone) to prevent accidental panning
    // during taps. The drag then starts from where the finger is, so the map
    // does not jump the 15px.
    if (touches.length === 1 && !touchDraggingRef.current) {
      const dx = touches[0].x - start.touches[0].x
      const dy = touches[0].y - start.touches[0].y
      if (Math.abs(dx) < 15 && Math.abs(dy) < 15) return
      touchDraggingRef.current = true
      start = { ...start, touches }
      touchStartRef.current = start
    }
    // A drag or pinch during a zoom animation takes over from the view the
    // animation has reached (touches were dropped until it ended, and the
    // first move after it then jumped from the previous gesture's start).
    if (animRef.current) {
      animRef.current = null
      setIsAnimating(false)
      baselineTouches(e.touches, transformRef.current)
      return
    }

    if (touches.length === 1) {
      // Track velocity (px/ms) for release momentum
      const now = performance.now()
      const v = panVelocityRef.current
      const dt = now - v.lastT
      if (dt > 0) {
        v.vx = (touches[0].x - v.lastX) / dt
        v.vy = (touches[0].y - v.lastY) / dt
        v.lastX = touches[0].x
        v.lastY = touches[0].y
        v.lastT = now
      }
      markGesture()
      applyTouchTransform({
        scale: start.scale,
        x: start.x + touches[0].x - start.touches[0].x,
        y: start.y + touches[0].y - start.touches[0].y,
      })
    } else if (touches.length === 2) {
      // On mobile, pinch zoom does NOT clear focus — user is just adjusting their view
      touchDraggingRef.current = true
      const [s0, s1] = start.touches
      const startDist = Math.hypot(s1.x - s0.x, s1.y - s0.y)
      const curDist = Math.hypot(touches[1].x - touches[0].x, touches[1].y - touches[0].y)
      const newScale = clampScale(start.scale * curDist / Math.max(startDist, 1))
      const k = newScale / start.scale
      // The map point that was between the fingers stays between them, so
      // moving both fingers pans while they pinch
      const startMidX = (s0.x + s1.x) / 2, startMidY = (s0.y + s1.y) / 2
      const midX = (touches[0].x + touches[1].x) / 2
      const midY = (touches[0].y + touches[1].y) / 2
      markGesture()
      applyTouchTransform({
        scale: newScale,
        x: midX - (startMidX - start.x) * k,
        y: midY - (startMidY - start.y) * k,
      })
    }
  }, [clampScale, markGesture, baselineTouches, applyTouchTransform])

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    // A finger lifted from a pinch: the fingers left carry on from this view
    // (they used to do nothing until every finger was up)
    if (e.touches.length > 0) {
      baselineTouches(e.touches, transformRef.current)
      return
    }
    if (isAnimating) return
    // Momentum glide when a drag ends with velocity
    if (e.touches.length === 0 && touchDraggingRef.current) {
      startMomentum()
    }
    // Last finger up: paint the map normally (after the glide, if any)
    if (e.touches.length === 0) endGesture()
    if (e.changedTouches.length === 1 && e.touches.length === 0) {
      const start = touchStartRef.current
      // Only fire tap if single-finger gesture and no dragging occurred
      if (start.touches.length === 1 && !touchDraggingRef.current) {
        // Prevent the browser from also firing mousedown/mouseup/click
        // which would double-toggle the seat selection
        e.preventDefault()
        const touch = e.changedTouches[0]
        const rect = canvasRef.current?.getBoundingClientRect()
        if (rect) {
          const cx = touch.clientX - rect.left, cy = touch.clientY - rect.top

          // Double-tap zoom: two taps within 300ms and 30px zoom toward the
          // point (or reset when already deep). Consumes the tap.
          const nowTap = performance.now()
          const last = lastTapRef.current
          if (last && nowTap - last.time < 300 && Math.hypot(cx - last.x, cy - last.y) < 30) {
            lastTapRef.current = null
            zoomTowardPoint(cx, cy)
            return
          }
          lastTapRef.current = { time: nowTap, x: cx, y: cy }

          const result = hitTest(cx, cy)
          if (result?.type === 'seat') {
            const touchSeatClickable = result.seat.status === 'FREE'
              || selectedSeatIds.has(result.seat.seatId)
              || (allowHeldSeatInteraction && result.seat.status === 'HELD')
            if (touchSeatClickable) {
              // Re-read from cache in case polling updated status since last
              // render. Suppress for true races, but when the caller opts into
              // HELD interaction (override flow) let HELD clicks through.
              const freshSeats = cache.get(result.seat.sectionKey)
              const freshSeat = freshSeats?.find(s => s.seatId === result.seat.seatId)
              const heldInteractable = !!(freshSeat && allowHeldSeatInteraction && freshSeat.status === 'HELD')
              if (freshSeat && freshSeat.status !== 'FREE' && !selectedSeatIds.has(freshSeat.seatId) && !heldInteractable) {
                setRenderTick(t => t + 1)
              } else {
                if (heldInteractable) result.seat = freshSeat
                onSeatClick(result.seat)
                // On mobile, don't re-center — user controls the view with touch gestures
                // Just ensure the section is focused so seats stay visible
                if (!focusedSectionKeyRef.current) {
                  setFocusedSectionKey(result.seat.sectionKey)
                  onSectionFocus?.(result.seat.sectionKey)
                }
              }
            }
          } else if (result?.type === 'section') {
            const sd = sectionDataMap.get(result.sectionKey)
            const ls = layoutSectionMap.get(result.sectionKey)
            if (activeFloor != null && ls?.floor != null && ls.floor !== activeFloor) {
              onFloorSwitch?.(ls.floor)
            } else if (sd) {
              const hasAvailability = sd.available > 0 || (allowHeldSeatInteraction && sd.held > 0)
              const sType = ls?.sectionType || sd.sectionType || 'SEATED'
              if (hasAvailability && sType === 'GA' && onSectionClick) {
                onSectionClick(result.sectionKey, 'GA')
              } else if (hasAvailability && sType === 'BOOTH' && onSectionClick) {
                onSectionClick(result.sectionKey, 'BOOTH')
              } else if (hasAvailability && sType === 'TABLE' && ls?.tableConfig?.bookingMode !== 'by-seat' && onSectionClick) {
                onSectionClick(result.sectionKey, 'WHOLE_TABLE')
              } else {
                // Zoom in around the tapped point (seats ~8px wide on touch)
                zoomToSection(result.sectionKey, {
                  x: (cx - transform.x) / transform.scale,
                  y: (cy - transform.y) / transform.scale,
                })
              }
            }
          }
        }
      }
    }
  }, [isAnimating, transform, hitTest, selectedSeatIds, onSeatClick, sectionDataMap, layoutSectionMap, zoomToSection, activeFloor, onFloorSwitch, onSectionClick, onSectionFocus, allowHeldSeatInteraction, cache, endGesture, baselineTouches])

  // --- Zoom controls ---
  const handleZoomIn = useCallback(() => {
    setTransform(prev => {
      const newScale = clampScale(prev.scale * 1.3)
      const cx = dimensions.width / 2
      const cy = viewH / 2
      return { scale: newScale, x: cx - (cx - prev.x) * (newScale / prev.scale), y: cy - (cy - prev.y) * (newScale / prev.scale) }
    })
  }, [dimensions, viewH, clampScale])

  const handleFitAll = useCallback(() => {
    zoomToOverview()
  }, [zoomToOverview])

  // Screen-reader seat list fallback: a DOM alternative to the canvas
  // (the industry-standard accessible mode). Scoped to the focused section to
  // bound DOM size; each available seat is a real button wired to the same
  // selection handler as canvas clicks. Built only when the focused section,
  // its seats or the selection change — not on every frame of a pan, where it
  // re-rendered a few hundred buttons each time.
  const srSeatList = useMemo(() => (
    <div className="sr-only" aria-label="Seat list">
      {focusedSectionKey && (() => {
        const srSeats = cache.get(focusedSectionKey)
        if (!srSeats || srSeats.length === 0) {
          return <p>Zoom into a section to list its seats.</p>
        }
        const sdName = sectionDataMap.get(focusedSectionKey)?.name || 'Section'
        return (
          <ul aria-label={`Seats in ${sdName}`}>
            {srSeats.map(seat => {
              const isSelected = selectedSeatIds.has(seat.seatId)
              const canPick = seat.status === 'FREE' || isSelected
              return (
                <li key={seat.seatId}>
                  {canPick ? (
                    <button type="button" onClick={() => onSeatClick(seat)}>
                      {`Row ${seat.rowLabel} seat ${seat.seatNumber}, ${seat.category?.name || 'standard'}${seat.price != null ? `, $${seat.price}` : ''}${isSelected ? ', selected — activate to remove' : ', available — activate to select'}`}
                    </button>
                  ) : (
                    <span>{`Row ${seat.rowLabel} seat ${seat.seatNumber}, unavailable`}</span>
                  )}
                </li>
              )
            })}
          </ul>
        )
      })()}
    </div>
  ), [focusedSectionKey, cache, sectionDataMap, selectedSeatIds, onSeatClick, renderTick]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col h-full">
      {/* Canvas */}
      <div
        ref={containerRef}
        className="flex-1 relative overflow-hidden bg-surface-secondary"
        style={{
          minHeight: '300px', touchAction: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          tabIndex={0}
          onDoubleClick={(e) => {
            const rect = canvasRef.current?.getBoundingClientRect()
            if (rect) zoomTowardPoint(e.clientX - rect.left, e.clientY - rect.top)
          }}
          role="application"
          aria-label="Interactive seat map. Arrow keys move between sections; Enter zooms into a section, then arrow keys move between individual seats and Enter selects a seat. Escape zooms out. A seat list for screen readers follows this map."
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className="absolute inset-0"
          style={{
            touchAction: 'none', userSelect: 'none', outline: 'none',
            textRendering: 'geometricPrecision',
          }}
        />

        {/* Screen-reader seat list fallback: a DOM alternative to the canvas
            (the industry-standard accessible mode). Scoped to the focused
            section to bound DOM size; each available seat is a real button
            wired to the same selection handler as canvas clicks. */}
        {srSeatList}

        {/* Section hover tooltip with view image */}
        {hoveredSectionKey && hoverPos && (() => {
          const sd = sectionDataMap.get(hoveredSectionKey)
          if (!sd) return null
          // Unit sections (TABLE/BOOTH/GA) have no individual seat tooltips, so always show.
          // For SEATED sections, hide when zoomed in (individual seat tooltips take over).
          const isUnitSD = sd.sectionType === 'TABLE' || sd.sectionType === 'BOOTH' || sd.sectionType === 'GA'
          if (isZoomedIn && !isUnitSD) return null
          const cat = sd.categoryId ? categoryMap.get(sd.categoryId) : null
          const ls = layoutSectionMap.get(hoveredSectionKey)
          const sectionDescription = ls?.description
          // Position tooltip to the right of cursor, flip left if near edge
          const tooltipW = sd.viewImageUrl ? 220 : (allowHeldSeatInteraction ? 200 : 190)
          const tooltipH = allowHeldSeatInteraction ? 140 : (sd.viewImageUrl ? 220 : sectionDescription ? 110 : 90)
          const tooltipX = hoverPos.x + tooltipW + 20 > dimensions.width
            ? hoverPos.x - tooltipW - 12
            : hoverPos.x + 16
          const tooltipY = Math.max(8, Math.min(hoverPos.y - 20, dimensions.height - tooltipH))
          return (
            <div
              className="absolute z-20 pointer-events-none animate-fade-in"
              style={{ left: tooltipX, top: tooltipY, width: tooltipW }}
            >
              <div className="bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-lg overflow-hidden">
                {sd.viewImageUrl && (
                  <img
                    src={sd.viewImageUrl}
                    alt={`View from ${sd.name}`}
                    className="w-full h-[120px] object-cover"
                  />
                )}
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {cat && (
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                    )}
                    <span className="text-sm font-semibold text-text-primary">{sd.name}</span>
                  </div>
                  {allowHeldSeatInteraction ? (
                    <>
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1">
                        {sd.available > 0 && <span className="text-xs text-green-600">{sd.available} free</span>}
                        {sd.held > 0 && <span className="text-xs text-amber-600">{sd.held} held</span>}
                        {sd.booked > 0 && <span className="text-xs text-blue-600">{sd.booked} booked</span>}
                        {sd.blocked > 0 && <span className="text-xs text-red-600">{sd.blocked} blocked</span>}
                        {sd.available === 0 && sd.held === 0 && sd.booked === 0 && sd.blocked === 0 && (
                          <span className="text-xs text-text-tertiary">No seats</span>
                        )}
                      </div>
                      {sd.channelRestrictions && Object.keys(sd.channelRestrictions).length > 0 && (
                        <div className="mt-1 pt-1 border-t border-surface-border">
                          {Object.entries(sd.channelRestrictions).map(([ch, count]) => (
                            <div key={ch} className="text-xs text-text-tertiary">
                              Channel: <span className="font-medium text-text-secondary">{ch === 'boxoffice' ? 'Box Office Only' : ch === 'online' ? 'Online Only' : ch}</span> ({count})
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-text-tertiary">
                        {sd.available > 0 ? (showRemainingQuantity ? `${sd.available} available` : 'Available') : 'Sold out'}
                      </span>
                      {sd.minPrice != null && sd.available > 0 && (() => {
                        const isWholeUnit = (sd.sectionType === 'BOOTH' || (sd.sectionType === 'TABLE' && sd.bookingMode !== 'by-seat')) && sd.sectionType !== 'GA'
                        // What the buyer pays: fees and tax folded in by the overview
                        const displayMin = sd.allInMinPrice ?? sd.minPrice
                        const displayMax = sd.allInMaxPrice ?? sd.maxPrice
                        const label = isWholeUnit ? '/table' : ''
                        return (
                          <span className="text-xs font-medium text-text-secondary">
                            {displayMin === displayMax ? `${formatPrice(displayMin)}${label}` : `${formatPrice(displayMin)}–${formatPrice(displayMax != null ? displayMax : displayMin)}${label}`}
                          </span>
                        )
                      })()}
                    </div>
                  )}
                  {sectionDescription && (
                    <p className="text-xs text-text-tertiary mt-1.5 leading-snug border-t border-surface-border pt-1.5">
                      {richTextToPlain(sectionDescription)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

        {/* Seat hover tooltip (admin mode only) */}
        {allowHeldSeatInteraction && hoveredSeat && seatHoverPos && (() => {
          const tooltipW = 200
          const tooltipH = 90
          // Position above cursor to avoid covering adjacent seats
          const tooltipX = Math.max(8, Math.min(seatHoverPos.x - tooltipW / 2, dimensions.width - tooltipW - 8))
          const aboveCursor = seatHoverPos.y - tooltipH - 20
          const tooltipY = aboveCursor >= 8 ? aboveCursor : seatHoverPos.y + 24
          const sectionData = sectionDataMap.get(hoveredSeat.sectionKey)
          const statusLabel = hoveredSeat.status === 'HELD' && hoveredSeat.holdType
            ? `HELD (${hoveredSeat.holdType})`
            : hoveredSeat.status
          const channelLabel = hoveredSeat.channelRestrict
            ? (hoveredSeat.channelRestrict === 'boxoffice' ? 'Box Office Only' : hoveredSeat.channelRestrict === 'online' ? 'Online Only' : hoveredSeat.channelRestrict)
            : null
          return (
            <div
              className="absolute z-20 pointer-events-none animate-fade-in"
              style={{ left: tooltipX, top: tooltipY, width: tooltipW }}
            >
              <div className="bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-lg px-3 py-2">
                <div className="text-xs font-semibold text-text-primary">
                  {sectionData?.name || hoveredSeat.sectionKey} - Row {hoveredSeat.rowLabel} - Seat {hoveredSeat.seatNumber}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    hoveredSeat.status === 'FREE' ? 'bg-green-500'
                    : hoveredSeat.status === 'HELD' ? 'bg-amber-500'
                    : hoveredSeat.status === 'BOOKED' ? 'bg-blue-500'
                    : 'bg-red-500'
                  }`} />
                  <span className="text-xs text-text-secondary">{statusLabel}</span>
                </div>
                {channelLabel && (
                  <div className="text-xs text-text-tertiary mt-0.5">
                    Channel: <span className="font-medium text-text-secondary">{channelLabel}</span>
                  </div>
                )}
                {hoveredSeat.price != null && (
                  <div className="text-xs text-text-tertiary mt-0.5">{formatPrice(hoveredSeat.allInPrice ?? hoveredSeat.price)}</div>
                )}
              </div>
            </div>
          )
        })()}

        {/* Public seat tooltip (non-admin mode) — seats.io style with section/row/seat header */}
        {!allowHeldSeatInteraction && hoveredSeat && seatHoverPos && (() => {
          const tooltipW = 200
          const tooltipH = 80
          // Position above cursor to avoid covering adjacent seats
          const tooltipX = Math.max(8, Math.min(seatHoverPos.x - tooltipW / 2, dimensions.width - tooltipW - 8))
          const aboveCursor = seatHoverPos.y - tooltipH - 20
          const tooltipY = aboveCursor >= 8 ? aboveCursor : seatHoverPos.y + 24
          const sectionData = sectionDataMap.get(hoveredSeat.sectionKey)
          const cat = hoveredSeat.category
          const isFree = hoveredSeat.status === 'FREE'
          return (
            <div
              className="absolute z-20 pointer-events-none animate-fade-in"
              style={{ left: tooltipX, top: tooltipY, width: tooltipW }}
            >
              <div className="bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-xl overflow-hidden">
                {cat && (
                  <div className="h-1" style={{ backgroundColor: cat.color }} />
                )}
                <div className="px-3 py-2">
                  <div className="text-[10px] font-medium text-text-tertiary uppercase tracking-wider">
                    {sectionData?.name || hoveredSeat.sectionKey} / Row {hoveredSeat.rowLabel} / Seat {hoveredSeat.seatNumber}
                  </div>
                  {cat && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                      <span className="text-xs font-semibold text-text-primary">{cat.name}</span>
                    </div>
                  )}
                  {hoveredSeat.price != null && (
                    <div className="text-sm font-bold text-text-primary mt-0.5">{formatPrice(hoveredSeat.allInPrice ?? hoveredSeat.price)}</div>
                  )}
                  <div className={`mt-1 text-[10px] font-medium ${isFree ? 'text-success' : 'text-text-tertiary'}`}>
                    {isFree ? 'Available -- Click to select' : 'Unavailable'}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Mobile floating context — hidden on mobile (SelectSeatsClient header shows section name),
            shown on desktop when zoomed in for hover context */}
        {mobileContext && isZoomedIn && (
          <div className="hidden lg:block absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none animate-fade-in">
            <div className="bg-surface/90 backdrop-blur-sm border border-surface-border rounded-lg px-3 py-1.5 shadow-lg">
              <div className="text-xs font-medium text-text-primary text-center">
                {mobileContext.sectionName}
                {mobileContext.rowLabel && (
                  <span className="text-text-tertiary ml-1">• Row {mobileContext.rowLabel}</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Zoom controls + floor toggle (44px touch targets) */}
        <div className={`absolute z-10 flex gap-1 bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-lg p-1 ${narrowControls ? 'right-3 flex-row items-center' : 'right-4 flex-col'}`} style={{ bottom: `${(narrowControls ? 8 : 16) + bottomOffset}px` }}>
          <button onClick={handleZoomIn} className="w-11 h-11 flex items-center justify-center hover:bg-surface-secondary rounded-lg text-text-secondary transition-colors" aria-label="Zoom in">
            <Plus className="w-4 h-4" />
          </button>
          {!narrowControls && (
            <div className="text-center text-[10px] text-text-tertiary tabular-nums py-0.5">
              {Math.round(transform.scale * 100)}%
            </div>
          )}
          <button onClick={handleZoomOut} className="w-11 h-11 flex items-center justify-center hover:bg-surface-secondary rounded-lg text-text-secondary transition-colors" aria-label="Zoom out">
            <Minus className="w-4 h-4" />
          </button>
          <div className={narrowControls ? 'border-l border-surface-border self-stretch mx-0.5' : 'border-t border-surface-border my-0.5'} />
          <button onClick={handleFitAll} className="w-11 h-11 flex items-center justify-center hover:bg-surface-secondary rounded-lg text-text-secondary transition-colors" aria-label="Fit all">
            <Maximize2 className="w-4 h-4" />
          </button>
          {onFullscreenToggle && (
            <>
              <div className="hidden lg:block border-t border-surface-border my-0.5" />
              <button
                onClick={() => onFullscreenToggle(!isFullscreen)}
                className="hidden lg:flex w-11 h-11 items-center justify-center hover:bg-surface-secondary rounded-lg text-text-secondary transition-colors"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Shrink className="w-4 h-4" /> : <Expand className="w-4 h-4" />}
              </button>
            </>
          )}
          {floors && floors.length > 1 && (
            <>
              <div className={narrowControls ? 'border-l border-surface-border self-stretch mx-0.5' : 'border-t border-surface-border my-0.5'} />
              <button
                onClick={() => onFloorSwitch?.(null)}
                className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-colors ${
                  activeFloor === null
                    ? 'bg-[rgb(var(--color-primary))] text-text-inverse'
                    : 'hover:bg-surface-secondary text-text-secondary'
                }`}
                title="All Levels"
              >
                All
              </button>
              {floors.map(f => (
                <button
                  key={f.number}
                  onClick={() => onFloorSwitch?.(activeFloor === f.number ? null : f.number)}
                  className={`px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-colors ${
                    activeFloor === f.number
                      ? 'bg-[rgb(var(--color-primary))] text-text-inverse'
                      : 'hover:bg-surface-secondary text-text-secondary'
                  }`}
                  title={f.name}
                >
                  L{f.number}
                </button>
              ))}
            </>
          )}
        </div>

        {/* View from seat banner — shows when section is focused (mobile + desktop) */}
        {focusedSectionKey && (() => {
          const sd = sectionDataMap.get(focusedSectionKey)
          if (!sd?.viewImageUrl) return null
          return (
            <div className="absolute top-2 right-2 z-10 animate-fade-in lg:top-4 lg:right-4">
              <div className="bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-lg overflow-hidden" style={{ width: 180 }}>
                <img
                  src={sd.viewImageUrl}
                  alt={`View from ${sd.name}`}
                  className="w-full h-[100px] object-cover"
                />
                <div className="px-2 py-1.5 text-center">
                  <span className="text-[10px] font-medium text-text-secondary">View from {sd.name}</span>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Back to overview button (desktop only — mobile uses SelectSeatsClient header) */}
        {isZoomedIn && (
          <button
            onClick={zoomToOverview}
            className="hidden lg:flex absolute top-4 left-4 z-10 items-center gap-1.5 px-3 py-2 min-h-[44px] bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl text-sm text-text-secondary hover:text-text-primary shadow-lg transition-colors animate-fade-in"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Overview
          </button>
        )}

        {/* Compact STATE key — one color = one price tier (the Pricing panel
            maps colors to prices); this key only explains state treatments.
            Replaces the old 5-entry legend whose "Your Hold" amber and
            "Selected" primary collided with category colors. Its height is
            measured and kept clear of the overview fit. */}
        {(
          <div ref={legendRef} className="hidden lg:flex absolute left-4 right-4 z-10 bg-surface/95 backdrop-blur-sm border border-surface-border rounded-xl shadow-lg px-3 py-2 animate-fade-in max-w-fit" style={{ bottom: `${16 + bottomOffset}px` }}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-medium text-text-secondary">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: 'conic-gradient(#7c3aed, #2563eb, #059669, #d97706, #7c3aed)' }} /> Available (color = price tier)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full border-2 border-primary flex-shrink-0" style={{ background: 'conic-gradient(#7c3aed, #2563eb, #059669, #d97706, #7c3aed)' }} /> Your seats
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#e2e8f0' }} /> Unavailable
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full border-2 flex-shrink-0" style={{ borderColor: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.2)' }} /> ADA
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default UnifiedSeatMap
